import { createQueryToolbarRuntime } from './query-toolbar-runtime/index.js'
import type { QueryToolbarRuntimeOptions } from './query-toolbar-runtime/types.js'

import { Toasts } from './toasts.js'
import { showNavigationConfirmation } from './navigation-confirmation.js'
import { readDisplay, resolveDevice, watchDisplay, MIN_MEDIUM_SIZE, MIN_LARGE_SIZE } from './device.js'
import type { DeviceKind, DeviceWindow } from './device.js'
import { createSupport } from './support/entry.js'
import type { SupportApi } from './support/types.js'
import { LocalModel } from './local-model.js'
import type { LocalModelApi } from './local-model.js'
import * as Types from '@aeppic/types'
import { markdown, parse as formdown } from '@aeppic/forms-parser'

import safeStringify from 'fast-safe-stringify'
import FileSaver from 'file-saver'
import DOMPurify from 'dompurify'
import anime from 'animejs'
import Sortable from './modules/sortable.js'
import Zip from './modules/zip.js'

import { DateTime, Duration, Interval, Info as LuxonInfo, Settings as DateTimeSettings } from 'luxon'
import * as Luxon from 'luxon'
import * as queryString from 'query-string'

import uuid from '@aeppic/shared/uuid'
import { waitForNextTick, waitForNextMicroTick } from '@aeppic/shared/wait-for-tick'
import { UniqueItemProcessingQueue } from '@aeppic/shared/processing-queue'
import { setTimeoutNoKeepAlive } from '@aeppic/shared/timer'
import { buildDeferred } from '@aeppic/shared/defer'
import { deprecated } from '@aeppic/shared/deprecation'
import { EventEmitter } from '@aeppic/shared/event-emitter'
import { default as DirectCache } from '@aeppic/shared/cache'
import { formatToHumanReadableWithLuxon } from '@aeppic/shared/date-formatting'
import { formatCurrency, FormatCurrencyOptions } from '@aeppic/shared/common-formatting'
import { createFormattingAPI } from './formatting.js'
import { createFinancialAPI } from './financial.js'
import type { FormattingAPI } from './formatting.js'
import type { FinancialAPI } from './financial.js'

import { lastOneWins } from '@aeppic/shared/async-calling-strategies'
import { readId } from './../model/id.js'
import { NotificationReason } from './../model/notification-reason.js'

import Vue from '../externals/vue.js'

import { IServerAdapter, IDocumentLookup, BatchFlushOptions } from '../shared/types/server-adapter.js'
import Popup from './popup.js'
import { closeAllDialogs, closeLayeredDialogsAbove, getNearestDialogRegion, isLayeredDialogRegistered } from './dialog-stack.js'
import { getLogicalParentNode, LAYERED_OVERLAYS_FEATURE, setOverlayOwner } from './overlay-host.js'
import { debounce } from './utils/debounce.js'
import { isReference, isDocument, isStamp } from '../model/is.js'
import { getAllResolveQuerySteps, resolveDocumentPath } from '../model/resolve-path.js'
import ZipWriter from './utils/zip-writer.js'
import { readJsonlObjects } from './utils/jsonl-reader.js'
import { Cache } from './cache/index.js'
import { Offline } from './offline/offline.js'

import Changes from './changes.js'
import Translator from './utils/translator.js'
import Preferences from './utils/preferences.js'
import Services from './utils/services.js'
import Developer from './utils/developer.js'
import History from './utils/history.js'
import { compareDocumentTrees, compareDocumentSubTrees, DefaultDocumentAdapter, DifferenceNode } from './utils/compare-tree.js'
import buildCompleteSearchString from './utils/build-query-string.js'
import sort from './utils/sort.js'

import resolveField from '../model/query/resolve-field.js'
import deriveChildId from './utils/derive-child-id.js'

import { Logger, buildLogger } from '../model/log.js'

import { Content } from './content.js'
import { Storage } from './storage.js'

import { IQuerySubscription, QuerySubscription } from './query.js'
import { Commands, ICommands } from './commands/index.js'
import { Validations } from './validations.js'

import type {
  ModelOptions,
  CloneOptions,
  NewDocumentOptions,
  ChangeDocumentFormOptions,
  IDocumentWatcher,
  DocumentWatchCallback,
  Notification,
  DocumentsWatchCallback,
  IQueryWatcher,
  DocumentLookupCallback,
  ImportOperation,
  ImportResult,
  FindOptions,
  FindOneOptions,
  FieldSortInfo,
  FileField,
  StampOptions,
  Write,
  PreviousChange,
} from '../model'

import {
  default as Model,
  ChangeType,
  Form,
  Change,
  EditableDocument,
  importIntoModel,
  parseDateRange,
  getDocumentId,
  isAdmin,
  Query,
  toQueryAndOptions,
  getFindOptionsForFindOne,
  warnIfFindOneMatchedMultiple,
  Feature as ModelFeature,
  Formatting as TextFormatting
} from '../model'

export { Write }

import Release from './release.js'
import RootComponent from './components/ae-root.js'

import LayoutComponent from './components/ae-layout.js'
import LayoutSelectorComponent from './components/ae-layout-selector.js'
import DesignComponent from './components/ae-design.js'
import DesignSelectorComponent from './components/ae-design-selector.js'
import ListComponent from './components/ae-list.js'
import ListSelectorComponent from './components/ae-list-selector.js'
import QueryComponent from './components/ae-query.js'
import VarsComponent from './components/ae-vars.js'
import ControlComponent from './components/ae-control.js'
import ControlSelectorComponent from './components/ae-control-selector.js'
import FormComponent from './components/ae-form.js'
import FormSectionComponent from './components/ae-form-section.js'
import FormSectionNextComponent from './components/ae-form-section-next.js'
import FormParagraphComponent from './components/ae-form-paragraph.js'
import FormFieldComponent from './components/ae-form-field.js'
import WireframeViewComponent from './components/ae-wireframe-view.js'
import FormPreviewComponent from './components/ae-form-preview.js'
import HeaderComponent from './components/ae-header.js'
import HeadroomComponent from './components/ae-headroom.js'

import LinkComponent from './components/ae-link.js'
import SplitterComponent from './components/ae-splitter.js'
import TabsComponent from './components/ae-tabs.js'
import ExpandableComponent from './components/ae-expandable.js'
import PopupComponent from './components/ae-popup.js'
import SelectComponent from './components/ae-select.js'
import DialogComponent from './components/ae-dialog.js'
import IconComponent from './components/ae-icon.js'
import FetchComponent from './components/ae-fetch.js'

import DragAndDropExtension from './directives/v-drag-and-drop.js'
import FocusDirective from './directives/v-focus.js'
import ResponsiveExtension from './directives/v-responsive.js'
import TranslateExtension from './directives/v-translate.js'
import VisibilityDirective from './directives/v-observe-visibility.js'
import DensityExtension from './directives/v-density.js'

import { loadStyle, loadStyleFromUrl, unloadStyle } from './utils/css.js'
// import { loadScript, unloadScript, Group as ScriptGroup } from './utils/script.js'
import ReactiveObjectProxy from '../shared/reactive-object-proxy.js'
import { Context, contextify, rewrapContext } from './contextify/index.js'

import { Packages } from './packages/packages.js'
import type { Options as PackagesOptions } from './packages/packages.js'
import { FeatureNamespace, Features } from './features.js'
import { createServiceWorkerClient, type ServiceWorkerClient } from '@aeppic/service-worker'
import { clearAeppicClientCaches } from './client-cache-sequence.js'
import { applyClientCacheSequence, CLIENT_CACHE_SEQUENCE_FEATURE } from './client-cache-sequence.js'
import { ClientUpdate } from './client-update.js'
import { SharedContext } from './shared-context.js'
import { EditContextRegistry, EditContextHandle, EditContextCreateOptions, findEditContextInDOM, isInsideEditContextBoundary } from './edit-context.js'
import { SelectionFeature } from './selection.js'
import { Navigation } from './navigation.js'
import { Colors } from './utils/colors.js'
import { APP_MANIFEST_FEATURE, AppManifest } from './app-manifest.js'

import { Uploads, IUploadJobRequest } from './uploads/index.js'

import { ServerAdapter } from './server-adapter.js'
import {
  ILocalReadModelAdapter,
  LOCAL_READ_MODEL_FEATURE,
  LOCAL_READ_MODEL_MEMORY_KEY,
  LOCAL_READ_MODEL_SWITCHED_OFF_KEY,
  LocalReadModelFactory,
  LocalFirstServerAdapter,
  type LocalLookupRequest,
} from './local-first-server-adapter.js'
import { perf } from './perf.js'
import { IRouter, NavigationOptions, RouteParams } from './router.js'
import { AccessRightCache } from './access-cache.js'
import {
  ResolveLockRightsHasRightCache,
  RESOLVE_LOCK_RIGHTS_HAS_RIGHT_CACHE_FEATURE,
} from './resolve-lock-rights-has-right-cache.js'
import { Designs } from './dynamic/design-lookup.js'
import DynamicComponent, { loadComponentPackages } from './dynamic/dynamic-component.js'
import { ViewComponentGroups, type ViewComponentsFeatureOptions } from './dynamic/view-component-groups.js'
import { Forms } from './forms/forms.js'
import { Controls } from './controls.js'
import { QueryScopes, Scopes } from '../model/query/index.js'
import { MatchOptions, isQueryOfPartialDocuments } from '../model/query/index.js'
import { CommandAeppicInterface } from './commands/executor.js'
import { Actions } from './actions.js'
import { Warn } from './warn.js'
import { GetOptions } from '../model/index.js'
import { calculateQueryCacheKey, QueryCache } from './query-cache.js'


const FULL_ACCESS_RIGHT_ID = 'aa3ba2de-4b65-4152-ab1d-655e21de9efd'

const MAX_DATA_URL_LENGTH = 128

const DEFAULT_MIN_REFRESH_DELAY_IN_MS = 1000

const WELL_KNOWN_CLASSES = {
  /**
   * ExpandRegion marks a DOM node which
   * is the top-level DOM node that will become the
   * container for any expanded element inside it
   */
  ExpandRegion: 'ae-expand-region',
}

declare const TextEncoder: any
declare const TextDecoder: any

type FlagSettings = {
  enabled?: boolean,
  options?: object,
}

/**
 * @public
 */
export interface Flag extends FlagSettings {
  enabled: boolean,
  title?: string,
  description?: string,
  options?: object,
}

/**
 * @public
 */
export interface Flags {
  watchUIRevisions?: Flag
  discoverElements?: Flag
  disableStylesheets?: Flag
  logServerLoads?: Flag
  logQueryRefresh?: Flag
  cacheControls?: Flag
}

type FlagNames = keyof Flags

/**
 * @public
 */
export type FlagStatus = {
  [key in FlagNames]?: boolean | FlagSettings
}

const DEFAULT_FLAGS: Flags = {
  watchUIRevisions: {
    title: 'Watch UI Revisions',
    enabled: true,
    description: 'When enabled all designs/layouts/controls/lists/forms will update while live while being edited',
  },
  discoverElements: {
    title: 'Discover Elements',
    enabled: true,
    description: 'Pressing CTRL-SHIFT while moving mouse discovers the element hierarchy',
  },
  disableStylesheets: {
    title: 'Disable Stylesheets',
    enabled: false,
    description: 'Don\'t load or render stylesheets',
  },
  logServerLoads: {
    title: 'Log all server data load operations',
    enabled: false,
    description: 'Add a log entry whenever Aeppic needs to load data from the server'
  },
  logQueryRefresh: {
    title: 'Log all query subscription refreshes',
    enabled: false,
    description: 'Add a log entry whenever Aeppic refreshes local query subscriptions'
  },
  cacheControls: {
    title: 'Cache Control lookups',
    enabled: true,
    description: 'Cache Control lookups'
  }
}

const NO_AUTH = { loggedIn: false, accountId: null, backedBy: null, impersonatedBy: null, groupAccountId: null, profile: null, info: null }

export interface DocumentReadStream {
  read(): Types.Document
}

export interface DocumentSourceFunction {
  (): DocumentReadStream | Promise<DocumentReadStream>
}

export interface VueComponentOptions {
  aeIcon: AeIconOptions
}

export interface AeIconOptions {
  baseIconPath: string
  baseClassToUrlExtension: Object
  noIconNames: Array<string>
  alias: Object
}

//#region FileEditEvent
type LocalFilePath = string

interface FileChangeEvent {
  type: 'files:changed'
  document: Types.Reference
  fieldName: string
  localFilePath: LocalFilePath
  editInfo: DocumentEditInfo
  detectedAt: number
}

interface FileUploadEvent {
  type: 'upload:started' | 'upload:canceling' | 'upload:canceled' | 'upload:finished' | 'upload:failed'
  document: Types.Reference
  fieldName: string
  localFilePath: LocalFilePath
  uploadInfo: DocumentUploadInfo
  detectedAt: number
}

type FileEditEvent = FileChangeEvent | FileUploadEvent
//#endregion FileEditEvent

interface DocumentUploadInfo {
  document: Types.Reference
  fieldName: string
  uploadKey: string
  filePath: string
  targetUrl: string
  totalTimeout: Duration
  uploadFileFieldName: string
  accessToken: string
  fields: {
    [fieldName: string]: string
  }
}


interface DocumentEditInfo {
  document: Types.Reference
  fieldName: string
  url: string
  uploadUrl: string
  uploadFileFieldName: string
  mimeType: string
  fields: {
    [fieldName: string]: string
  },
  explicitFieldName: string
  accessToken: string
}

interface ClientEditInfo {
  event: 'files:edit'
  payload:
    {
      Upload:
        {
          Started: [LocalFilePath, DocumentUploadInfo]
        }
        |
        {
          Canceling: [LocalFilePath, DocumentUploadInfo]
        }
        |
        {
          Canceled: [LocalFilePath, DocumentUploadInfo]
        }
        |
        {
          Finished: [LocalFilePath, DocumentUploadInfo]
        }
        |
        {
          Failed: [LocalFilePath, DocumentUploadInfo]
        }
    }
    |
    {
      Changed: [LocalFilePath, DocumentEditInfo]
    }
}

//#region AeppicClientHostInterface
interface AeppicClientHostInterface {
  type: string,
  version: string,
  matches: (semver: string) => boolean,
  features: {
    [featureGroupName: string]: {
      [featureName: string]: boolean
    }
  },
  files: {
    openFile(
      url: string,
      fileName: string,
      documentInformation: {
        id: string,
        v: string,
        fieldName: string,
        mimeType: string,
      }): void,
    editFile(
      url: string,
      fileName: string,
      documentInformation: {
        id: string,
        v: string,
        fieldName: string,
        mimeType: string,
        uploadFormFields?: {
          [fieldName: string]: string
        },
        uploadFileFieldName?: string,
        uploadUrl?: string,
      }): void,
    subscribeToEditEvents(callback: (editInfo: ClientEditInfo) => void): () => void,
  }
}
//#endregion AeppicClientHostInterface

export type DEVICE = DeviceKind
export type RESPONSIVE_LEVELS = 'Small' | 'Medium' | 'Large'

const DEFAULT_MAXIMUM_CONCURRENT_LOOKUPS = 5

export interface Options {
  /**
   * Element or query-selector to define
   * the root mount point.
   *
   */
  el?: string | HTMLElement,

  /**
   * Address of the Server API Endpoint. Defaults to /api
   */
  server?: string

  /**
   * User Agent override. If not provided the it will be read
   * from the window
   */
  userAgent?: string

  /**
   * device informs designs/layouts what device to render
   * for. This is automatically extracted from the userAgent by
   * default
   */
  device?: DEVICE

  /**
   * Optionally override the fetch function to use
   * will fall-back to the global fetch function if not
   * defined
   *
   */
  fetch?: (input: RequestInfo, init?: RequestInit) => Promise<Response>

  /**
   * Optionally override the WebSocket implementation to use
   * will fall-back to the global WebSocket if not
   * defined
   *
   */
  WebSocket?: typeof WebSocket

  /**
   * Defaults to true and will call /api/auth/info to verify login status
   */
  auth?: boolean | { username: string, password: string }

  /**
   * Documents specified in here are loaded into the model
   * before the loaded callback is called and the view model
   * is instantiated
   *
   *    */
  boot?: LoadOptions,

  /**
   * Documents specified in here are loaded after the loaded
   * callback is called and happens in the background.
   *
   * If a document is requested after boot that is not yet available
   * the documents specified here are waited for
   *
   *    */
  load?: LoadOptions,

  /**
   * Callback when linking to the dom and importing
   * documents defined in `boot` is completed
   *
   */
  booted?: (Aeppic) => void,

  /**
   * Callback when linking to the dom and importing
   * documents defined in `load` is completed
   *
   */
  loaded?: (Aeppic) => void,

  /**
   * The persistence adapter is used to retrieve documents that are required (parents, forms)
   * or requested via get / search.
   *
   * TODO: Will be used instead of `documentLookup`
   *
   *    */
  persistenceAdapter?: IServerAdapter

  /** Maximum queries per HTTP search POST. Omit to send the whole pending group.
   * Does not limit local-model calls. Applies to the built-in ServerAdapter only.
   */
  serverQueryBatchSize?: number

  /** HTTP search timeout in milliseconds (default 30000). Does not time out local SQLite queries. */
  serverQueryTimeoutMs?: number

  /**
   * Optional local read model used for covered reads before falling back to
   * the configured persistence adapter or server. Local routing is active only
   * while the `local-read-model` feature is enabled.
   */
  localReadModel?: ILocalReadModelAdapter

  /**
   * Lazily constructs the local read model after Aeppic has loaded its feature
   * namespace and confirmed that `local-read-model` is enabled.
   */
  localReadModelFactory?: LocalReadModelFactory

  /**
   * When documents are imported, for example as part of boot or documents specified
   * through these options, it is possible that the dataset provided is not
   * complete and more data is required before being able to fully import a document.
   *
   * This can either be a missing parent or a missing form version and is resolved
   * using this callback. The callback expects at least the specified data, but it
   * is ok to return more. Eg. all ancestors instead of just the immediate parent.
   *
   * Usually this is not required, since it defaults to either waiting
   * for the specified `documents` to be completely loaded or makes use of the
   * provided `persistenceAdapter` to fetch more information.
   *
   *    */
  documentLookup?: DocumentLookupCallback

  /**
   * Instance of PackageLoader to use for loading source files of packages.
   * Will fallback to a default loader if not defined.
   *
   *    */
  packageLoader?: any // TODO: IPackageLoader

  /**
   *
   */
  flags?: FlagStatus

  /**
   * After load is completed remove the DOM element selected by this
   */
  removeAfterBoot?: HTMLElement | string

  /**
   * After load is completed remove the DOM element selected by this
   */
  removeAfterLoad?: HTMLElement | string

  /**
   * Remove the specified DOM element the first time renderLayout is finished
   */
  removeAfterRender?: HTMLElement | string


  /**
   * DateTime behavior
   */
  dontThrowOnInvalidDateTime?: boolean

  /**
   *
   */
  concurrentUploadLimit?: number

  /**
   * Do NOT automatically connect to server API and sync changes
   */
  noConnect?: boolean

  /**
   * Tell Model to set timestamps on new documents or changed documents (`document.t`).
   *
   * This should be
   */
  setTimestamps?: boolean

  defaultPageLimit?: number,

  maximumConcurrentLookupCalls?: number,

  searchAllFields?: boolean,

  vueComponentOptions?: VueComponentOptions
  Logger?: Logger

  /*
   * A prefix to use to find packages or a function to return an url to get a file from
   *
   * In case of a string its the prefix to use which defaults to `/api/packages` and gets
   * combined with /${package.id}@${package.v}/files/` and the file path relative
   * to the package.
   *
   * In case of a function it
   *
   */
  packagesLookupUrl?: PackagesOptions['packagesLookupUrl']

  features?: FeatureNamespace

  customIndexing?: ModelOptions["customIndexing"],

  /**
   * Usually an url path under the api.
   */
  featuresLookupPath?: string
}


/**
 * Data to govern loading of static data.
 *
 * If a cacheIdentifier is specified the src is only
 * used when no previous matching cache identifier
 * is available.
 */
export interface LoadOptions {
  cacheIdentifier?: string,
  maxAgeInSeconds?: number
  src?: string | DocumentSourceFunction,
  transform?: (event: any) => Promise<any>
}

export interface LayoutRenderOptions {
  /**
   * Custom parameters of any shape i.e { paramA: 'test' } that
   * can be bound to from the render template
   *
   *    */
  params: object
  noWait?: boolean
}

const DEFAULT_LAYOUT_OPTIONS: LayoutRenderOptions = {
  params: {}
}

export interface DrillIntoOptions {
  source?: HTMLElement
  sourceElement?: HTMLElement
  sourceContext?: any
  /** The triggering UI event; Alt/Option-click switches to `mode: 'navigate'`. Falls back to `window.event`. */
  event?: Event
  mode?: 'dialog' | 'navigate' | 'event'
  preferredMode?: string
  dialogOptions?: DocumentDialogOptions
}

/** Layout IDs for document-dialog content, independent of the form's layoutId. */
export interface DocumentDialogLayouts {
  dialogLayoutId?: string
  headerLayoutId?: string
  bodyLayoutId?: string
  commandsLayoutId?: string
}

/** Reactive params.dialog supplied to every document-dialog layout. Reserved API context. */
export interface DocumentDialogLayoutContext {
  document: EditableDocument
  title: string
  subtitle: string
  labels: Record<string, string>
  contextLabel: string
  modeLabel: string
  readonly: boolean
  autoSave: boolean
  saving: boolean
  error: string | null
  fullscreen: boolean | null
  isDrillInto: boolean
  isCreate: boolean
  isExternalReference: boolean
  layered: boolean
  level: number
  formLayoutId: string
  formView?: string
  designName: string
  headerActions: DocumentDialogAction[]
  footerActions: DocumentDialogAction[]
  commands: DocumentDialogCommand[]
  layouts: DocumentDialogLayouts
  save(): Promise<void>
  abort(): void
  close(): void
  navigate(): Promise<void>
  toggleFullscreen(): void
  runAction(action: DocumentDialogAction): Promise<void>
  runCommand(command: DocumentDialogCommand, options?: { force?: boolean }): Promise<void>
  saveDocument(document: EditableDocument): unknown
}

export interface DocumentDialogCommand {
  id: string
  label: string
  primary?: boolean
  disabled?: boolean
  disabledReason?: string
  document: Types.Document
}

export interface DocumentDialogOptions extends DocumentDialogLayouts {
  title?: string
  subtitle?: string
  designName?: string
  layoutId?: string
  formView?: string
  saveLabel?: string
  cancelLabel?: string
  openLabel?: string
  command?: Types.Document
  dialog?: Types.Document
  commands?: Array<Types.Document | Types.IdOrReference>
  fullScreen?: boolean
  preferredMode?: string
  backdropClose?: boolean
  dialogSize?: object
  dialogStyle?: object
  dialogClasses?: object
  appearance?: string
  mountRegion?: string | HTMLElement
  sourceElement?: HTMLElement
  sourceContext?: any
  source?: 'command' | 'drillInto'
  readonly?: boolean
  autoSave?: boolean
  /** Extra params for the dialog design; the dialog's own layoutId/readonly/autoSave take precedence. */
  params?: Record<string, unknown>
  /** Application buttons shown in the dialog header (default) or footer. */
  actions?: DocumentDialogAction[]
  /** Language of the dialog's own labels (`de`, `en`, `fr`); defaults to the account, then the browser. */
  locale?: string
}

export type DocumentDialogAction = {
  id: string
  label: string
  /** Icon classes, e.g. `fa-solid fa-thumbtack`. Header actions show only the icon when set. */
  icon?: string
  placement?: 'header' | 'footer'
  primary?: boolean
  /** `dialog.close()` closes the dialog like its close button (a layered drill-in leaves the whole drill-in). */
  onClick: (document: EditableDocument, dialog: { close: () => void }) => void | Promise<void>
}

/**
 * Supplies application defaults for every drill-in dialog, wherever it was requested
 * (page, nested dialog, Aeppic fallback). Explicitly passed options win. Returning
 * `false` means the application handled the request itself and no dialog opens.
 */
export type DrillIntoDialogDefaultsProvider = (request: {
  documentId: Types.DocumentId | null
  options: DocumentDialogOptions
}) => DocumentDialogOptions | false | null | undefined | Promise<DocumentDialogOptions | false | null | undefined>

export type DocumentDialogResult = {
  action: 'save' | 'abort' | 'command' | 'navigate'
  document?: EditableDocument
  command?: Types.Document
  result?: any
}

type DrillIntoDialogStackEntry = {
  documentId: Types.DocumentId
  close: () => void
  result: Promise<DocumentDialogResult>
  /** The rendered ae-dialog, the drill-in's entry in the layered logical stack. */
  dialog?: () => any
}


const DOMEventSubscriberIdentifierKey = (typeof Symbol === 'undefined') ? '__ae_event_handler_id' : Symbol('Event Subscriber Identifier')

const DOMEvents = {
  WillNavigate: 'ae-will-navigate',
  DrillInto: 'ae-drill-into',
  Navigated: 'ae-navigated',
  RouteParamsChanged: 'ae-route-params-changed',
}

export { DOMEvents }

export interface ModelExport {
  name: string,
  extension: string,
  data: string | Blob | Buffer,
}

export interface ModelExportOptions {
  json?: 'lines' | 'array',
  format?: 'text' | 'blob' | 'buffer',
  rootDocumentId?: string,
  rootDocumentIds?: string[],
  compress?: boolean,
  includeUnusedForms?: boolean,
  includeBinaryContent?: boolean,
  transportManifestId?: string
}

const DEFAULT_MODEL_EXPORT_OPTIONS: ModelExportOptions = {
  json: 'lines',
  format: 'blob',
  rootDocumentId: 'root',
  rootDocumentIds: [],
  compress: false,
  includeUnusedForms: false,
  includeBinaryContent: false,
  transportManifestId: null
}


export interface ModelDownloadOptions {
  json?: 'lines' | 'array',
  filename?: string,
  extension?: string,
  rootDocumentId?: string,
  rootDocumentIds?: string[],
  compress?: boolean,
  includeUnusedForms?: boolean,
  includeBinaryContent?: boolean,
  transportManifestId?: string

}

const DEFAULT_MODEL_DOWNLOAD_OPTIONS: ModelDownloadOptions = {
  json: 'lines',
  filename: '',
  rootDocumentId: 'root',
  rootDocumentIds: [],
  compress: true,
  includeUnusedForms: true,
  includeBinaryContent: false,
  transportManifestId: null
}

const CSS_BLOCK_MARKER = /_CSS/g

const DEFAULT_OPTIONS: Options = {
  auth: true,
  featuresLookupPath: '/v4/features/namespaces/aeppic',
}

const DIALOG_LABELS: Record<string, Record<string, string>> = {
  de: {
    save: 'Speichern',
    abort: 'Abbrechen',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    close: 'Schließen',
    open: 'Vollständig öffnen',
    openmaster: 'Im Stammsatz öffnen',
    expand: 'Vollbild',
    collapse: 'Fenstergröße',
    new: 'Neu',
    newdocument: 'Neues Dokument',
    detail: 'Detail',
    readonly: 'Nur Lesen',
    editable: 'Bearbeitbar',
    back: 'Zurück',
    level: 'Ebene',
    reference: 'Referenz · Stammdaten',
    referenceinfotitle: 'Stammsatz – systemweit verwendet',
    referenceinfo: 'Dieses Dokument ist ein Stammsatz, auf den an mehreren Stellen verwiesen wird. Änderungen hier werden überall sichtbar, wo er verwendet wird.',
    autosavecontext: 'Auto-Save · im Bearbeitungskontext',
    editcontext: 'Bearbeiten · im Bearbeitungskontext',
    autosaved: 'Automatisch gespeichert',
  },
  en: {
    save: 'Save',
    abort: 'Cancel',
    cancel: 'Cancel',
    delete: 'Delete',
    close: 'Close',
    open: 'Open fully',
    openmaster: 'Open master record',
    expand: 'Full screen',
    collapse: 'Window size',
    new: 'New',
    newdocument: 'New document',
    detail: 'Detail',
    readonly: 'Read only',
    editable: 'Editable',
    back: 'Back',
    level: 'Level',
    reference: 'Reference · master record',
    referenceinfotitle: 'Master record – used system-wide',
    referenceinfo: 'This document is a master record referenced in several places. Changes made here become visible everywhere it is used.',
    autosavecontext: 'Auto-save · in editing context',
    editcontext: 'Edit · in editing context',
    autosaved: 'Saved automatically',
  },
  fr: {
    save: 'Enregistrer',
    abort: 'Annuler',
    cancel: 'Annuler',
    delete: 'Supprimer',
    close: 'Fermer',
    open: 'Ouvrir entièrement',
    openmaster: 'Ouvrir la fiche principale',
    expand: 'Plein écran',
    collapse: 'Taille de fenêtre',
    new: 'Nouveau',
    newdocument: 'Nouveau document',
    detail: 'Détail',
    readonly: 'Lecture seule',
    editable: 'Modifiable',
    back: 'Retour',
    level: 'Niveau',
    reference: 'Référence · fiche principale',
    referenceinfotitle: 'Fiche principale – utilisée partout',
    referenceinfo: 'Ce document est une fiche principale référencée à plusieurs endroits. Les modifications apportées ici sont visibles partout où elle est utilisée.',
    autosavecontext: "Enregistrement auto · dans le contexte d'édition",
    editcontext: "Modifier · dans le contexte d'édition",
    autosaved: 'Enregistré automatiquement',
  },
}

const COMMAND_DIALOG_CSS = `
.ae-command-dialog-host {
  display: contents;
}

.ae-command-dialog-host .ae-dialog__header,
.ae-command-dialog-host .ae-dialog__body,
.ae-command-dialog-host .ae-dialog__commands-bar {
  padding: 0;
  min-height: 0;
  border: 0;
}

.ae-command-dialog__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fff;
  font-weight: 600;
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
}

.ae-command-dialog__form-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 1rem;
}

.ae-command-dialog__form-icon-design {
  display: flex;
  align-items: center;
  justify-content: center;
}

.ae-command-dialog__header-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.ae-command-dialog__header-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.ae-command-dialog__action-btn {
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  color: #94a3b8;
  padding: 0.4rem;
  line-height: 1;
  border-radius: 0.375rem;
}

.ae-command-dialog__action-btn:hover {
  color: #475569;
  background: rgba(0, 0, 0, 0.04);
}

.ae-command-dialog__header-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: 0;
}

.ae-command-dialog__context-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.78rem;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.ae-command-dialog__level {
  font-size: 0.78rem;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #475569;
}

.ae-command-dialog__action-btn > i + span,
.ae-command-dialog__button > i + span {
  margin-left: 0.4rem;
}

.ae-command-dialog__mode-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  max-width: 100%;
  border-radius: 999px;
  padding: 0.25rem 0.65rem;
  font-size: 0.88rem;
  line-height: 1.2;
  font-weight: 700;
  color: #475569;
  background: #e2e8f0;
}

.ae-command-dialog__mode-icon {
  flex: 0 0 auto;
}

.ae-command-dialog__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 1rem;
  line-height: 1.3;
}

.ae-command-dialog__subtitle {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #6366f1;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.2;
}

.ae-command-dialog__close {
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 1.15rem;
  color: #94a3b8;
  padding: 0.4rem;
  line-height: 1;
  border-radius: 0.375rem;
}

.ae-command-dialog__close:hover {
  color: #1f2937;
  background: rgba(0, 0, 0, 0.04);
}

.ae-command-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 0.75rem 1rem;
  background: #fff;
}

.ae-command-dialog__commands {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  width: 100%;
  padding: 0.625rem 1rem;
  background: #fafafa;
  border-top: 1px solid rgba(148, 163, 184, 0.15);
}

.ae-command-dialog__button {
  cursor: pointer;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 0.5rem;
  padding: 0.4rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.3;
}

.ae-command-dialog__button--cancel {
  border: 0;
  background: transparent;
  color: #64748b;
  font-weight: 400;
}

.ae-command-dialog__button--cancel:hover {
  color: #1f2937;
}

.ae-command-dialog__button--primary {
  border-color: #4f46e5;
  background: #4f46e5;
  color: #fff;
  padding: 0.5rem 1.25rem;
  font-weight: 600;
  border-radius: 0.5rem;
}

.ae-command-dialog__button--primary:hover {
  background: #4338ca;
}

.ae-command-dialog__button:disabled {
  cursor: default;
  opacity: 0.4;
}

.ae-command-dialog__error {
  color: #9d1c1c;
  margin-bottom: 0.5rem;
}

.ae-command-dialog--drill-into {
  --ae-dialog-bg-color: #fff;
  --ae-dialog-border: 1px solid rgba(148, 163, 184, 0.42);
  --ae-dialog-border-radius: 0.875rem;
}

.ae-dialog.ae-command-dialog--drill-into > .ae-dialog__header {
  min-height: 0;
  padding: 0;
  border-bottom: 0;
}

.ae-dialog.ae-command-dialog--drill-into > .ae-dialog__body {
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.ae-dialog.ae-command-dialog--drill-into > .ae-dialog__commands-bar {
  min-height: 0;
  padding: 0;
  border-top: 0;
  background: #fff;
  box-shadow: 0 -8px 18px rgba(15, 23, 42, 0.08);
}

.ae-command-dialog--drill-into .ae-command-dialog__header {
  min-height: 4rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.22);
  background: #fff;
  color: #1f2937;
}

.ae-command-dialog--drill-into.ae-command-dialog--auto-save .ae-command-dialog__header {
  border-bottom-color: rgba(20, 184, 166, 0.34);
  background: #edfdf8;
  color: #134e4a;
}

.ae-command-dialog--drill-into.ae-command-dialog--context-editable .ae-command-dialog__header {
  border-bottom: 3px solid #0d9488;
  background: #e8f6f2;
  color: #172033;
}

.ae-command-dialog--drill-into.ae-command-dialog--context-editable .ae-command-dialog__context-label,
.ae-command-dialog--drill-into.ae-command-dialog--context-editable .ae-command-dialog__mode-badge {
  color: #0f766e;
}

.ae-command-dialog--drill-into.ae-command-dialog--context-editable .ae-command-dialog__mode-badge {
  background: #ccfbf1;
}

.ae-command-dialog--drill-into.ae-command-dialog--context-reference .ae-command-dialog__header {
  border-bottom: 1px solid #cbd5e1;
  background: #f1f5f9;
  color: #172033;
}

.ae-command-dialog--drill-into.ae-command-dialog--context-reference .ae-command-dialog__context-label,
.ae-command-dialog--drill-into.ae-command-dialog--context-reference .ae-command-dialog__mode-badge {
  color: #475569;
}

.ae-command-dialog--drill-into.ae-command-dialog--context-reference .ae-command-dialog__mode-badge {
  background: #e2e8f0;
}

.ae-command-dialog__reference-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 100%;
  padding: 0.2rem 0.55rem;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #f8fafc;
  color: #475569;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
}

.ae-command-dialog__reference-chip:hover,
.ae-command-dialog__reference-chip[aria-expanded="true"] {
  border-color: #94a3b8;
  background: #f1f5f9;
}

.ae-command-dialog__reference-chip:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}

.ae-command-dialog__reference-chip-info {
  color: #94a3b8;
}

.ae-command-dialog__reference-info {
  max-width: 18rem;
  padding: 0.75rem 0.9rem;
  color: #334155;
  font-size: 0.85rem;
  line-height: 1.45;
}

.ae-command-dialog__reference-info > strong {
  display: block;
  margin-bottom: 0.3rem;
  color: #1e293b;
}

.ae-command-dialog__reference-info > p {
  margin: 0;
}

.ae-command-dialog--drill-into.ae-command-dialog--auto-save .ae-command-dialog__close {
  color: #0f766e;
}

.ae-command-dialog--drill-into .ae-command-dialog__close {
  color: #64748b;
  font-size: 1.4rem;
  line-height: 1;
}

.ae-command-dialog--drill-into .ae-command-dialog__body {
  min-width: 0;
  max-height: none;
  padding: 1rem 1.25rem;
  background: #fff;
}

.ae-command-dialog--drill-into .ae-command-dialog__commands {
  padding: 0.875rem 1.25rem;
  border-top: 0;
  background: #fff;
}

.ae-command-dialog__commands--drill-into {
  justify-content: flex-end;
}

.ae-command-dialog__button--open {
  display: inline-flex;
  align-items: center;
}

/* Application actions stay left, the open button sits on the right. */
.ae-command-dialog__commands--drill-into > .ae-command-dialog__button--open {
  margin-left: auto;
}
`

const DOCUMENT_CACHE_SIZE = 500
const DOCUMENT_CACHE_EXPIRE = 60

type QueryCacheEntry = {
  expires?: number
}

interface AsyncFunction {
  (): Promise<void>
}

enum ChangeInspectionResult {
  Skip = 0,
  RequiresEvictionWithPotentialRefresh = 1,
  Import = 2,
  RefreshQueries = 4,
  HardDelete = 8,
  ImportAfterRightsCheck = 16,
  ImportAndRefreshQueries = 6,
}

export default class Aeppic {
  private _id: string = uuid()

  private _model: Model = null
  private _documentCache: DirectCache<Types.Document> = null
  private _activeModelQueries = new Map<string, Promise<Types.Document[]>>()
  private _activeServerQueries = new Map<string, Promise<Types.Document[]>>()
  private _queryInModelMap = new Map<string, QueryCacheEntry>()

  private _vue: Vue = null
  private _vueComponentOptions: VueComponentOptions
  private _environment = process.env.NODE_ENV

  private _localStorage = new Storage('localStorage')
  private _clientUpdate: ClientUpdate|null = null
  private _sessionStorage = new Storage('sessionStorage')
  private _commands: ICommands = null
  private _actions: Actions = null
  private _validations: Validations = null

  private _authenticateWithServer: boolean
  private _authenticationCookies: string = null

  private _fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>

  private _generatedDataUrlCount: number = 0
  private _urlToDataCache: Map<string, any> = new Map()

  private _uploads: Uploads
  private _apiUrl: string
  private _log: Logger
  private _warn: Warn

  private _toasts: Toasts = null

  get Toasts(): Toasts {
    if (!this._toasts) this._toasts = new Toasts()
    return this._toasts
  }

  private _support: SupportApi = null

  get Support(): SupportApi {
    if (!this._support) this._support = createSupport({ enabled: () => !this._dynamic.wasDestroyed && this._features?.isEnabled('support') === true })
    return this._support
  }

  private _localModel = new LocalModel()

  private _createViewComponents(): ViewComponentGroups|null {
    const options = this._features?.getOptionsFor('view-components') as ViewComponentsFeatureOptions

    // The server enables the feature and sends the group keys only with a working compiler.
    if (!this._features?.isEnabled('view-components') || !options?.groupKeys || !isInBrowser()) {
      return null
    }

    return new ViewComponentGroups({
      options,
      packageLoader: references => loadComponentPackages(this, references),
      renderError: DynamicComponent.prototype.renderError,
      // As DynamicComponent: no component stylesheets with the unified stylesheet or disabled stylesheets.
      stylesheets: !this._features.isEnabled('unified-stylesheet') && !this.flags?.disableStylesheets?.enabled,
    })
  }

  private _reportComponentStartup(groups: ViewComponentGroups): void {
    const used = groups.finishStartup()
    const generation = groups.startupGeneration

    if (!used || !generation) {
      return
    }

    void this._fetch(this._getApiUrl('/view-components/startup'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify({ id: uuid(), generation, groups: used }),
    }).catch(() => {
      // Optional optimization telemetry: offline/failing reports do not affect the UI.
    })
  }

  /**
   * Precompiled view components (`view-components` feature): component
   * groups compiled on the server. `preloadList()` gives the groups this page
   * load used, for the feature's `preload` option. Null without the feature.
   */
  get ViewComponents(): ViewComponentGroups|null {
    return this._viewComponents
  }

  /**
   * The server's base hash (`Aeppic-Base-Hash` of `_boot`): keys caches
   * derived from the seed together with DTS values. Null when unknown.
   */
  get baseHash(): string|null {
    return this._baseHash
  }

  get LocalModel(): LocalModelApi {
    return this._localModel
  }

  private _serviceWorkerClient: ServiceWorkerClient | null = null

  /** Origin-wide caching service, using the stable protocol-v1 npm client. */
  public get ServiceWorker(): ServiceWorkerClient {
    if (!this._serviceWorkerClient) {
      const environment = globalThis as any
      this._serviceWorkerClient = environment.aeppicServiceWorker || createServiceWorkerClient()
      this._serviceWorkerClient.setBeforeReset(async () => {
        if (this._serverAdapter instanceof LocalFirstServerAdapter) {
          await this._serverAdapter.closeLocal()
        }
      })
      this._serviceWorkerClient.setClearCaches(clearAeppicClientCaches)
    }
    return this._serviceWorkerClient
  }

  public invalidateCachesAndReload() {
    return this.ServiceWorker.invalidateCachesAndReload()
  }

  private _features: Features

  // private _loadedPackages: PackageCache
  private _status: {
    stage: 'BOOTING' | 'BOOTED' | 'LOADING' | 'LOADED'
  }

  private _flags: Flags = {}

  private _lookup: DocumentLookupCallback = null

  private _modelIncomplete = true
  private _deferredInitialSync: (() => Promise<unknown>) | null = null
  private _initialSyncStarted = false
  private _serverAdapter: IServerAdapter = null
  private _serverChangeSliceStarted = 0
  private _serverChangeYield: Promise<void> | null = null
  /** The server's base hash (`Aeppic-Base-Hash` of `_boot`), null when unknown. */
  private _baseHash: string|null = null
  /** Precompiled view component groups; null without the feature. */
  private _viewComponents: ViewComponentGroups|null = null
  private _hasRightCache = new AccessRightCache()
  private _resolvedLockRightsHasRightCache: ResolveLockRightsHasRightCache = null

  private _globals: any = {}
  private _packages: Packages = null
  private _sharedContext: SharedContext = null
  private _editContextRegistry: EditContextRegistry = null
  private _selection: SelectionFeature = null
  private _navigation: Navigation = null

  private _discoveredElements: HTMLElement[] = []

  private _inspectorDiscoveryPath = []
  private _emitter = new EventEmitter()
  private _defaultReactiveHandler = null
  private _cache: Cache = null
  private _offline: Offline = null


  private _changes: Changes = null
  private _translator = null
  private _preferences = null
  private _services = null
  private _designs: Designs = null
  private _forms: Forms = null
  private _controls: Controls = null
  private _developer = null
  private _history = null
  private _drillIntoDialogStack: DrillIntoDialogStackEntry[] = []
  private _drillIntoDialogDefaults: DrillIntoDialogDefaultsProvider | null = null
  private _formatting = new TextFormatting()
  private _rootContext: Context = null
  private _context: Context = null

  private _allContexts = []

  private _userAgent = ''
  private _deviceCleanup: (() => void) | null = null
  private _deviceState = Vue.observable({ device: <DEVICE>'Desktop', display: readDisplay() })

  private _queryCache: QueryCache = null

  private _dynamic = {
    navigationConfirmation: null as Promise<boolean> | null,
    auth: NO_AUTH,
    connectionStatus: {
      connected: false,
      interrupted: false,
      error: false,
      errorMessage: ''
    },
    querySubscriptions: <QuerySubscription[]>[],
    concurrentLookups: <Promise<void>[]>[],
    pendingPersistenceAdapterNextLookup: <Promise<void>>null,
    pendingPersistenceAdapterLookups: <IDocumentLookup[]>[],
    pendingPersistenceAdapterLookupsSet: new Set<string>(),
    pendingPersistenceAdapterNonImportLookups: <IDocumentLookup[]>[],
    pendingLookups: <string[]>[],
    pendingChanges: <Change[]>[],
    appManifest: <AppManifest>null,
    pendingBooted: <{ promise: Promise<void>, resolve: Function }>null,
    pendingLoaded: <{ promise: Promise<void>, resolve: Function }>null,
    router: <IRouter>null,
    lastDOMId: 0,
    wasDestroyed: false,
    firstLayoutRendered: false,
    queryCount: 0,
    refreshPending: false,
    watcherCleanupPending: false,
    lastRefresh: 0,
    device: <DEVICE>'Desktop',
    unsubscribeFromEditEvents: <() => void> null,
    // responsivenessLevel:  <RESPONSIVE_LEVELS> 'Small',
  }

  private _importQueue = new UniqueItemProcessingQueue<Types.Document>(
    'ImportDocumentQueue',
    {
      keyLookup: (document) => document.id,
      itemProcessingAction: (batch) => this.import(batch as ImportOperation[]),
      itemExcludeCondition: (document) => {
        const existingDocument = this._model.get(document)

        if (existingDocument && existingDocument.v === document.v && existingDocument.t === document.t) {
          return true
        }

        return false
      },
      maxProcessingBatchSize: 100,
      minTimeBetweenProcessingBatchesInMs: 1,
    })

  private _root = Vue.observable({
    type: 'none',
    layout: {
      id: ''
    },
    missingLayout: false,
    params: {},
    history: {
      historic: false,
      timestamp: '',
    }
  })

  private _onlySeeingAnonymousData = true

  private _options: Options

  private _deprecationWarnings = new Set()

  static load(specifiedOptions: Options): Promise<Aeppic> {
    const loading = buildDeferred<Aeppic>()
    // tslint-disable-next-line
    const aeppic = new Aeppic({ ...specifiedOptions, loaded: (a) => loading.resolve(a) })
    return loading.promise
  }

  constructor(specifiedOptions: Options) {
    const options: Options = { ...DEFAULT_OPTIONS, ...specifiedOptions }

    this._options = options
    this._log = buildLogger(options.Logger, { class: 'Aeppic' })
    this._warn = new Warn(this._log)

    this._rootContext = this._buildRootContext()
    this._context = this._rootContext

    this._cache = new Cache(this)
    this._documentCache = new DirectCache(DOCUMENT_CACHE_SIZE, DOCUMENT_CACHE_EXPIRE)

    DateTimeSettings.throwOnInvalid = (options.dontThrowOnInvalidDateTime === true) ? false : true
    this.setDateTimeLocale('auto')

    // console.log('Aeppic:create')
    this._authenticateWithServer = !(options.auth === false)
    this._apiUrl = options.server

    this._fetch = this._bindFetch(options.fetch)
    this._flags = mergeFlags(options.flags, DEFAULT_FLAGS)
    this._status = { stage: 'BOOTING' }

    this._serverAdapter = this._buildServerAdapter(options)
    this._serverAdapter?.setClientId(this._id)

    const uploadOptions: any = {}

    if (options.concurrentUploadLimit != null) {
      uploadOptions.concurrentUploadLimitPerChannel = options.concurrentUploadLimit
    }

    this._uploads = new Uploads(this._serverAdapter, uploadOptions)
    this._ensureUploadFollowUp(this._uploads)

    this._lookup = options.documentLookup
    this._defaultReactiveHandler = this._buildDefaultReactiveHandler()

    if (!this._lookup) {
      if (this._serverAdapter) {
        this._lookup = async (request) => {
          const documentLookups: IDocumentLookup[] = []

          for (let i = 0; i < request.length; i += 2) {
            const id = request[i]
            const v = request[i + 1]
            documentLookups.push({ id, v })

            if (v !== null) {
              documentLookups.push({ id, v: null })
            }
          }

          return this._serverAdapter.getDocuments(documentLookups)
        }
      } else {
        // console.warn('No lookup defined.')
        this._lookup = async () => []
      }
    }

    const self = this

    this._actions = new Actions(this, {
      packageLoader: async (packageRefs) => {
        if (!this._packages) return {}
        return this._loadActionPackages(packageRefs)
      }
    })
    this._commands = new Commands(this, this._createCommandExecutorOptions(<ICommands><any>this._serverAdapter))
    this._validations = new Validations(this)

    this._changes = new Changes(this)
    this._packages = new Packages(this._createPackagesOptions(this._options))
    this._sharedContext = new SharedContext(this)
    this._editContextRegistry = new EditContextRegistry()
    this._editContextRegistry.setFlushCallback((documents, deletedDocuments) => this._flushEditContextDocuments(documents, deletedDocuments))
    this._editContextRegistry.setDiscardCallback((documents) => this._discardEditContextDocuments(documents))
    this._selection = new SelectionFeature()
    this._navigation = new Navigation(this)
    this._translator = new Translator()
    this._history = new History(this)
    this._dynamic.appManifest = new AppManifest()

    this._detectDevice(options.userAgent, options.device)

    this._vueComponentOptions = { ...options.vueComponentOptions }

    //
    // All fields/properties of Aeppic must have been at
    // least been set to null before this point
    //
    // Object.preventExtensions(this)
    this._load(options).then(() => {
      // Initialize optional modules (which are influenced by features which is loaded in this._load)
      this._offline = new Offline(this)
      this._dynamic.appManifest.configureDefaults(this._features.getOptionsFor(APP_MANIFEST_FEATURE))

      Object.freeze(this)
      options.loaded?.(this)
    })
  }

  private _getLocalTemporaryAnonymousAccountId() {
    return ''

    const knownAnonymousId = this._localStorage.getItem('ae-temporary-anonymous-id')

    if (knownAnonymousId) {
      return knownAnonymousId
    } else {
      const id = uuid()
      this._localStorage.setItem('ae-temporary-anonymous-id', id)
      return id
    }
  }

  private _buildRootContext() {
    const ctx = new Context('Aeppic', {
      Logger: this._log,
      Query: Scopes,
    })

    ctx.rootElement = this._vue?.$el ?? null

    return ctx
  }

  /** Set by Aeppic.LocalModel.disable(); wins over the feature flag. */
  private _localReadModelSwitchedOff() {
    return this._localStorage.getValue(LOCAL_READ_MODEL_SWITCHED_OFF_KEY) === true
  }

  private _localReadModelWanted() {
    return this._features?.isEnabled(LOCAL_READ_MODEL_FEATURE) === true && !this._localReadModelSwitchedOff()
  }

  private _buildServerAdapter(options: Options) {
    let adapter: IServerAdapter
    if (options.persistenceAdapter) {
      if (!options.persistenceAdapter.on) {
        throw new Error('PersistenceManager must implement basic EventEmitter')
      }
      adapter = options.persistenceAdapter
    } else if (options.server) {
      adapter = new ServerAdapter(options.server, this._fetch, null, options.WebSocket, options.serverQueryBatchSize, options.serverQueryTimeoutMs, () => {
        if (!this._features?.isEnabled('websocket-change-batching')) return null
        return this._features.getOptionsFor('websocket-change-batching')
      })
    } else {
      if (options.localReadModel || options.localReadModelFactory) {
        throw new Error('A local read model requires a remote persistence adapter or server')
      }
      return null
    }

    if (options.localReadModel && options.localReadModelFactory) {
      throw new Error('Specify either localReadModel or localReadModelFactory, not both')
    }

    const localReadModel = options.localReadModel ?? options.localReadModelFactory
    if (localReadModel) {
      const localFirstAdapter = new LocalFirstServerAdapter(adapter, localReadModel, {
        enabled: () => this._localReadModelWanted(),
        monitor: this._localModel,
      })
      localFirstAdapter.on('local-model-disabled', () => {
        this._localStorage.setValue(LOCAL_READ_MODEL_MEMORY_KEY, false)
        if (typeof window !== 'undefined' && typeof window.location?.reload === 'function') {
          window.location.reload()
        }
      })
      this._localModel.setRetryHandler(async () => {
        const featureOptions = this._features?.getOptionsFor(LOCAL_READ_MODEL_FEATURE)
        const local = await localFirstAdapter.retryLocal(featureOptions)
        if (!local) return
        await localFirstAdapter.finishBootSeed()
        await localFirstAdapter.startInitialSync(featureOptions?.startupWaitMs ?? 5000, {
          blockUntilCriticalReady: featureOptions?.blockUntilCriticalReady === true,
        })
      })
      this._localModel.setSwitchHandlers({
        isDisabled: () => this._localReadModelSwitchedOff(),
        disable: async () => {
          this._localStorage.setValue(LOCAL_READ_MODEL_SWITCHED_OFF_KEY, true)
          this._localStorage.setValue(LOCAL_READ_MODEL_MEMORY_KEY, false)
          await localFirstAdapter.closeLocal()
        },
        enable: async () => {
          this._localStorage.removeItem(LOCAL_READ_MODEL_SWITCHED_OFF_KEY)
          if (!this._localReadModelWanted()) return
          this._localStorage.setValue(LOCAL_READ_MODEL_MEMORY_KEY, true)
          await this._localModel.retry()
        },
      })
      adapter = localFirstAdapter
    }
    this._wireServerAdapter(adapter)
    return adapter
  }

  private _wireServerAdapter(adapter: IServerAdapter) {
    adapter.on('changes:batch', (changes, sliceMs, registerCompletion) => {
      if (this._dynamic.wasDestroyed || (this._authenticateWithServer && !this.Auth?.loggedIn)) return
      for (const change of changes) this._resolvedLockRightsHasRightCache?.clearIfAffected(change)
      registerCompletion?.(this._importServerChangeBatch(changes, sliceMs))
    })
    adapter.on('change', (change) => {
      if (!this._authenticateWithServer || (this.Auth && this.Auth.loggedIn)) {
        this._resolvedLockRightsHasRightCache?.clearIfAffected(change)
        this._importChanges([change])
        this._emitter.emit('change', change)
      }
    })

    adapter.on('connection:status:changed', (status) => {
      this._dynamic.connectionStatus.connected = status.connected
      this._dynamic.connectionStatus.interrupted = status.interrupted
      this._dynamic.connectionStatus.error = status.error
      this._dynamic.connectionStatus.errorMessage = status.errorMessage

      this._emitter.emit('connection:status:changed', this._dynamic.connectionStatus)
    })

    let serviceWorkerBuild: string | null = null
    adapter.on('service-worker:version', (build: string) => {
      if (serviceWorkerBuild === build || !isInBrowser()) return
      serviceWorkerBuild = build
      void this.ServiceWorker.checkForUpdate().catch(error => this._log.warn(error, 'Service-worker update check failed'))
    })

    adapter.on('server:changed', () => this._emitter.emit('server:changed'))
    adapter.on('client:outdated', (request) => {
      this._emitter.emit('client:outdated', request)
      if (isInBrowser()) {
        this._clientUpdate ??= new ClientUpdate()
        this._clientUpdate.requestReload(request)
      }
    })
    adapter.on('server:message', (message) => this._emitter.emit('server:message', message))

    adapter.on('changes:missed', () => this._emitter.emit('changes:missed'))
    adapter.on('changes:missed-found', () => this._emitter.emit('changes:missed-found'))
    adapter.on('local:error', error => {
      this._log.warn('Local read model failed; continuing with remote reads. %s', error?.message ?? error)
    })
  }

  private _importServerChangeBatch(changes: any[], sliceMs: number): Map<string, Promise<void>> {
    const completions = new Map<string, Promise<void>>()
    for (const change of changes) {
      const id = change.document?.id
      if (!id) continue
      const previous = completions.get(id) ?? Promise.resolve()
      const completion = previous.then(async () => {
        await this._yieldServerChangeSlice(sliceMs)
        if (this._dynamic.wasDestroyed) return
        // Inspect each version after its predecessor has been applied. Inspecting
        // all versions first can skip the newest version as an apparent duplicate.
        await this._importChanges([change])
        if (!this._dynamic.wasDestroyed) this._emitter.emit('change', change)
      }).catch(error => this._log.error({ error }, 'Error importing batched server change'))
      completions.set(id, completion)
    }
    return completions
  }

  private async _yieldServerChangeSlice(sliceMs: number): Promise<void> {
    const budget = Math.max(1, Math.min(8, Number.isFinite(sliceMs) ? sliceMs : 4))
    if (this._serverChangeYield) return this._serverChangeYield
    if (Date.now() - this._serverChangeSliceStarted < budget) return
    this._serverChangeYield = new Promise<void>(resolve => setTimeout(resolve, 0)).then(() => {
      this._serverChangeSliceStarted = Date.now()
      this._serverChangeYield = null
    })
    return this._serverChangeYield
  }

  private async _importChanges(changes: any[]) {
    let queryRefreshRequired = false

    const allDocumentsToImport: (Types.Document|Promise<Types.Document>)[] = []

    for (const update of changes) {
      const document = update.document as Types.Document

      if (!document) {
        continue
      }

      this._queryCache?.updateCacheForDocument(document)

      const check = this._inspectChangeToImport(update)

      if (check === ChangeInspectionResult.Skip) {
        continue
      }

      if (check & ChangeInspectionResult.RefreshQueries) {
        queryRefreshRequired = true
      }

      if (check === ChangeInspectionResult.HardDelete) {
        this._model.deleteHard(document.id)
        continue
      }

      if (check & ChangeInspectionResult.RequiresEvictionWithPotentialRefresh) {
        const wasEvicted = this._model.evict(document.id)

        if (wasEvicted) {
          queryRefreshRequired = true
        }

        continue
      }

      if (check & ChangeInspectionResult.ImportAfterRightsCheck) {
        const documentIfRight = this.hasRight(document).then(hasRight => hasRight ? document : null)
        allDocumentsToImport.push(documentIfRight)
        continue
      }

      if (check & ChangeInspectionResult.Import) {
        allDocumentsToImport.push(document)
      }
    }

    const documentsToImport = await Promise.all(allDocumentsToImport)

    for (const documentToImport of documentsToImport) {
      if (this._dynamic.wasDestroyed) return
      if (documentToImport) {
        this._model.import(documentToImport)
      }
    }

    if (queryRefreshRequired) {
      this._scheduleRefresh()
    }
  }

  private _inspectChangeToImport({ document, type }: { document: Types.Document, type: string }): ChangeInspectionResult {
    const isRoot = document.id === 'root'

    if (isRoot) {
      // Changes to root should always get imported and cause a full refresh
      return ChangeInspectionResult.ImportAndRefreshQueries
    }

    // A hard delete is rather rare, do a full refresh
    if (type === 'deleted-hard') {
      return ChangeInspectionResult.HardDelete | ChangeInspectionResult.RefreshQueries
    }

    const formId = document.f?.id

    if (!formId) {
      return ChangeInspectionResult.Skip
    }

    // If the document is a form, import it and refresh. Does not
    // happen that often anyway.
    const documentFormIsForm = 'form' === formId

    if (documentFormIsForm) {
      return ChangeInspectionResult.ImportAndRefreshQueries
    }

    // There are a number of security related changes which could influence all queries.
    const isChangeToCurrentAccount = this.Account?.id == document.id

    if (isChangeToCurrentAccount) {
      return ChangeInspectionResult.ImportAndRefreshQueries
    }

    const isGroupChange = 'group' === formId

    if (isGroupChange) {
      return ChangeInspectionResult.ImportAndRefreshQueries
    }

    const isUnknownParent = !this.Model.get(document.p)

    // locks and keys may may be sent in the order LOCKED_DOCUMENT -> LOCK -> KEY.
    // Once the LOCK for the LOCKED_DOCUMENT was imported, the key couldn't be imported because the account did not have access to the LOCKED_DOCUMENT yet.
    // => always import keys and locks without access checks
    const isKeyChange = 'key-form' === formId
    const isLockChange = 'lock-form' === formId

    // If the change changed a lock
    if (isKeyChange || isLockChange) {
      if (isUnknownParent) {
        return ChangeInspectionResult.RefreshQueries
      } else {
        return ChangeInspectionResult.ImportAndRefreshQueries
      }
    }

    // If the document moved away from a previously known parent (not the recycler)
    // we should import and check rights.
    //
    // If it did come from the recycler it matters where it is moved to (which is handled below)
    const previousParent = document.previous?.p
    const previousParentKnownAndNotRecycler = previousParent && previousParent !== 'recycler' && !!this._model.get(previousParent)

    if (previousParentKnownAndNotRecycler) {
      // The document moved so we might need to check auth
      return ChangeInspectionResult.ImportAfterRightsCheck | ChangeInspectionResult.RefreshQueries
    }

    // If the document is moved into the recycler (and we did not know the parent before) we can skip
    // the import just evict to be sure
    if (document.p === 'recycler') {
      return ChangeInspectionResult.RequiresEvictionWithPotentialRefresh
    }

    // If the document was moved and the parent is unknown and the previous parent wasn't known either
    // (see check above) we can skip the import
    if (isUnknownParent) {
      return ChangeInspectionResult.RequiresEvictionWithPotentialRefresh
    }

    const isDocumentUnknownToThisLocalModel = !!this._model.get(document)

    // If the document already exists in the model we wont check for rights,
    // and assume read access, but if it is unknown we need to check
    if (isDocumentUnknownToThisLocalModel) {
      return ChangeInspectionResult.ImportAfterRightsCheck | ChangeInspectionResult.RefreshQueries
    }

    // This updates a known document in the model.
    // TODO: Insert into queries more cleverly instead of running a refresh
    return ChangeInspectionResult.ImportAndRefreshQueries
  }

  private _createCommandExecutorOptions(server: ICommands): { serverExecutor: ICommands, packageLoader?: import('./actions.js').ActionPackageLoader } {
    return {
      serverExecutor: server,
      packageLoader: async (packageRefs) => {
        if (!this._packages) return {}
        return this._loadActionPackages(packageRefs)
      }
    }
  }

  private _createPackagesOptions(options: Options): PackagesOptions {
    return {
      Aeppic: this,
      // transform: options.packageTransform,
    packagesLookupUrl: options.packagesLookupUrl ?? '/api/packages',
      fetch: (...args) => (<any>this._fetch)(...args),
    }
  }

  // The instance's zone (feature `timezone`, added by the server from its
  // `timezone` setting); DateTime values are stored in it.
  private _instanceTimezone(): string | null {
    const timezone = this._features.getOptionsFor('timezone')?.name

    if (typeof timezone !== 'string' || !timezone) {
      return null
    }

    if (!LuxonInfo.isValidIANAZone(timezone)) {
      console.warn(`Ignoring unknown instance time zone "${timezone}" (feature timezone)`)
      return null
    }

    return timezone
  }

  private _createModelOptions(customIndexing: ModelOptions['customIndexing'], createFileURL: ModelOptions['createFileURL'], setTimestamps, defaultPageLimit, searchAllFields, usesServerAuthentication) {
    const options: ModelOptions = {
      clientId: this._id,
      createFileURL,
      setTimestamps,
      customIndexing,
      parseFormViews: true,
      typeValidation: {
        strictTemporalTypes: this._features.isEnabled('typeValidation') &&
          this._features.getOptionsFor('typeValidation').strictTemporalTypes === true,
        dateTimeToDateFieldMode: this._features.isEnabled('typeValidation') &&
          this._features.getOptionsFor('typeValidation').dateTimeToDateFieldMode === 'strip'
          ? 'strip'
          : 'preserve',
        timezone: this._instanceTimezone(),
      },
    }

    if (usesServerAuthentication) {
      // There is no need to filter client side reads when the server already
      // filters the received data out
      options.dontFilterReads = true
    }

    if (defaultPageLimit != null) {
      options.defaultPageLimit = defaultPageLimit
    }

    if (searchAllFields) {
      options.searchAllFields = searchAllFields
    }

    // Configure Features
    options.features = []

    if (this._features.isEnabled('meta-modified')) {
      options.features.push(ModelFeature.MetaModified)
    }

    if (this._features.isEnabled('advanced-indexing')) {
      options.features.push(ModelFeature.AdvancedIndexing)
    }

    return options
  }

  private _ensureUploadFollowUp(uploads: Uploads) {
    uploads.on('uploaded', (document) => {
      const pendingChanges = this._dynamic.pendingChanges
      this._dynamic.pendingChanges = []

      this._tryToWriteChangesToPersistenceAdapter(pendingChanges)
    })
  }

  /**
   * Return the function to be used to build object urls from Files/Blobs and Node.js Buffers/Streams
   */
  private _buildAddToDataCacheFunction(clientId: string): typeof URL.createObjectURL {
    let self = this

    // In a Node.js context we need to simulate this
    return (object: any, options?: any) => {
      if (typeof object === 'string' &&
        object.startsWith('data:') &&
        object.length <= MAX_DATA_URL_LENGTH) {
        return object
      }

      const url = self._buildNewFileDataURL()
      self._urlToDataCache.set(url, object)
      return url
    }
  }

  private _buildNewFileDataURL() {
    this._generatedDataUrlCount++
    return `aeppic-local://${this._id}/${this._generatedDataUrlCount}`
  }

  get translator() {
    this._deprecated('Aeppic.translator is deprecated. Use Translator instead. Property was renamed.')
    return this.Translator
  }

  public translate(...keys) {
    return this._translator.translate(...keys)
  }


  public async newAeppic(objectUrlToDocumentsJsonlBlob, rootElementSelector) {
    return new Promise((resolve, reject) => {
      const newAeppicInstance = new Aeppic({
        auth: false,
        el: rootElementSelector,
        boot: {
          src: 'loaded-jsonl',
        },
        booted: async () => resolve(newAeppicInstance),
        fetch: async () => await this._fetch(objectUrlToDocumentsJsonlBlob, { credentials: 'include' })
      })
    })
  }

  public async compareDocumentTrees(otherAeppicInstance, rootDocumentIds, cbProgress, options?: { findOptions?: FindOptions }) {
    const thisAeppicInstance = this
    const adapter = new DefaultDocumentAdapter(otherAeppicInstance, thisAeppicInstance, cbProgress)

    if (Array.isArray(rootDocumentIds)) {
      // new method signature
      return compareDocumentSubTrees(rootDocumentIds, adapter, options)
    }
    else {
      // old method signature
      const rootDocumentId = rootDocumentIds
      const rootDocument1 = await adapter.getDocumentAsync(1, rootDocumentId)
      const rootDocument2 = await adapter.getDocumentAsync(2, rootDocumentId)

      return compareDocumentTrees(rootDocument1, rootDocument2, adapter, options)
    }
  }


  get flags() { return this._flags }

  public setTimeout(callback, timeout?: number, ...args): any {
    if (this.context.isReleased) {
      return null
    }

    const id = setTimeout(() => {
      this.context.releaseUnnamedResource('TIMER', id)
      callback()
    }, timeout, ...args)

    this.context.addUnnamedResource('TIMER', id)

    return id
  }


  public clearTimeout(timeoutId) {
    this.context.releaseUnnamedResource('TIMER', timeoutId)
    clearTimeout(timeoutId)
  }

  public setInterval(callback, interval?: number, ...args) {
    if (this.context.isReleased) {
      return
    }

    const id = setInterval(callback, interval, ...args)
    this.context.addUnnamedResource('INTERVAL', id)
    return id
  }

  public clearInterval(intervalId) {
    this.context.releaseUnnamedResource('INTERVAL', intervalId)
    clearInterval(intervalId)
  }

  debounce(callback, wait?, context = this) {
    return debounce(callback, wait, context, (cb, timeout, ...args) => this.setTimeout(cb, timeout, ...args))
  }

  newDOMId() {
    this._dynamic.lastDOMId += 1
    return 'ae_i_' + this._dynamic.lastDOMId
  }

  get wellKnownClasses() {
    return WELL_KNOWN_CLASSES
  }

  get Log() {
    return this.context.Log || this._log
  }

  get Warn() {
    return this._warn
  }

  set Router(router: IRouter) {
    this._dynamic.router = router
  }

  get Router(): IRouter {
    return this._dynamic.router
  }

  static get ModelConstructor() {
    return Model
  }

  public static get Version() { return Release.VERSION }
  public get Version() { return Aeppic.Version }

  public get Status() { return this._status }
  public get Root() { return this._root }
  public get Model() { return this._model }
  public get Cache() { return this._cache }
  public get Offline() { return this._offline }

  private _bindFetch(customFetch: typeof fetch): (input: RequestInfo, init?: RequestInit) => Promise<Response> {
    if (customFetch === null) {
      return null
    } else if (customFetch) {
      return (input: RequestInfo, init?: RequestInit) => customFetch(input, init)
    } else {
      const globalFetch = typeof fetch !== 'undefined' ? fetch : null

      if (!globalFetch) {
        // console.warn('No fetch defined')
        return null
      }

      return (input: RequestInfo, init?: RequestInit) => globalFetch(input, init)
    }
  }

  public get Auth() {
    return this._dynamic.auth
  }

  public get Account() {
    if (this._dynamic.auth) {
      return this._model.get(this._dynamic.auth.accountId)
    }
  }

  public get GroupAccount() {
    if (this._dynamic.auth) {
      return this._model.get(this._dynamic.auth.groupAccountId)
    }
  }

  public get HomePage() {
    if (this._dynamic.auth && this._dynamic.auth.info && this._dynamic.auth.info.defaultPage && this._dynamic.auth.info.defaultPage.id) {
      return this._dynamic.auth.info.defaultPage
    } else {
      return { id: 'root' }
    }
  }

  public get ProfilePage() {
    if (this._dynamic.auth && this._dynamic.auth.profile) {
      return this._dynamic.auth.profile
    } else {
      return null
    }
  }

  public async getProfilePageForAccount(accountIdentifier: Types.IdOrReference | Types.Document) {
    const account = await this.get(accountIdentifier)

    if (!account || account.f.id !== 'account') {
      return null
    }

    if (account.data.parentIsProfile) {
      return this._get(account.p)
    }

    if (account.data.profilePage) {
      return this._get(<Types.Reference>account.data.profilePage)
    }

    return null
  }

  public async getAccountForProfilePage(profilePageIdentifier: Types.IdOrReference | Types.Document) {
    const profile = await this.get(profilePageIdentifier)

    if (!profile) {
      return null
    }

    const asProfileParent = await this.findOne(`p:${profile.id} f.id:account data.parentIsProfile:true`, { includeHidden: true })

    if (asProfileParent) {
      return asProfileParent
    }

    const asProfileRef = await this.findOne(`f.id:account data.profilePage.id:${profile.id}`, { includeHidden: true })

    if (asProfileRef) {
      return asProfileRef
    }

    return null
  }

  public async navigateHome() {
    if (!this._dynamic.router) {
      return
    }

    this._dynamic.router.navigateToUrl('/')
  }

  public async navigateToProfile() {
    const page = await this.get(this.ProfilePage)

    if (page) {
      this.navigateTo(page.id)
    }
  }

  public async sleep(ms: number) {
    return new Promise((resolve) => this.setTimeout(resolve, ms))
  }

  public async login(username: string, password: string, remember = false, loadData = true, impersonationToken?: string) {
    this._hasRightCache.clear()
    this._resolvedLockRightsHasRightCache?.clear()

    if (this._authenticateWithServer) {
      const response = await this._requestServerLogin(username, password, remember, impersonationToken)
      if (response.ok) {
        return this._completeServerLogin(response, loadData)
      } else {
        let payload: any = null

        try {
          payload = await response.json()
        } catch (error) {
          payload = null
        }

        return {
          ok: false,
          status: response.status,
          error: payload && payload.error,
          resetPassword: payload && payload.resetPassword,
          payload,
        }
      }
    }
  }

  private async _requestServerLogin(username: string, password: string, remember = false, impersonationToken?: string) {
    let headers: any

    if (typeof Headers !== 'undefined') {
      headers = new Headers()
      headers.append('Accept', 'application/json')
      headers.append('Content-Type', 'application/x-www-form-urlencoded')
    } else {
      headers = {}
      headers['Accept'] = 'application/json'
      headers['Content-Type'] = 'application/x-www-form-urlencoded'
    }

    let body = ''

    if (impersonationToken) {
      body = `impersonationToken=${encodeURIComponent(impersonationToken)}`
    } else {
      body = `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}` + (remember ? `&remember=true` : '')
    }

    const loginUrl = this._getApiUrl('/auth/_login')

    return this._fetch(loginUrl, { method: 'POST', headers, body, credentials: 'include' })
  }

  public async resetPassword(username: string, password: string, newPassword: string, remember = false, loadData = true) {
    if (!this._authenticateWithServer) {
      return {
        ok: false,
        status: 0
      }
    }

    let headers: any

    if (typeof Headers !== 'undefined') {
      headers = new Headers()
      headers.append('Accept', 'application/json')
      headers.append('Content-Type', 'application/x-www-form-urlencoded')
    } else {
      headers = {}
      headers['Accept'] = 'application/json'
      headers['Content-Type'] = 'application/x-www-form-urlencoded'
    }

    const body = `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&newPassword=${encodeURIComponent(newPassword)}` + (remember ? `&remember=true` : '')
    const resetPasswordUrl = this._getApiUrl('/auth/_resetPassword')
    const response = await this._fetch(resetPasswordUrl, { method: 'POST', headers, body, credentials: 'include' })

    if (response.ok) {
      return this._completeServerLogin(response, loadData)
    }

    let payload: any = null

    try {
      payload = await response.json()
    } catch (error) {
      payload = null
    }

    return {
      ok: response.ok,
      status: response.status,
      error: payload && payload.error,
      resetPassword: payload && payload.resetPassword,
      payload,
    }
  }

  private async _completeServerLogin(response: any, loadData = true) {
    if (this._apiUrl.startsWith('http')) {
      const cookies = getCookies(response)
      const cookieValues = cookies.map(c => c.replace(/;.*$/, ''))
      this._authenticationCookies = cookieValues.join('; ')
      this._serverAdapter.setCookies(this._authenticationCookies)
    }

    const payload = await response.json()

    this._dynamic.auth = JSON.parse(JSON.stringify(NO_AUTH))
    this._dynamic.auth.loggedIn = true
    this._dynamic.auth.accountId = payload.accountId
    this._dynamic.auth.backedBy = payload.backedBy
    this._dynamic.auth.impersonatedBy = payload.impersonatedBy

    this._dynamic.auth.profile = payload.profile
    this._dynamic.auth.info = payload.info

    this.setDateTimeLocale(payload.info?.locale ?? 'auto')

    this._onlySeeingAnonymousData = false
    this._model.setAccountInfo(this._dynamic.auth.accountId, this._dynamic.auth.backedBy, this._dynamic.auth.impersonatedBy)

    await this._get([this._dynamic.auth.accountId, this._dynamic.auth.groupAccountId, this._dynamic.auth.backedBy, this._dynamic.auth.impersonatedBy])

    if (loadData) {
      await this._loadDocumentsAfterLogin()
    }

    this._emitter.emit('login', this._dynamic.auth)

    return {
      ok: true,
      auth: this._dynamic.auth,
      status: response.status
    }
  }

  private async _loadSecurity() {
    // if (this._dynamic.auth) {
    //   // TODO: Not required when rights calls executed against server
    //   const groupsPromise = this.find('f.id:group', { size: 5000 })
    //   const keysPromise = this.find('f.id:key-form', { size: 5000 })

    //   const [groups, keys] = await Promise.all([groupsPromise, keysPromise])

    //   this.Log.debug('Loaded security #%d groups and #%d keys', groups.length, keys.length)
    // }
  }

  private _getApiUrl(route = '') {
    if (this._apiUrl) {
      return this._apiUrl + route
    } else {
      return route
    }
  }

  public async logout() {
    this._toasts?.clear()
    await this._support?.clear()
    this._hasRightCache.clear()
    this._resolvedLockRightsHasRightCache?.clear()

    let idpLogoutRedirect: string | null = null

    if (this._authenticateWithServer) {
      const logoutUrl = this._getApiUrl('/auth/_logout')
      const response = await this._fetch(logoutUrl, { method: 'POST', body: '', credentials: 'include' })

      if (!response.ok) {
        console.error('Could not logout correctly (No Server connection?)')
        return
      }

      // Check if server returned a redirect (e.g. SAML IdP logout URL)
      try {
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json()
          if (data.redirect) {
            idpLogoutRedirect = data.redirect
          }
        }
      } catch {
        // No JSON body or parse error — continue with normal logout
      }

      // NOT needed as long as reload is triggered below
      // this._evictAuthenticatedServerData()
    }

    if (this._serverAdapter) {
      // This does NOT reset cookies for group accounts etc yet. This will therefore
      // cause issues in node.js usage. logout is not really supported for that
      // use case
      this._serverAdapter.setCookies(null)
    }

    this._model.setAccountInfo(this._getLocalTemporaryAnonymousAccountId(), 'anonymous')

    await this._updateAuthInfo()
    this.setDateTimeLocale('auto')

    this._emitter.emit('logout', this._dynamic.auth)

    // If the server returned an IdP logout redirect (SAML SLO), navigate there
    // instead of reloading — the IdP will terminate its session
    if (idpLogoutRedirect && isInBrowser()) {
      window.location.href = idpLogoutRedirect
      return
    }

    this.navigateHome()

    // If window is not available, we are in a node.js context and
    // reload is not required

    if (isInBrowser()) {
      setTimeout(() => {
        (<any>window.location.reload)(true)
      }, 5)
    }
  }

  public reload(clearCache = true) {
    if (isInBrowser()) {
      (<any>window.location.reload)(clearCache)
    }
  }

  private _evictAuthenticatedServerData() {
    const keepRootChildren = ['system', 'apps', 'recycler']

    const rootChildren = this._model.find(`p:root`, { sort: null })
    const rootChildrenToEvict = rootChildren.filter(d => keepRootChildren.indexOf(d.id) === -1)

    for (const childToEvict of rootChildrenToEvict) {
      this._model.evict(childToEvict)
    }

    this._model.evict('recycler', { onlyDescendants: true })

    this._onlySeeingAnonymousData = true

    this._scheduleRefresh()
  }

  // TODO: Check behavior in local mode
  private async _updateAuthInfo(response?: Response) {
    this._hasRightCache.clear()
    this._resolvedLockRightsHasRightCache?.clear()

    if (this._authenticateWithServer) {
      const authInfoUrl = this._getApiUrl('/auth/info')
      response = response ?? await this._fetch(authInfoUrl, { credentials: 'include' })

      if (response.ok) {
        this._dynamic.auth = await response.json()

        if (this._dynamic.auth.loggedIn || this._dynamic.auth.backedBy) {
          this._onlySeeingAnonymousData = false
          this._model.setAccountInfo(this._dynamic.auth.accountId, this._dynamic.auth.backedBy, this._dynamic.auth.impersonatedBy)
        } else {
          this._onlySeeingAnonymousData = true
          this._dynamic.auth = JSON.parse(JSON.stringify(NO_AUTH))
        }
      } else {
        this._dynamic.auth = JSON.parse(JSON.stringify(NO_AUTH))
        this._onlySeeingAnonymousData = true
      }
    } else {
      this._onlySeeingAnonymousData = false
      this._dynamic.auth = JSON.parse(JSON.stringify(NO_AUTH))
    }
  }

  public download(url = '/api') {
    return this._fetchFromServerApi('GET', url)
  }

  private _fetchFromServerApi(method = 'GET', url = '/api/docs/_all', body?) {
    const headers: any = this._buildHeaders()

    if (typeof Headers !== 'undefined') {
      if (!this._authenticationCookies) {
        (<Headers>headers).delete('Cookie')
      } else {
        (<Headers>headers).append('Cookie', this._authenticationCookies)
      }
    } else {
      if (!this._authenticationCookies) {
        delete headers.Cookie
      } else {
        headers['Cookie'] = this._authenticationCookies
      }
    }

    return this.fetch(url, { method, headers, body, credentials: 'include' })
  }

  private _buildHeaders() {
    if (typeof Headers !== 'undefined') {
      const headers = new Headers()
      headers.append('Accept', 'application/json')
      headers.append('Content-Type', 'application/json')
      return headers
    } else {
      const headers = {}
      headers['Accept'] = 'application/json'
      headers['Content-Type'] = 'application/json'
      return headers
  }
  }

  private async _load(options: Options) {
    // Stays open until design, component, control, and list loads go quiet.
    perf.begin('ui')
    // Open OPFS during boot only when this browser already had the local model on.
    // A first visit waits for the feature flag. Synchronization still waits for the boot seed.
    if (this._serverAdapter instanceof LocalFirstServerAdapter) this._serverAdapter.beginBootSeed()
    const rememberedLocalReadModel = this._localStorage.getValue(LOCAL_READ_MODEL_MEMORY_KEY) === true
      && !this._localReadModelSwitchedOff()
    const preloadSpan = rememberedLocalReadModel ? perf.start('boot:local-preload') : null
    const localPreload = rememberedLocalReadModel && this._serverAdapter instanceof LocalFirstServerAdapter
      ? this._serverAdapter.preloadLocal()
      : null
    void Promise.resolve(localPreload).finally(() => preloadSpan?.end())

    // Dispatch boot first. Features and authentication overlap its download;
    // none of these requests needs a model, preferences, or a mounted Vue root.
    const bootResponse = perf.track('boot:fetch', () =>
      typeof options.boot?.src === 'string'
        ? this._fetchFromServerApi('GET', options.boot.src)
        : Promise.resolve(null))

    const featuresReady = perf.track('boot:features', async () => {
      if (options.features) {
        this._features = new Features(options.features, { Logger: this._log })
      } else if (this._fetch && process.env.NODE_ENV !== 'test') {
        const lookupUrl = this._getApiUrl(options.featuresLookupPath)
        this._features = await Features.buildFromServer(this._fetch, { Logger: this._log, lookupUrl })
      } else {
        this._features = new Features({}, { Logger: this._log })
      }

      if (isInBrowser() && 'serviceWorker' in navigator) {
        void this.ServiceWorker.ready().catch(error => this._log.warn(error, 'Service worker unavailable'))
      }

      if (this._features.isEnabled(CLIENT_CACHE_SEQUENCE_FEATURE)) {
        const sequence = this._features.getOptionsFor(CLIENT_CACHE_SEQUENCE_FEATURE).sequence
        const localFirstServerAdapter = this._serverAdapter instanceof LocalFirstServerAdapter
          ? this._serverAdapter
          : null
        const closeLocalReadModel = localFirstServerAdapter
          ? () => localFirstServerAdapter.disableLocalModel()
          : undefined
        const reloading = await applyClientCacheSequence(sequence, undefined, closeLocalReadModel,
          isInBrowser() && 'serviceWorker' in navigator
            ? () => this.invalidateCachesAndReload()
            : undefined)
        if (reloading) {
          return false
        }
      }

      if (this._serverAdapter instanceof LocalFirstServerAdapter) {
        const localReadModelEnabled = this._features.isEnabled(LOCAL_READ_MODEL_FEATURE)
        this._localStorage.setValue(LOCAL_READ_MODEL_MEMORY_KEY, localReadModelEnabled && !this._localReadModelSwitchedOff())
        if (localReadModelEnabled) {
          // Switched off in this browser: initializeLocal reports it as disabled
          // and keeps the local data for LocalModel.enable().
          void this._serverAdapter.initializeLocal(this._features.getOptionsFor(LOCAL_READ_MODEL_FEATURE))
        } else {
          void this._serverAdapter.disableLocalModel()
        }
      }

      return true
    })

    const loginAtStartup = this._authenticateWithServer && hasLoginInfo(options.auth)
    const authResponse = perf.track('boot:auth-fetch', () => {
      if (!this._authenticateWithServer) return Promise.resolve(null)
      if (hasLoginInfo(options.auth)) {
        return this._requestServerLogin(options.auth.username, options.auth.password)
      }
      return this._fetch(this._getApiUrl('/auth/info'), { credentials: 'include' })
    })

    // Boot import and seeding do not wait for auth. The feature flag decides
    // whether the preloaded worker may receive the current boot documents.
    // A sequence change reloads the page; don't wait for a potentially large
    // boot stream before allowing that reload to happen.
    void bootResponse.catch(() => {})
    const bootReady = featuresReady.then(async (shouldContinueBoot) => {
      if (!shouldContinueBoot) {
        return false
      }

      const response = await bootResponse

      // Proxy mode is now always enabled on EditableDocument

      // Caching
      if (this._features.isEnabled('query-cache')) {
        const options = this._features.getOptionsFor('query-cache')
        this._queryCache = new QueryCache(options, (query: Query.Query, options: FindOptions) => this._findUncached(query, options));
      }

      if (this._serverAdapter) {
        this._resolvedLockRightsHasRightCache = new ResolveLockRightsHasRightCache(this, this._serverAdapter)
      }

      // Load Model
      const createFileURL = this._buildAddToDataCacheFunction(this._id)

      this._model = new Model(this._createModelOptions(options.customIndexing, createFileURL, options.setTimestamps, options.defaultPageLimit, options.searchAllFields, this._authenticateWithServer))
      if (this._features.isEnabled('units')) {
        this._model.setDefaultUnits(this._features.getOptionsFor('units'))
      }
      this._model.setAccountInfo(this._getLocalTemporaryAnonymousAccountId(), 'anonymous')

      // The server's base hash keys caches derived from the seed (the local
      // model, precompiled view components) together with DTS values.
      this._baseHash = response?.headers?.get?.('Aeppic-Base-Hash') ?? null

      await perf.track('boot:import', async () => {
        if (response) {
          await this._loadDocumentsFromResponse(response, options.boot, this._lookup, 0, true)
        } else if (options.boot) {
          await this._loadDocuments(options.boot, this._lookup, 0, true)
        }
      })
      if (this._serverAdapter instanceof LocalFirstServerAdapter) {
        // This queues the completion marker; it does not await the worker.
        void this._serverAdapter.finishBootSeed({ baseHash: this._baseHash })
      }

      return true
    })

    const [shouldContinueBoot, response] = await Promise.all([bootReady, authResponse])
    if (!shouldContinueBoot) {
      return
    }
    await perf.track('boot:auth', async () => {
      if (loginAtStartup && response?.ok) {
        await this._completeServerLogin(response, false)
      } else if (!loginAtStartup) {
        await this._updateAuthInfo(response)
      }
    })

    this._preferences = new Preferences(this)
    await perf.track('boot:preferences', () => this._preferences.delayUntilLoaded())
    this._services = new Services(this)
    this._designs = new Designs(this)
    this._forms = new Forms(this)
    this._controls = new Controls(this)
    this._developer = new Developer(this)

    if (this._serverAdapter instanceof LocalFirstServerAdapter) {
      const localReadModelAdapter = this._serverAdapter
      const localReadModelOptions = this._features.getOptionsFor(LOCAL_READ_MODEL_FEATURE)
      const blockUntilCriticalReady = localReadModelOptions?.blockUntilCriticalReady === true
      const startInitialSync = () => localReadModelAdapter.startInitialSync(localReadModelOptions?.startupWaitMs ?? 5000, {
        blockUntilCriticalReady,
      })
      // Package prefetch runs inside sync and keeps the worker busy. Unless the
      // deployment asks to block on critical scopes, let the first layout read
      // documents before that background work starts.
      if (blockUntilCriticalReady || !isInBrowser()) {
        await perf.track('boot:initial-sync', () => startInitialSync())
      } else {
        this._deferredInitialSync = startInitialSync
        perf.whenIdle(() => this._startDeferredInitialSync())
      }
    }

    // Precompiled view components: the configured groups are in place before
    // the root layout renders.
    this._viewComponents = this._createViewComponents()

    if (this._viewComponents) {
      await perf.track('boot:view-components', () => this._viewComponents.preload())
    }

    // ae-root may now read boot documents, authentication, and preferences.
    perf.mark('boot:vue-init')
    this._initializeVue(options.el)

    if (this._viewComponents) {
      // The same idle boundary as deferred sync, before later navigation.
      // Reporting is best effort and never delays boot.
      const groups = this._viewComponents
      perf.whenIdle(() => this._reportComponentStartup(groups))
    }

    this._status.stage = 'BOOTED'

    if (this._dynamic.pendingBooted) {
      this._dynamic.pendingBooted.resolve()
      this._dynamic.pendingBooted = null
    }

    removeElement(options.removeAfterBoot)

    await perf.track('boot:security', () => this._loadSecurity())
    perf.mark('boot:booted')

    if (options.booted) {
      this._scheduleRefresh()
      options.booted(this)
    }

    // Load remaining documents
    this._status.stage = 'LOADING'

    if (options.load) {
      await this._loadDocuments(options.load, this._lookup, 100)
      this._modelIncomplete = false
    }

    this._status.stage = 'LOADED'

    removeElement(options.removeAfterLoad)

    // Fills this.Account with information
    await this._get([this._dynamic.auth.accountId, this._dynamic.auth.groupAccountId, this._dynamic.auth.backedBy, this._dynamic.auth.impersonatedBy])

    // Requires this.Account to be have the data.locale information
    this.setDateTimeLocale(this.Account?.data.locale  as string ?? 'auto')

    if (this._dynamic.pendingLoaded) {
      this._dynamic.pendingLoaded.resolve()
      this._dynamic.pendingLoaded = null
    }

    if (this._serverAdapter && !options.noConnect) {
      this._serverAdapter.connect(/* TODO: communicate which changes have already been received during boot/load in order to ignore those */)
      this._modelIncomplete = true
    } else {
      this._modelIncomplete = false
    }

    this._scheduleRefresh()
  }

  public async _loadDocumentsAfterLogin() {
    if (this._options.load) {
      return this._loadDocuments(this._options.load, this._lookup, 109)
    } else {
      return this._loadSecurity()
    }
  }

  private _detectDevice(userAgent: string, device: DEVICE) {
    this._serviceWorkerClient?.dispose()
    this._deviceCleanup?.()
    this._userAgent = userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '')
    const browserWindow = typeof window !== 'undefined' ? window as unknown as DeviceWindow : undefined
    const update = () => {
      if (this._dynamic.wasDestroyed) return
      const display = readDisplay(browserWindow)
      const category = resolveDevice(this._userAgent, display, device)
      const changed = category !== this._deviceState.device || JSON.stringify(display) !== JSON.stringify(this._deviceState.display)
      if (!changed) return

      this._dynamic.device = category
      this._deviceState.device = category
      this._deviceState.display = display
      this._emitter.emit('device:changed', { device: category, display })
    }

    update()
    if (browserWindow) this._deviceCleanup = watchDisplay(browserWindow, update)
  }

  public get ConnectionStatus() {
    return this._dynamic.connectionStatus
  }

  public get Device() {
    return this._deviceState.device
  }

  /** Browser-reported fold/posture geometry, in layout viewport CSS pixels. */
  public get Display() {
    return this._deviceState.display
  }

  public get ResponsiveSizes() {
    return { MIN_MEDIUM_SIZE, MIN_LARGE_SIZE }
  }

  private _initializeVue(mountElement?: string | HTMLElement) {
    // TODO: Offer a way to opt out of Vue support.
    //       e.g by setting el explicitly to null or something similar
    // if (mountElement) {
    //   return
    // }

    if (isBrowserButVeryOldBrowser()) {
      console.error('Browser not supported')
    }

    const aeppic = this

    this._registerExtensions()
    this._registerGlobalComponents()
    this._registerGlobalDirectives()

    let mountElementId = null
    if (typeof mountElement === 'string') {
      if (mountElement[0] === '#') {
        mountElementId = mountElement.substr(1)
      }
    } else if (mountElement) {
      mountElementId = mountElement.id
    }

    if (!mountElementId) {
      mountElementId = 'app'
    }

    this._vue = new Vue(<any>{
      el: mountElement,
      /**
       * Build a reactive and data-bound Aeppic object that every sub-component can inject and use
       *
       * @returns
       */
      data() {
        return {
          Aeppic: aeppic,
        }
      },
      /**
       * Provide objects that all descendant components can use for injection
       * See https://vuejs.org/v2/api/#provide-inject.
       *
       * The provided objects themselves are not automatically reactive so
       * we expose a getter to return the reactive data instance defined above
       *
       * @returns
       */
      provide() {
        return {
          getAeppicContext(contextName: string, queryScopeRoot: Types.DocumentId | Types.Document) {
            return aeppic.contextify(contextName, queryScopeRoot)
          }
        }
      },
      /**
       * Only render the root-component
       *
       * @returns
       */
      render: function (createElement) { return createElement('root-component', { attrs: { id: mountElementId } }) },
      /**
       *
       */
      mounted() {
        aeppic._rootContext.rootElement = this.$el

        // We need to sleep here to ensure aeppic actually has the vue instance registered
        if (!aeppic._dynamic) {
          return
        }

        aeppic._addDOMEventListener('OpenDrillIntoDialogFallback', DOMEvents.DrillInto, (event: any) => {
          if (event.defaultPrevented) {
            return
          }

          const detail = event.detail || {}

          if (detail.mode === 'event') {
            return
          }

          if (detail.mode === 'navigate') {
            aeppic.navigateTo(detail.targetDocument)
            return
          }

          event.preventDefault()

          aeppic.showDrillIntoDialog(detail.targetDocument, {
            ...(detail.dialogOptions || {}),
            sourceElement: detail.sourceElement,
            sourceContext: detail.sourceContext,
            preferredMode: detail.preferredMode || detail.dialogOptions?.preferredMode || 'dock',
            backdropClose: detail.dialogOptions?.backdropClose !== false,
          }).catch((error) => {
            console.error('Could not show drill-in dialog', error)
          })
        })

        if (aeppic.flags.discoverElements.enabled) {
          const optionsSupported = isPassiveOnHandlersSupported()
          this.passiveCaptureOptions = optionsSupported ? { capture: true, passive: true } : true

          let monitorElement = false

          const updateDiscoveryPath = (event) => {
            if (window.requestAnimationFrame) {
              window.requestAnimationFrame(() => {
                this.setInspectorDiscoveryPath(event)
              })
            } else {
              this.setInspectorDiscoveryPath(event)
            }
          }

          this.discoverElementsAtMousePosition = debounce(updateDiscoveryPath, 10)

          this.startMonitoring = (event) => {
            if (monitorElement) {
              return
            }

            monitorElement = true

            aeppic._addDOMEventListener('[Dev] Monitor Mouse Movements', 'mousemove', this.discoverElementsAtMousePosition, this.passiveCaptureOptions)
            aeppic._addDOMEventListener('[Dev] Monitor Mouse Clicks', 'click', this.inspectElementAtMousePosition, true)

            updateDiscoveryPath(event)
          }

          this.stopMonitoring = () => {
            if (!monitorElement) {
              return
            }

            monitorElement = false

            aeppic._removeDOMEventListener('[Dev] Monitor Mouse Movements')
            aeppic._removeDOMEventListener('[Dev] Monitor Mouse Clicks')
          }

          this.enableDisable = (event) => {
            if (isDiscoverKeyCombination(event)) {
              this.startMonitoring(event)
            } else {
              this.stopMonitoring()
            }
          }

          this.inspectElementAtMousePosition = (event) => {
            if (isDiscoverKeyCombination(event)) {
              this.setInspectorDiscoveryPath(event, { inspect: true })
              event.stopPropagation()
              event.preventDefault()
            } else {
              this.stopMonitoring()
            }
          }

          aeppic._addDOMEventListener('[Dev] Monitor Key Down', 'keydown', this.enableDisable, this.passiveCaptureOptions)
          aeppic._addDOMEventListener('[Dev] Monitor Key Up', 'keyup', this.enableDisable, this.passiveCaptureOptions)
        }
      },
      beforeDestroy() {
        if (this.$el) {
          this.stopMonitoring()
          aeppic._removeAllDOMEventListeners()
          this._dynamic?.unsubscribeFromEditEvents?.()
        }
      },
      methods: {
        setInspectorDiscoveryPath(event, options) {
          const target: Element = event.target

          const pathElements =
            'path' in event ? event.path
              // : 'composedPath' in event ? event.composedPath()  // This is weirdly [] all the time
              : composedPath(target)

          const path = aeppic.Developer.buildInspectorDiscoveryPath(pathElements)
          aeppic.Developer.setInspectorDiscoveryPath(path, pathElements, options)
        }
      },
      /**
       * Make the root component available without registering it globally
       */
      components: {
        'root-component': RootComponent
      }
    })
  }

  public lookupComponentForElement(element: HTMLElement) {
    // __vue__ is not reliable due to children/parent hidden component
    // mismatches
    //
    // Do not use:
    //
    // if (element['__vue__']?.$el === element) {
    //   return element['__vue__']
    // }

    for (const component of this._recurseComponents()) {
      if (component.$el === element) {
        return component
      }
    }
  }

  private *_recurseComponents(rootComponent = this._vue) {
    yield rootComponent

    for (const child of rootComponent.$children) {
      yield* this._recurseComponents(child)
    }
  }

  triggerResize() {
    if (isInBrowser()) {
      if (window.dispatchEvent && typeof Event !== 'undefined') {
        window.dispatchEvent(new Event('resize'))
      }
    }
  }

  contextify(name: string, queryRoot?: Types.Document | Types.DocumentId): Aeppic {
    const queryRootId = getDocumentId(queryRoot)
    const contexifiedInstance = contextify(this, name, {
      Logger: this._log,
      Query: new QueryScopes(queryRoot ?? queryRootId),
      rootDocumentId: queryRootId,
    })

    const context = contexifiedInstance.context

    if (this._allContexts.indexOf(context) >= 0) {
      console.warn('context already known ?!', context)
    }

    this._allContexts.push(context)

    return contexifiedInstance
  }

  setContextRootElement(rootElement: HTMLElement) {
    if (!rootElement) {
      throw new Error('Must set to valid HTML Element')
    }

    if (!rootElement.parentNode) {
      console.warn('Root element must be part of the DOM. Setting root element to a detached element is not supported')
      return
    }

    const context = this.context

    if (context === this._rootContext) {
      throw new Error('Cannot set context root element on non contextified root Aeppic instance')
    }

    context.rootElement = rootElement
  }

  private _addDOMEventListener(id: string, name: string, callback, options?, context: Context = this.context) {
    if (context.domElementListeners.has(id)) {
      throw new Error('Registering multiple handlers for same event not supported. Mostly to ensure no mistakes when de-registering')
    }

    const element = context.rootElement

    context.domElementListeners.set(id, { name, callback, options })
    element.addEventListener(name, callback, options)
  }

  private _removeDOMEventListener(id: string) {
    const listener = this.context.domElementListeners.get(id)
    this.context.domElementListeners.delete(id)

    const element = this.context.rootElement
    element.removeEventListener(listener.name, listener.callback, listener.options)
  }

  private _removeAllDOMEventListeners() {
    const element = this.context.rootElement

    for (const listener of this.context.domElementListeners.values()) {
      element.removeEventListener(listener.name, listener.callback, listener.options)
    }

    this.context.domElementListeners.clear()
  }

  release() {
    if (this._dynamic.wasDestroyed) {
      return
    }

    if (this.context === this._rootContext) {
      throw new Error('Root context must be destroyed not released')
    }

    this._release()
  }

  private _release() {
    const context = this.context

    this._removeAllDOMEventListeners()
    context.release()

    if (context !== this._rootContext) {
      removeFromArray(this._allContexts, context)
    }
  }

  isDOMEvent(eventName: string) {
    for (const name of Object.values(DOMEvents)) {
      if (name === eventName) {
        return true
      }
    }
    return false
  }
  /*
   * Events
   */
  on(event, cb): any {
    return this.addEventListener(event, cb)
  }
  off(event, cb): any {
    return this.removeEventListener(event, cb)
  }

  addEventListener(event, cb): any {
    if (this.isDOMEvent(event)) {
      const existingId = cb[DOMEventSubscriberIdentifierKey]

      if (existingId) {
        this._log.error('Could not add event listener for DOM event. The callback was already registered before')
      }

      const id = cb[DOMEventSubscriberIdentifierKey] = uuid()
      this._addDOMEventListener(id, event, cb, { passive: true })
      return
    }

    return this._emitter.addListener(event, cb)
  }

  removeEventListener(event, cb): any {
    if (this.isDOMEvent(event)) {
      const id = cb[DOMEventSubscriberIdentifierKey]

      if (!id) {
        this._log.error('Could not remove event listener for DOM event. The callback was not registered before')
        return
      }

      this._removeDOMEventListener(id)

      delete cb[DOMEventSubscriberIdentifierKey]
    }

    return this._emitter.removeListener(event, cb)
  }

  /*
   * `window` Support
   */

  async loadStylesheet(stylesheetId, cssBlockId = '') {
    const stylesheet = await this.get(stylesheetId)
    const css = <string>stylesheet.data.css

    let patchedCss = css.replace(CSS_BLOCK_MARKER, cssBlockId)

    return loadStyle(css, stylesheetId)
  }

  unloadStylesheet(stylesheetId) {
    if (!stylesheetId) {
      throw new Error('Invalid stylesheet ID')
    }

    unloadStyle(stylesheetId)
  }

  async loadStylesheetFromUrl(url: string, stylesheetId: string = url, { groupId = 'unknown', timeout = 10_000, sortIndex = 0 } = {}) {
    return loadStyleFromUrl(url, stylesheetId, groupId, sortIndex, timeout)
  }

  // async loadScript(src, group: ScriptGroup = 'ae-dynamic', sortIndex = null) {
  //   return loadScript(src, group, sortIndex)
  // }

  // unloadScript(src) {
  //   return unloadScript(src)
  // }


  public get navigator() {
    if (typeof navigator !== 'undefined') {
      return {
        userAgent: navigator.userAgent
      }
    } else {
      return {
        userAgent: 'nodejs'
      }
    }
  }

  navigateBack() {
    if (isInBrowser()) {
      return window.history.back()
    }
  }

  navigateForward() {
    if (isInBrowser()) {
      return window.history.forward()
    }
  }

  /*
   * Model exports functionality
   */

  public *recurse(startDocument: Types.Document): IterableIterator<Types.Document> {
    return yield* this._model.recurse(startDocument)
  }

  public async downloadModel(requestedOptions: ModelDownloadOptions, cbProgress?: (progress: number, subtask: string) => void) {
    const options: ModelDownloadOptions = { ...DEFAULT_MODEL_DOWNLOAD_OPTIONS, ...requestedOptions }

    const exportOptions: ModelExportOptions = {
      format: 'blob',
      compress: options.compress,
      rootDocumentId: options.rootDocumentId,
      rootDocumentIds: options.rootDocumentIds,
      json: options.json,
      includeUnusedForms: options.includeUnusedForms,
      includeBinaryContent: options.includeBinaryContent,
      transportManifestId: options.transportManifestId
    }

    const result = await this.exportModel(exportOptions, cbProgress)

    let filename = options.filename || result.name
    if (options.extension) {
      filename = filename + options.extension
    }
    else {
      filename = filename + result.extension + (options.compress ? '.zip' : '')
    }

    FileSaver.saveAs(result.data, filename)
  }

  async exportForms(parentDocumentId = 'root', { alsoFilterSystem = false, includeFn = null }: { alsoFilterSystem?: Boolean, includeFn?: (id: string) => Boolean } = {}): Promise<(Change | Types.Document)[]> {
    return this._model.exportForms(parentDocumentId, { alsoFilterSystem, includeFn })
  }

  public async exportModel(requestedOptions: ModelExportOptions, cbProgress?: (progress: number, subtask: string) => void): Promise<ModelExport> {
    const options: ModelExportOptions = { ...DEFAULT_MODEL_EXPORT_OPTIONS, ...requestedOptions }
    const manifest = await this._createTransportManifest(options)

    let rootDocumentIds = []
    let excludeSystemBinaries = false

    // always include system, currently required by merge-tool
    if (!manifest.rootDocumentIds.includes('system') && !manifest.rootDocumentIds.includes('root')) {
      rootDocumentIds.push('system', ...manifest.rootDocumentIds)
      excludeSystemBinaries = true
    }
    else {
      rootDocumentIds = manifest.rootDocumentIds
      excludeSystemBinaries = false
    }

    const { prefix, separator, suffix, stringify, extension, mime } = getJsonFormattingOptions(options.json)
    const lines = []
    const contentUrls = new Map()

    let total = 0
    let processed = 0
    let progress = 0

    if (rootDocumentIds.length === 1 && rootDocumentIds[0] === 'root') {
      total = this.Model.numberOfDocuments
    }
    else {
      for (const rootDocumentId of rootDocumentIds) {
        const descendants = await this.find(`a:${rootDocumentId}`)
        total += descendants.length
      }
    }

    // Prefix the export with dynamically created (multi-document) manifest
    for (const doc of manifest.manifestDocuments) {
      lines.push(stringify(doc))
    }

    // Only export forms that are actually used by default
    // unless they are inside of system and we want to export from 'root' downwards
    const formExportOptions = {
      excludeLatest: false,
      alsoFilterSystem: !rootDocumentIds.includes('root'),
      includeFn: (formId: string) => (
        this._model.some(`f.id:${formId}`)
      )
    }

    if (manifest.includeUnusedForms) {
      formExportOptions.alsoFilterSystem = false
      formExportOptions.includeFn = null
    }

    for (const rootDocumentId of rootDocumentIds) {
      for (const form of this._model.exportForms(rootDocumentId, formExportOptions)) {
        lines.push(stringify(form))
      }
    }

    for (const ancestor of await this._exportAncestors(rootDocumentIds)) {
      lines.push(stringify(ancestor))
    }

    for (const rootDocumentId of rootDocumentIds) {
      for (const entry of this._model.export(rootDocumentId)) {
        lines.push(stringify(entry))

        if (manifest.includeBinaryContent[rootDocumentId]) {
          if (!(rootDocumentId === 'system' && excludeSystemBinaries)) {
            for (const urlEntry of await this._getContentUrls(entry)) {
              contentUrls.set(urlEntry.dataUrl, urlEntry)
            }
          }
        }

        processed++
        const p = total ? Math.floor(processed / total * 100) : 0

        if ((p !== progress) && cbProgress) {
          progress = p
          cbProgress(progress, 'exporting documents')
        }

        // TODO: Export previous versions in descending order (e.g 4 -> 3 -> 2. Import will ignore them or use them for history)
      }
    }

    const text = prefix + lines.join(separator) + suffix

    let result: ModelExport = null

    if (options.compress) {
      result = {
        name: manifest.name,
        extension,
        data: await this._createExportZip(`documents${extension}`, text, 'content', contentUrls, (progress) => {
          cbProgress && cbProgress(progress, 'exporting file content')
        })
      }
    } else if (options.format === 'text') {
      result = {
        name: manifest.name,
        extension,
        data: text,
      }
    } else if (options.format === 'buffer') {
      result = {
        name: manifest.name,
        extension,
        data: Buffer.from(text)
      }
    } else {
      const encoder = new TextEncoder()
      const uint8array = encoder.encode(text)

      result = {
        name: manifest.name,
        extension,
        data: new Blob([uint8array], { type: mime })
      }
    }

    return result
  }

  private async _createTransportManifest(options): Promise<any> {
    const result = {
      name: '',
      manifestDocuments: [],
      rootDocumentIds: [],
      includeBinaryContent: {},
      includeUnusedForms: false
    }

    const sourceMachine = '<n/a>'
    const now = new Date()
    const exportDate = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0')

    //
    // clone the specified manifest
    //
    if (options.transportManifestId) {

      if (await this.get(options.transportManifestId)) {
        const clonedDocs = await this.cloneDeep(options.transportManifestId, 'root', { save: false })
        const manifest = clonedDocs.root

        manifest.data.sourceMachine = sourceMachine
        manifest.data.exportDate = exportDate
        manifest.data.active = true
        result.manifestDocuments.push(manifest.cloneAsDocument())
        result.name = manifest.data.name
        result.includeUnusedForms = manifest.data.includeUnusedForms

        for (const manifestEntryRef of manifest.data.rootDocuments) {
          const manifestEntry = clonedDocs.documents.find(d => d.id === manifestEntryRef.id)

          result.manifestDocuments.push(manifestEntry.cloneAsDocument())

          const rootRef = manifestEntry.data.rootDocument as Types.Reference

          if (rootRef && rootRef.id) {
            result.rootDocumentIds.push(rootRef.id)
            result.includeBinaryContent[rootRef.id] = !!manifestEntry.data.includeBinaryContent
          }
        }

        return result
      }
      else {
        console.error('invalid transport manifest id.')
      }
    }

    //
    // alternatively dynamically create a default manifest from the specified options
    //
    const rootDocumentIds = options.rootDocumentIds.length ? options.rootDocumentIds : [options.rootDocumentId]

    result.rootDocumentIds = rootDocumentIds

    const manifest = await this.new('1327bd9b-34e5-4c38-bc43-5e709d8c01a2', 'root')
    let manifestEntry = null
    manifest.data.active = true
    manifest.data.sourceMachine = sourceMachine
    manifest.data.exportDate = exportDate
    manifest.data.includeUnusedForms = options.includeUnusedForms

    for (const rootDocumentId of rootDocumentIds) {
      const rootDocument = await this.get(rootDocumentId)

      if (!rootDocument) {
        continue
      }

      if (!manifest.data.name) {
        manifest.data.name = rootDocument.data.name || ''
      }

      manifestEntry = await this.new('573a7cb1-789b-4600-94a1-95edc76fc34c', manifest, { allowMissingParent: true })
      manifest.addReference('rootDocuments', manifestEntry)
      manifestEntry.addReference('rootDocument', rootDocument)
      manifestEntry.data.includeBinaryContent = options.includeBinaryContent

      result.manifestDocuments.push(manifestEntry.cloneAsDocument())
      result.includeBinaryContent[rootDocumentId] = options.includeBinaryContent
    }

    if (manifest.data.name && manifest.data.rootDocuments.length > 1) {
      manifest.data.name = `${manifest.data.name} + ${manifest.data.rootDocuments.length - 1} other${manifest.data.rootDocuments.length > 2 ? 's' : ''}`
    }
    else if (!manifest.data.name) {
      manifest.data.name = 'export-manifest'
    }

    result.name = manifest.data.name
    result.includeUnusedForms = manifest.data.includeUnusedForms
    result.manifestDocuments.unshift(manifest.cloneAsDocument())

    result.manifestDocuments.push((await this.getFormForDocument(manifest)).document)
    manifestEntry && result.manifestDocuments.push((await this.getFormForDocument(manifestEntry)).document)
    return result
  }

  private async _exportAncestors(identifiers: Types.IdOrReference[]) {

    const result = new Set()

    for (let identifier of identifiers) {
      const document = await this.get(identifier)

      if (!document || !document.a) {
        continue
      }

      for (let aId of ['root'].concat(document.a)) {
        const a = await this.get(aId)
        result.add(a)
      }
    }

    return result.values()
  }

  private async _getContentUrls(document: Types.Document) {
    const result = []
    const form = await this.getFormForDocument(document)

    if (!form) {
      return result
    }

    for (let field of form.fields) {
      if (field.subType === 'file' || field.subType === 'image') {
        const fileField = document.data[field.name] as FileField
        if (fileField && fileField.dataUrl) {
          result.push({
            name: fileField.dataUrl.startsWith('data:') ? `data-uri-${document.id}-${field.name}` : ascii2hex(fileField.dataUrl),
            dataUrl: fileField.dataUrl,
            src: this.Content.getSrc(document, field.name)
          })
        }
      }
    }

    return result
  }

  private async _createExportZip(documentsFileName, documentsFileText, contentFolderName, contentUrls, cbProgress?): Promise<Blob> {
    let total = contentUrls.size + 1
    let processed = 0
    let progress

    const zip = new ZipWriter()

    await zip.open()
    await zip.addText(documentsFileName, documentsFileText)
    processed++
    cbProgress && cbProgress(Math.floor(processed / total * 100))

    for (let entry of contentUrls.values()) {
      const response = await this._fetchFromServerApi('GET', entry.src)

      if (!response.ok) {
        console.error(`failed to fetch ${entry.src}`)
        continue
      }
      const data = await response.blob()
      const path = `${contentFolderName}/${entry.name}`
      await zip.addBlob(path, data)
      // await zip.addUrlContent(path, entry.src)
      processed++
      let p = Math.floor(processed / total * 100)
      if ((p !== progress) && cbProgress) {
        progress = p
        cbProgress(progress)
      }
    }

    return await zip.close() as Blob
  }

  public async canCreateDocument(document: Types.Document) {
    await this._ensureLoaded()

    if (!isDocument(document)) {
      return false
    }

    if (isAdmin(this.Account)) {
      return true
    }

    const knownDocument = this._model.get(document.id)

    if (knownDocument) {
      return this.canEdit(knownDocument)
    }

    if (!this._isLocallyNewDocument(document)) {
      return false
    }

    const parentId = document.p

    if (!parentId) {
      return false
    }

    const parentDocument = this._model.get(parentId)

    if (!parentDocument) {
      return false
    }

    return this.hasRight(parentDocument, FULL_ACCESS_RIGHT_ID)
  }

  public async canEdit(document: Types.Document) {
    await this._ensureLoaded()

    if (!isDocument(document)) {
      throw new Error('Aeppic.canEdit expects a document. Use Aeppic.hasRight for id/reference based rights checks.')
    }

    if (isAdmin(this.Account)) {
      return true
    }

    if (this._isLocallyNewDocument(document)) {
      return this.canCreateDocument(document)
    }

    return this.hasRight(document, FULL_ACCESS_RIGHT_ID)
  }

  @deprecated('Aeppic', 'hasAccess', 'hasRight')
  public async hasAccess(identifier: Types.IdOrReference, rightIdentifier?: Types.IdOrReference) {
    if (rightIdentifier == null) {
      return this.hasRight(identifier)
    } else if (rightIdentifier === '') {
      throw new Error('Empty right identifier not supported')
    } else {
      return this.hasRight(identifier, [rightIdentifier])
    }
  }

  /**
   * Checks whether the current account has access to at least **one** of the rights specified.
   * If no rights are specified (undefined parameter) only read access is checked.
   *
   * @param target
   * @param rights
   */
  public async hasRight(target: Types.IdOrReference | Types.Document): Promise<boolean>
  public async hasRight(target: Types.IdOrReference | Types.Document, rightIdentifier: Types.IdOrReference): Promise<boolean>
  public async hasRight(target: Types.IdOrReference | Types.Document, rightIdentifiers: Types.IdOrReference[]): Promise<boolean>
  public async hasRight(target: Types.IdOrReference | Types.Document, arg2?: Types.IdOrReference|Types.IdOrReference[]): Promise<boolean> {
    await this._ensureLoaded()

    if (arg2 == null) {
      return this._checkReadAccessOnly(target)
    }

    const rights = Array.isArray(arg2) ? arg2 : [arg2]

    return this._checkAccessWithRights(target, rights)
  }

  private _isLocallyNewDocument(document: Types.IdOrReference | Types.Document) {
    if (!isDocument(document)) {
      return false
    }

    if (document.v !== 'initial' || document.previous) {
      return false
    }

    return !this._model.get(document.id)
  }

  private async _checkReadAccessOnly(targetIdentifier: Types.IdOrReference | Types.Document) {
    const targetId = getDocumentId(targetIdentifier)

    if (this._isLocallyNewDocument(targetIdentifier)) {
      return false
    }

    const canCheckLocally = !this._modelIncomplete
    const canUseResolvedLockRightsCache = this._canUseResolveLockRightsHasRightCache()
    const canCheckRemotely = this._serverAdapter != null

    if (canCheckLocally || !canCheckRemotely) {
      if (!canCheckRemotely) {
        console.warn('Cannot check read access correctly. Model is not complete with no server adapter, but will try anyway')
      }
      return this._model.hasRight(targetIdentifier)
    }

    if (canUseResolvedLockRightsCache) {
      try {
        return await this._resolvedLockRightsHasRightCache.hasRight(targetIdentifier)
      } catch (error) {
        this._log?.warn({ error }, 'Resolve-lock-rights read access denied for %s', targetId)
        return false
      }
    }

    if (canCheckRemotely) {
      return this._serverAdapter.hasRight(targetId, null)
    }

    console.error('Cannot check read access')
    return false
  }

  private async _checkAccessWithRights(targetIdentifier: Types.IdOrReference | Types.Document, rightIdentifiers: Types.IdOrReference[]) {
    const backingAccount = this._dynamic.auth?.backedBy
      ? this._model.get(this._dynamic.auth.backedBy)
      : null

    // The server-side rights model grants administrators every right. Keep
    // that invariant when the incomplete client model uses the batched
    // resolveLockRights cache, which otherwise only knows rights exposed by
    // the document's locks and can incorrectly hide commands/workflows.
    if (isAdmin(this.Account) || isAdmin(backingAccount)) {
      return true
    }

    const canCheckLocally = !this._modelIncomplete
    const canUseResolvedLockRightsCache = this._canUseResolveLockRightsHasRightCache()
    const canCheckRemotely = this._serverAdapter != null && !canUseResolvedLockRightsCache

    const documentId = getDocumentId(targetIdentifier)
    const rightIds = rightIdentifiers.map(getDocumentId)

    if (this._isLocallyNewDocument(targetIdentifier)) {
      if (rightIds.includes(FULL_ACCESS_RIGHT_ID)) {
        return this.canCreateDocument(targetIdentifier as Types.Document)
      }

      return false
    }

    if (canUseResolvedLockRightsCache) {
      try {
        return await this._resolvedLockRightsHasRightCache.hasRight(targetIdentifier, rightIdentifiers)
      } catch (error) {
        this._log?.warn({ error, documentId, rightIds }, 'Resolve-lock-rights right access denied for %s', documentId)
        return false
      }
    }

    const accessRightsNotCached = []

    // Check cache for each right first
    for (const rightId of rightIds) {
      const cachedAccessResult = this._hasRightCache.get(documentId, rightId)

      // We only need one
      if (cachedAccessResult === true) {
        return true
      }

      if (cachedAccessResult === false) {
        continue
      }

      accessRightsNotCached.push(rightId)
    }

    if (canCheckLocally || !canCheckRemotely) {
      if (!canCheckRemotely) {
        console.warn('Cannot check rights correctly. Model is not complete with no server adapter, but will try anyway')
      }

      for (const rightId of accessRightsNotCached) {
        const hasRight = await this._model.hasRight(targetIdentifier, rightId)
        this._hasRightCache.set(documentId, rightId, hasRight)

        if (hasRight) {
          return true
        }
      }
    }

    if (canCheckRemotely) {
      // All rights not yet cached need to be checked with the server adapter when available or locally
      const pendingChecks = accessRightsNotCached.map(rightId => {
        if (canCheckRemotely) {
          return {
            rightId,
            request: this._serverAdapter.hasRight(documentId, [rightId])
          }
        }

        if (!canCheckLocally) {
          console.warn('Cannot check access correctly as the model is incomplete. Trying anyway since we have no adapter')
        }

        return {
          rightId,
          request: this._model.hasRight(documentId, [rightId])
        }
      })

      const evaluatedRights = await Promise.all(pendingChecks.map(p => p.request))

      for (const check of pendingChecks) {
        this._hasRightCache.set(documentId, check.rightId, await check.request)
      }

      return evaluatedRights.some(Boolean)
    } else {
      console.error('Cannot check rights')
      return true
    }
  }

  private _canUseResolveLockRightsHasRightCache() {
    return this._modelIncomplete &&
      this._serverAdapter != null &&
      typeof this._serverAdapter.resolveLockRights === 'function' &&
      this._features?.isEnabled(RESOLVE_LOCK_RIGHTS_HAS_RIGHT_CACHE_FEATURE) === true &&
      this._resolvedLockRightsHasRightCache != null
  }

  private _registerGlobalComponents() {
    Vue.component('ae-layout', LayoutComponent)
    Vue.component('ae-layout-selector', <any>LayoutSelectorComponent)
    Vue.component('ae-design', DesignComponent)
    Vue.component('ae-design-selector', <any>DesignSelectorComponent)
    Vue.component('ae-list', <any>ListComponent)
    Vue.component('ae-list-selector', <any>ListSelectorComponent)
    Vue.component('ae-query', QueryComponent)
    Vue.component('ae-vars', VarsComponent)
    Vue.component('ae-control', ControlComponent)
    Vue.component('ae-control-selector', <any>ControlSelectorComponent)
    Vue.component('ae-splitter', SplitterComponent)
    Vue.component('ae-tabs', TabsComponent)
    Vue.component('ae-expandable', ExpandableComponent)
    Vue.component('ae-popup', PopupComponent)
    Vue.component('ae-select', SelectComponent)
    Vue.component('ae-dialog', DialogComponent)
    Vue.component('ae-icon', IconComponent)
    Vue.component('ae-fetch', FetchComponent)
    Vue.component('ae-link', LinkComponent)
    Vue.component('ae-form', <any>FormComponent)
    Vue.component('ae-form-preview', <any>FormPreviewComponent)
    Vue.component('ae-form-section', <any>FormSectionComponent)
    Vue.component('ae-form-section-next', <any>FormSectionNextComponent)
    Vue.component('ae-form-field', <any>FormFieldComponent)
    Vue.component('ae-form-paragraph', <any>FormParagraphComponent)
    Vue.component('ae-wireframe-view', <any>WireframeViewComponent)

    Vue.component('ae-header', <any>HeaderComponent)
    Vue.component('ae-headroom', <any>HeadroomComponent)


    // Vue.component('ae-field', FieldComponent)
    // Vue.component(Dialog.name, Dialog)
  }

  private _registerGlobalDirectives() {
    Vue.directive('focus', FocusDirective)
    Vue.directive('observe-visibility', VisibilityDirective)
  }

  private _registerExtensions() {
    Vue.use(DragAndDropExtension)
    Vue.use(TranslateExtension, { aeppic: this.contextify('v-translate') })
    Vue.use(ResponsiveExtension, { aeppic: this.contextify('v-responsive') })
    Vue.use(DensityExtension)
  }

  public get wasDestroyed() {
    return this._dynamic.wasDestroyed
  }

  public pauseChanges() {
    this._serverAdapter?.pause()
  }

  public async resumeChanges(options?: BatchFlushOptions): Promise<void> {
    await this._serverAdapter?.resume(options)
  }

  public destroy() {
    if (this.context !== this._rootContext) {
      throw new Error('Contextified Aeppic instances should be released not destroyed')
    }

    this._deviceCleanup?.()
    this._deviceCleanup = null
    this._toasts?.destroy()
    this._support?.destroy()
    this._dynamic.wasDestroyed = true
    this._dynamic.refreshPending = false

    try {
      this._vue?.$destroy()
    } catch (error) {
      console.error('ERROR destroying vue instance', error)
    }

    this._release()

    this._model?.clear()

    this._emitter.removeAllListeners()

    if (this._serverAdapter && this._serverAdapter.disconnect) {
      this._serverAdapter.disconnect()

      if (this.Developer?.remoteDeveloperMode !== 'None') {
        this._serverAdapter?.disconnectDeveloper()
      }
    }

    this.Developer?.destroy()

    if (this._editContextRegistry) {
      this._editContextRegistry.destroy()
    }
  }

  private get destroyed() { return this._dynamic.wasDestroyed }

  private async _loadDocuments(options: LoadOptions, documentLookupCallback: DocumentLookupCallback, burst = 0, seedLocalModel = false) {
    if (!options.src) {
      return
    }

    if (typeof options.src === 'string') {
      return this._loadDocumentsFromUrl(options.src, options, documentLookupCallback, burst, seedLocalModel)
    } else {
      return this._loadDocumentsFromFunction(options.src, options, documentLookupCallback, burst, seedLocalModel)
    }
  }

  private async _loadDocumentsFromUrl(src: string, options: LoadOptions, documentLookupCallback: DocumentLookupCallback, burst = 0, seedLocalModel = false) {
    const response = await this._fetchFromServerApi('GET', src)
    return this._loadDocumentsFromResponse(response, options, documentLookupCallback, burst, seedLocalModel)
  }

  private async _loadDocumentsFromResponse(response: Response, options: LoadOptions, documentLookupCallback: DocumentLookupCallback, burst = 0, seedLocalModel = false) {
    let importFirstSequenceOfFormsIntoFormsDirectoryOnly = false
    let rootDocumentHasPassed = false
    let indexOfRootObjectInBatch = -1

    const BATCH_SIZE = 300

    if ('ok' in response && !response.ok) {
      throw new Error(`Could not GET data from server (${response.status})`)
    }

    let start = Date.now()

    // const manifestLines = []
    let batch = []

    for await (const { lineNo, object: rawEventLine } of readJsonlObjects(response)) {
      const event = options.transform ? await options.transform(rawEventLine) : rawEventLine

      const isDocumentDataEvent = event.data && event.id && event.f && event.f.id

      if (!rootDocumentHasPassed) {
        const isManifestEntryLine = isDocumentDataEvent
          && event.f.id === '1327bd9b-34e5-4c38-bc43-5e709d8c01a2'
          && event.data.active

        if (isManifestEntryLine) {
          // manifestLines.push(line)

          // The presence of a manifest indicates the export has been preformed with
          // the new code-base which may include detached form-versions which should
          // be imported into the model with formsDirectoryOnly flag.
          //
          // this distinction can be removed once all code-bases have been updated
          importFirstSequenceOfFormsIntoFormsDirectoryOnly = true
          continue
        }

        const isFormDocumentEvent = event.f?.id === 'form'

        if (isFormDocumentEvent) {
          importFirstSequenceOfFormsIntoFormsDirectoryOnly = true
        }
      }

      const isRootDocument = event.id === 'root'

      if (isRootDocument && indexOfRootObjectInBatch === -1) {
        rootDocumentHasPassed = true
        indexOfRootObjectInBatch = batch.length
      }

      batch.push(event)

      if (batch.length < BATCH_SIZE) {
        continue
      }

      const batchToImport = batch
      batch = []

      // Import consumes the batch (replacing its entries with null).
      // Queue independent raw boot documents before that happens.
      if (seedLocalModel && this._serverAdapter instanceof LocalFirstServerAdapter) {
        await this._serverAdapter.seedBootDocuments(batchToImport.filter(event => event?.id && ((event?.data && event?.f) || (['apps', 'system'].includes(event.id) && event.dts != null))))
      }

      if (importFirstSequenceOfFormsIntoFormsDirectoryOnly) {
        await this._importIntoFormsDirectoryOnlyOrIntoModelToo(batchToImport, rootDocumentHasPassed, indexOfRootObjectInBatch, { documentLookupCallback })
        indexOfRootObjectInBatch = -1
      } else {
        await this.import(batchToImport, { documentLookupCallback })
      }

      if (burst) {
        const now = Date.now()
        const timeSpentOnLastImport = now - start

        if (timeSpentOnLastImport > burst) {
          await this.waitForNextAnimationFrame()
          start = Date.now()
        }
      }
    }

    if (seedLocalModel && this._serverAdapter instanceof LocalFirstServerAdapter) {
      await this._serverAdapter.seedBootDocuments(batch.filter(event => event?.id && ((event?.data && event?.f) || (['apps', 'system'].includes(event.id) && event.dts != null))))
    }

    if (importFirstSequenceOfFormsIntoFormsDirectoryOnly) {
      await this._importIntoFormsDirectoryOnlyOrIntoModelToo(batch, rootDocumentHasPassed, indexOfRootObjectInBatch, { documentLookupCallback })
    } else {
      await this.import(batch, { documentLookupCallback })
    }

  }

  private async _loadDocumentsFromFunction(srcFunction: DocumentSourceFunction, options: LoadOptions, documentLookupCallback: DocumentLookupCallback, burst = 0, seedLocalModel = false) {
    const arrayOfEvents = []
    const stream = await srcFunction()

    let currentDocument: any

    while (currentDocument = stream.read()) {
      arrayOfEvents.push(currentDocument)
    }

    let events: any[]

    if (options.transform) {
      events = await options.transform(arrayOfEvents)
    } else {
      events = arrayOfEvents
    }

    if (seedLocalModel && this._serverAdapter instanceof LocalFirstServerAdapter) {
      await this._serverAdapter.seedBootDocuments(events.filter(event => event?.id && ((event?.data && event?.f) || (['apps', 'system'].includes(event.id) && event.dts != null))))
    }
    await this.import(events, { documentLookupCallback })
  }

  private async _importIntoFormsDirectoryOnlyOrIntoModelToo(batch, rootDocumentHasPassed, indexOfRootObjectInBatch, options) {
    if (!rootDocumentHasPassed) {
      await this.import(batch, { ...options, addIntoFormsDirectoryOnly: true })
    }
    else if (rootDocumentHasPassed && indexOfRootObjectInBatch >= 0) {
      let intoFormsDirectoryOnlyBatch = batch.slice(0, indexOfRootObjectInBatch)
      let intoModelTooBatch = batch.slice(indexOfRootObjectInBatch, batch.length)

      await this.import(intoFormsDirectoryOnlyBatch, { ...options, addIntoFormsDirectoryOnly: true })
      await this.import(intoModelTooBatch, options)
    }
    else {
      await this.import(batch, options)
    }
  }

  public waitForNextAnimationFrame() {
    return new Promise((resolve) => {
      if (isInBrowser() && window.requestAnimationFrame) {
        window.requestAnimationFrame(resolve)
      } else {
        setTimeout(resolve, 0)
      }
    })
  }

  public waitForNextTick() {
    return waitForNextTick()
  }

  public waitForNextMicroTick() {
    return waitForNextMicroTick()
  }

  public get el() {
    return this._context?.rootElement
  }

  public get rootEl() {
    return this._rootContext.rootElement
  }

  public mount(elementOrSelector: string | HTMLElement) {
    this._vue.$mount(elementOrSelector)
  }

  public async clone(identiferOrReference: Types.IdOrReference, options?: CloneOptions): Promise<EditableDocument>
  public async clone(identiferOrReference: Types.IdOrReference, parentIdentiferOrReference?: Types.IdOrReference, options?: CloneOptions): Promise<EditableDocument>
  public async clone(identiferOrReference: Types.IdOrReference, ...args: any[]): Promise<EditableDocument> {
    const parentIdentiferOrReferenceOrOptions = args[1]
    const cloneToParent = parentIdentiferOrReferenceOrOptions && isReference(parentIdentiferOrReferenceOrOptions)

    const [document] = await this.getAll([
      identiferOrReference,
      cloneToParent && parentIdentiferOrReferenceOrOptions
    ])

    await this.getFormForDocument(document)

    return this._model.clone(identiferOrReference, ...args)
  }

  async cloneDeep(identifierOrReference: Types.IdOrReference, parentIdentifierOrReferenceOrEditableDocument: Types.IdOrReference | EditableDocument, options: { save?: boolean, formFieldsNotToClone?: Object, identifiersNotToClone?: Types.IdOrReference[], readonly?: boolean, hidden?: boolean } = {}) {
    const identifier = isReference(identifierOrReference) ? identifierOrReference.id : identifierOrReference

    let descendantsQuery = `a:${identifier}`

    // TODO: exclude identifiersNotToClone already in query
    // if (options?.identifiersNotToClone) {
    //   const identifierNotToClone = options.identifiersNotToClone.map(identifier => isReference(identifier) ? identifier.id : identifier)
    //   descendantsQuery = `${descendantsQuery} AND NOT (${identifierNotToClone.map(id => `id:${id} OR a:${id}`)})`
    // }

    const [ensuredIdentifierAndParent, ensuredDescendants] = await Promise.all([
      this.getAll([identifierOrReference, parentIdentifierOrReferenceOrEditableDocument]),
      // also find hidden documents, since cloneDeep internally cloned all descendants, even hidden ones. Without includeHidden the document-form of hidden document might not be loaded
      this.find(descendantsQuery, { size: Number.MAX_SAFE_INTEGER, includeHidden: true })
    ])

    // NOTE: parent in ensuredIdentifierAndParent is null, if it was an EditableDocument
    await Promise.all([...ensuredIdentifierAndParent, ...ensuredDescendants].map(document => document && this.getFormForDocument(document)))

    const result = this._model.cloneDeep(identifierOrReference, parentIdentifierOrReferenceOrEditableDocument, { formFieldsNotToClone: options.formFieldsNotToClone, identifiersNotToClone: options.identifiersNotToClone, readonly: options.readonly })

    if (options && options.save) {
      this.saveAll(result.documents)
    }

    return result
  }

  async new(formId: string, parent: Types.IdOrReference, options?: NewDocumentOptions): Promise<EditableDocument> {
    await this.getAll([formId, parent])

    const latestForm = await this.getForm(formId)

    if (!latestForm) {
      throw new Error(`Form '${formId}' not found`)
    }

    const skipDefaults = options?.skipDefaults === true
    const useDefaults = !skipDefaults
    const defaults = latestForm.document.data.defaults

    if (useDefaults && defaults) {
      switch (defaults) {
        case 'TEMPLATE':
          // Ensure that the template and its descendants are loaded (cloneDeep needs all forms)
          const templateReference = latestForm.document.data.newTemplate as Types.Reference
          const templateDocument = await this.get(templateReference)
          await this.getFormForDocument(templateDocument)

          const templateId = typeof templateReference === 'string' ? templateReference : templateReference.id
          const descendants = await this.find(`a:${templateId}`, { size: Number.MAX_SAFE_INTEGER, includeHidden: true })
          await Promise.all(descendants.map(d => this.getFormForDocument(d)))
          break;
        case 'ACTION':
          // Load the action and run it
          const action = await this.get(latestForm.document.data.newAction as Types.Reference)
          const [result] = await this.Actions.run([action], { parent, form: latestForm })

          if (result.ok) {
            if (this.isEditableDocument(result.value)) {
              const newDocument = result.value
              this._model.commit(newDocument)
              this._autoTrackInEditContext(newDocument)
              return newDocument
            } else {
              throw new Error(`Action '${action.data.name} (${action.id})' did not return an editable document`)
            }
          } else {
            throw new Error(`Action '${action.data.name} (${action.id})' failed: ${result.error}`)
          }
          break
        default:
          this.Log.warn("Document form (%s '%s') wants defaults, but those are not yet defined. Falling through to standard behavior", formId, latestForm.data.name)
      }
    }

    const newDocument = this._model.new(formId, parent, options)
    this._model.commit(newDocument)
    this._autoTrackInEditContext(newDocument)

    return newDocument
  }

  async import(operationsParameter: ImportOperation | ImportOperation[], options?: { addIntoFormsDirectoryOnly?: boolean, documentLookupCallback?: DocumentLookupCallback }): Promise<ImportResult> {
    const operations: ImportOperation[] =
      Array.isArray(operationsParameter) ? operationsParameter : [operationsParameter]

    if (operations.length === 0) {
      return { added: 0, failed: [], cycles: 0, ignored: 0, deleted: 0 }
    }

    const importOptions = { documentLookupCallback: this._lookup, ...options }

    const result = await importIntoModel(this._model, operations, importOptions)

    if (result && result.added && result.deleted) {
      this._scheduleRefresh()
      await this._model.flushAllPendingNotifications()
    }

    return result
  }

  commit(document: EditableDocument) {
    if (!EditableDocument.isEditableDocument(document)) {
      throw new Error('Argument must be an EditableDocument')
    }

    // TODO: Extract files if required
    this._startPendingFieldUploads(document)
    this._model.commit(document)
    this._editContextRegistry.notifyTrackedDocumentChanged(document.id)
  }

  private _startPendingFieldUploads(document: EditableDocument) {
    const uploadFields = document.retrieveUploads()

    if (uploadFields) {
      for (const fieldName of uploadFields) {
        this._triggerUploadOfFileField(document, fieldName)
      }
    }
  }

  private _triggerUploadOfFileField(document: EditableDocument, fieldName) {
    const field = document.data[fieldName]
    const content = this._urlToDataCache.get(field.dataUrl)

    if (content) {
      this._uploads.add({ document, fieldName, dataUrl: field.dataUrl, content })
    }
  }

  async readAsBuffer(field: FileField): Promise<Buffer> {
    if (typeof Buffer === 'undefined') {
      throw new Error('Buffer not supported')
    }

    const cachedContent = this._urlToDataCache.get(field.dataUrl)

    if (cachedContent) {
      if (Buffer.isBuffer(cachedContent)) {
        return cachedContent
      } else {
        throw new Error('not yet supported')
      }
    } else {
      throw new Error('not yet supported')
    }
  }

  /**
   * @internal
   *
   * This function should NOT be used by user code directly
   */
  knowsLocally(identifier: Types.IdOrReference): boolean {
    let document = this._model.get(identifier)
    return document != null
  }

  isFlagEnabled(flagName: FlagNames) {
    return this._flags[flagName].enabled
  }

  // Document Modification Methods
  public sendToWebsocket(message) {
    if (this._serverAdapter) {
      this._serverAdapter.sendToWebsocket(message)
    }
  }

  async stamp(target: Types.Document, stampType: Types.IdOrReference | EditableDocument, options?: StampOptions) {
    await this.get('stamp')

    if (!EditableDocument.isEditableDocument(stampType)) {
      await this.get(stampType)
    }
    if (options && options.workflow) {
      await this.get(options.workflow)
    }

    const changes = this._model.stamp(target, stampType, options)

    this._handleChanges(changes)
  }

  async verifyStamp(target: Types.Document, stampOrStampDocument: Types.Stamp | Types.Document) {
    let document = null

    if (isStamp(stampOrStampDocument)) {
      document = await this.get(stampOrStampDocument.id)
    } else {
      document = await this.get(stampOrStampDocument)
    }

    return this._model.verifyStamp(target, document)
  }


  /**
   *
   * @param document
   */
  save(document: EditableDocument, options: { refresh?: boolean } = {}): void {
    if (!EditableDocument.isEditableDocument(document)) {
      throw new Error('Argument must be an EditableDocument')
    }

    if (!this._model.hasValidAncestry(document)) {
      this._model.commit(document)
      this._changes.remember(document)
      this._editContextRegistry.notifyTrackedDocumentChanged(document.id)
      return
    }

    this._changes.forget(document)

    this._startPendingFieldUploads(document)
    const changes = this._model.save(document)
    this._handleChanges(changes, options)
    this._editContextRegistry.notifyTrackedDocumentChanged(document.id)

    const children = this._changes.removeRememberedChildren(document)

    for (const child of children) {
      this.save(child, options)
    }
  }

  /**
   * Called by EditContext when it flushes tracked documents.
   * Performs the actual save for each dirty document.
   */
  private _autoTrackInEditContext(document: EditableDocument) {
    const parentId = document.p
    const ancestors: string[] = Array.isArray(document.a) ? document.a : []
    const lookupIds = parentId ? [parentId, ...ancestors] : ancestors
    const rootElement = this.context?.rootElement as HTMLElement | null

    if (rootElement && isInsideEditContextBoundary(rootElement)) {
      const localEditContext = findEditContextInDOM(rootElement, true)
      if (localEditContext) {
        localEditContext.track(document, { newDocument: !this._model.isPersistedDocument(document.id) })
      }
      return
    }

    const editContext = this._editContextRegistry.findContextForAnyOf(lookupIds)

    if (editContext) {
      editContext.track(document, { newDocument: !this._model.isPersistedDocument(document.id) })
    }
  }

  private async _flushEditContextDocuments(documents: EditableDocument[], deletedDocuments: Types.Document[] = []) {
    const pending = new Map(documents.map(document => [document.id, document]))
    const deletedIds = new Set(deletedDocuments.map(document => document.id))

    for (const document of documents) {
      const context = this._editContextRegistry.findContextForDocument(document.id)
      for (const tracked of context?.trackedDocuments || []) {
        if (!this._model.isPersistedDocument(tracked.id) && !deletedIds.has(tracked.id)) {
          pending.set(tracked.id, tracked)
        }
      }
    }

    while (pending.size > 0) {
      const ready = [...pending.values()].filter(document => this._model.hasValidAncestry(document))
      if (ready.length === 0) throw new Error('Edit-context documents have missing parents')

      const newDocuments = ready.filter(document => !this._model.isPersistedDocument(document.id))
      for (const document of ready) this.save(document, { refresh: false })
      await this._serverAdapter?.flush()

      // Import newly persisted parents before saving their descendants. get()
      // returns tracked editables and cannot establish persisted ancestry here.
      await Promise.all(newDocuments.map(document => this._loadFromPersistenceAdapter(document.id)))
      for (const document of ready) pending.delete(document.id)
    }

    for (const document of deletedDocuments) {
      const changes = this._model.delete(document.id)
      this._handleChanges(changes, { refresh: false })
    }

    await this._serverAdapter?.flush()
  }

  private _discardEditContextDocuments(documents: EditableDocument[]) {
    for (const document of documents) {
      if (this._model.isPersistedDocument(document.id)) continue
      this._changes.forget(document)
      this._model.discardUnpersistedDocument(document.id)
    }
  }

  saveDetachedVersion(document: Types.Document): void {
    if (EditableDocument.isEditableDocument(document)) {
      this._startPendingFieldUploads(document as EditableDocument)
    }

    const changes = this._model.saveDetachedVersion(document)
    this._handleChanges(changes)
  }

  saveAll(documents: EditableDocument[], options: { refresh?: boolean } = {}): void {
    const changes = []

    for (const d of documents) {
      if (!EditableDocument.isEditableDocument(d)) {
        throw new Error('Argument must be an EditableDocument')
      }

      this._startPendingFieldUploads(d)
      const currentChanges = this._model.save(d)

      if (currentChanges) {
        changes.push(...currentChanges)
      }
    }

    this._handleChanges(changes, options)
  }

  change(identifier: Types.IdOrReference, targetForm: Types.IdOrReference, options?: ChangeDocumentFormOptions): void {
    this._change(identifier, targetForm, options)
  }

  private async _change(identifier: Types.IdOrReference, targetForm: Types.IdOrReference, options?: ChangeDocumentFormOptions) {
    await this.get(identifier)

    const targetFormRequest = this.getForm(targetForm)

    await targetFormRequest

    const changes = this._model.change(identifier, targetForm, options)
    this._handleChanges(changes)
  }

  private _handleChanges(changes: Change[] | null | undefined, options: { refresh?: boolean } = {}) {
    if (!changes || changes.length === 0) {
      return
    }

    if (options.refresh === false) {
      if (this._serverAdapter) {
        this._tryToWriteChangesToPersistenceAdapter(changes)
      }
      return
    }

    if (this._serverAdapter) {
      this._tryToWriteChangesToPersistenceAdapter(changes)
      this._scheduleRefresh(1000)
    } else {
      this._scheduleRefresh()
    }
  }

  private _tryToWriteChangesToPersistenceAdapter(changes: Change[]) {
    const { pendingChanges, changesThatCanBeWritten } = this._categorizeChangesForPersistence(changes)

    this._serverAdapter.writeChanges(changesThatCanBeWritten)
    this._dynamic.pendingChanges.push(...pendingChanges)
  }

  private _categorizeChangesForPersistence(changes: Change[]) {
    const changesThatCanBeWritten = []
    const pendingChanges = []

    for (const change of changes) {
      if (!(change.type === ChangeType.ADDED_DETACHED_VERSION) && !this._model.hasValidAncestry(change.document)) {
        pendingChanges.push(change)
      } else if (this.Uploads.isUploadAssociatedWithDocument(change.document)) {
        pendingChanges.push(change)
      } else {
        changesThatCanBeWritten.push(change)
      }
    }

    return {
      changesThatCanBeWritten,
      pendingChanges
    }
  }

  async canUpgrade(identifier: Types.IdOrReference) {
    const document = await this.get(identifier)

    return this._model.canUpgrade(document)
  }

  upgrade(identifier: Types.IdOrReference): void {
    this._upgrade(identifier)
  }

  private async _upgrade(identifier: Types.IdOrReference) {
    // TODO: this should try to get the document first
    // await this.get(identifier)
    const documentToUpgrade = await this.get(identifier)

    const mostRecentFormRequest = this.getForm(documentToUpgrade.f.id)

    await mostRecentFormRequest

    const changes = this._model.upgrade(identifier)
    this._handleChanges(changes)
  }

  delete(identifier: Types.IdOrReference): void {
    this._delete(identifier)
  }

  private async _delete(identifier: Types.IdOrReference) {
    if (typeof identifier !== 'string') {
      identifier = identifier.id
    }

    const trackedDocument = this._editContextRegistry.findTrackedDocument(identifier)
    if (trackedDocument && !this._model.isPersistedDocument(identifier)) {
      await this._editContextRegistry.deactivateContextsForRootDeletion(identifier)
      const context = this._editContextRegistry.findContextForDocument(identifier)
      context?.untrack(identifier, { resolveDirty: false })
      this._changes.forget(trackedDocument)
      this._model.discardUnpersistedDocument(identifier)
      return
    }

    const document = await this.get(identifier)

    if (document) {
      const documentForm = await this.getFormForDocument(document)
      if (!documentForm) {
        console.error('Could not find form for document')
        return
      }

      const context = this._editContextRegistry.findContextForDocument(document.id)
        || this._editContextRegistry.findAncestorContextForDocument(document, null)

      const deleteImmediately = async () => {
        const changes = this._model.delete(document.id)
        this._handleChanges(changes)
        await this._serverAdapter?.flush()
      }

      const deletedRoot = await this._editContextRegistry.withRootDeletion(document.id, deleteImmediately)
      if (deletedRoot) {
        return
      }

      if (context) {
        context.markDeleted(document)
        return
      }

      await deleteImmediately()
    }
  }

  deleteHard(identifier: Types.IdOrReference): void {
    this._deleteHard(identifier)
  }

  private async _deleteHard(identifier: Types.IdOrReference) {
    if (typeof identifier !== 'string') {
      identifier = identifier.id
    }

    const document = await this.get(identifier)

    if (document) {
      const changes = this._model.deleteHard(document.id)
      this._handleChanges(changes)
    }
  }

  move(identifier: Types.IdOrReference, newParentIdentifier: Types.IdOrReference): void {
    this._move(identifier, newParentIdentifier)
  }

  private async _move(identifier: Types.IdOrReference, newParentIdentifier: Types.IdOrReference) {
    const document = await this.get(identifier)
    const newParent = await this.get(newParentIdentifier)

    if (document && newParent) {
      const [documentForm, newParentForm] = await Promise.all([this.getFormForDocument(document), this.getFormForDocument(newParent)])

      if (!documentForm || !newParentForm) {
        console.error('Could not find forms for documents')
        return
      }

      const changes = this._model.move(document.id, newParent.id)
      this._handleChanges(changes)
    } else {
      // TODO: LOG ERROR ?
    }
  }

  async restoreFromRecycler(documentId, parentId?: string) {
    await this.getAll([documentId, parentId])

    const changes = this._model.restoreFromRecycler(documentId, parentId)
    this._handleChanges(changes)
  }

  restoreSingleDocumentFromRecycler(documentId, parentId?: string) {
    // NOTE: this should try to get documentId and parentId
    // await this.getAll([documentId, parentId])

    const changes = this._model.restoreSingleDocumentFromRecycler(documentId, parentId)
    this._handleChanges(changes)
  }

  buildTemporaryForm(formDocument: Types.Document): Form {
    return this._model.buildTemporaryForm(formDocument)
  }

  buildTemporaryEditableDocument(form: Form): EditableDocument {
    return this._model.buildTemporaryEditableDocument(form)
  }

  isEditableDocument(document: Types.Document): document is EditableDocument {
    return EditableDocument.isEditableDocument(document)
  }

  loadStyle(style) {
    return loadStyle(style)
  }

  /**
   * Returns a reference to the specified document (loading the document if necessary)
   * based on best currently available data (for version and text field)
   */
  async asReference(identifier: Types.IdOrReference | Types.Document | string) {
    if (isDocument(identifier) || typeof identifier === 'string') {
      const document = await this.get(identifier)
      return this._model.asReference(document)
    } else {
      return { id: identifier.id, v: identifier.v, text: <string>identifier.text }
    }
  }

  async get(identifier: Types.IdOrReference | Types.Document, options?: GetOptions): Promise<Types.Document> {
    if (!identifier) {
      return null
    }

    const id = typeof identifier === 'string' ? identifier : (identifier as any).id
    if (id) {
      const tracked = this._editContextRegistry.findTrackedDocument(id)
      if (tracked) return tracked
    }

    const modelDocument = this.Model.get(identifier, options)

    if (modelDocument) {
      return modelDocument
    }

    await this._ensureBooted()
    return (<Promise<Types.Document>>this._get(identifier, options))
  }

  async getAll(identifiers: (Types.IdOrReference | Types.Document)[]): Promise<Types.Document[]> {
    if (!identifiers) {
      return []
    }

    await this._ensureBooted()

    if (!Array.isArray(identifiers)) {
      console.error('getAll requires an array of identifiers to retrieve. Variable argument list is no longer supported')
      return []
    }

    return <Promise<Types.Document[]>>this._get(identifiers)
  }

  async hasChildren(identifier: Types.IdOrReference): Promise<Boolean> {
    await this.findOne(`p:${identifier}`)
    return this._model.hasChildren(identifier)
  }

  async getForm(identifier: Types.IdOrReference): Promise<Form> {
    if (isReference(identifier)) {
      const knownForm = this._model.getForm(identifier)

      if (knownForm) {
        return knownForm
      } else {
        return this._loadFormVersion(identifier.id, identifier.v)
      }
    } else {
      return this._loadFormVersion(identifier)
    }
  }

  private async _loadFormVersion(id: string, v?: string) {
    if (!v) {
      const existing = this._model.get(id)
      if (existing) {
        return this._model.getMostRecentForm(id)
      }
    } else {
      const existing = this._model.getForm({ id, v })
      if (existing) {
        return this._model.getForm({ id, v })
      }
    }

    const requests = [
      this._loadFromPersistenceAdapter(id)
    ]

    if (v) {
      requests.push(this._loadFromPersistenceAdapter(id, v))
    }

    await Promise.all(requests)

    if (v) {
      return this._model.getForm({ id, v })
    } else {
      return this._model.getMostRecentForm(id)
    }
  }

  async getFormForDocument(document: Types.Document | EditableDocument): Promise<Form> {
    if (EditableDocument.isEditableDocument(document)) {
      return document.form
    }

    return this.getForm(document.f)
  }

  getPreviousChanges(document: Types.Document | EditableDocument): PreviousChange | null {
    return this._model.getPreviousChanges(document)
  }

  /** @deprecated Use getFormForDocument instead */
  @deprecated('getFormForDocument', 'Use getFormForDocument instead')
  getDocumentForm(document: Types.Document): any {
    return this._model.getDocumentForm(document)
  }

  async getFormVersions(formIdentifier: Types.IdOrReference): Promise<string[]> {
    if (this._serverAdapter) {
      return this._serverAdapter.getFormVersions(formIdentifier)
    } else {
      return this._model.getFormVersions(formIdentifier)
    }
  }

  private async _get(identifier: Types.IdOrReference | Types.Document, options?: GetOptions): Promise<Types.Document>
  private async _get(identifier: (Types.IdOrReference | Types.Document)[], options?: GetOptions): Promise<Types.Document[]>
  private async _get(identifier: Types.IdOrReference | Types.Document | (Types.IdOrReference | Types.Document)[], options?: GetOptions): Promise<Types.Document | Types.Document[]> {
    if (identifier == null) {
      return null
    }

    const identifiers = Array.isArray(identifier) ? identifier : [identifier]

    const documents = identifiers.map(async (ident) => {
      if (!ident) {
        return null
      }

      let document = this._model.get(ident, options)

      if (document) {
        return document
      }

      const documentId = getDocumentId(ident)

      const trackedDocument = documentId ? this._editContextRegistry.findTrackedDocument(documentId) : null
      if (trackedDocument) {
        return trackedDocument
      }

      // document = await this._documentCache.lookup(documentId)

      // if (document) {
      //   return document
      // }
      await this._ensureLoaded()

      document = this._model.get(ident, options)

      if (document) {
        return document
      }

      const loadedTrackedDocument = documentId ? this._editContextRegistry.findTrackedDocument(documentId) : null
      if (loadedTrackedDocument) {
        return loadedTrackedDocument
      }

      if (this._modelIncomplete) {
        await this._loadFromPersistenceAdapter(documentId, null, options)

        document = this._model.get(documentId, options) // || await this._documentCache.lookup(documentId)

        if (this.isFlagEnabled('logServerLoads')) {
          if (document) {
            this._log.debug({ flag: 'logServerLoads', document }, 'Loaded document %s (%s) at %s', document.id, document.data.name, document.a)
          } else {
            this._log.debug({ flag: 'logServerLoads' }, 'Could not load document %s', documentId)
          }
        }

        return document
      }

      return null
    })

    if (Array.isArray(identifier)) {
      return Promise.all(documents)
    }

    return documents[0]
  }

  public watch(identifier: Types.IdOrReference, callback: DocumentWatchCallback): IDocumentWatcher {
    if (!identifier) {
      return null
    }

    if (this.context.isReleased) {
      throw new Error('Context already released')
    }

    const watcher = this._model.watch(identifier, callback)

    const originalStop = watcher.stop.bind(watcher)

    watcher.stop = () => {
      originalStop()
      this.context.releaseUnnamedResource('DOCUMENT_WATCHER', watcher)
      this._scheduleWatcherCleanup()
    }

    this.context.addUnnamedResource('DOCUMENT_WATCHER', watcher)

    return watcher
  }

  /**
   * @deprecated
   *
   * Use watchMatchingDocuments instead
   *
   * @param  query
   * @param  cb
   * @returns A watcher
   */
  public watchQuery(query, cb: DocumentWatchCallback): IQueryWatcher {
    this._deprecated('Use `watchMatchingDocuments` instead')
    return this.watchMatchingDocuments(query, cb)
  }

  /**
   * Watch all documents being changed in the underlying model and call
   * `cb` when they match the conditions of `query`
   *
   * This is less resource intensive then subscribing to a query when
   * the initial state or a list of all matches is not required
   *
   * @param query
   * @param cb
   * @returns
   */
  public watchMatchingDocuments(query, cb: DocumentWatchCallback): IQueryWatcher {
    const watcher = this._model.watchMatchingDocuments(query, cb)
    const originalStop = watcher.stop.bind(watcher)

    watcher.stop = () => {
      originalStop()
      this.context.releaseUnnamedResource('QUERY_WATCHER', watcher)
      this._scheduleWatcherCleanup()
    }

    this.context.addUnnamedResource('QUERY_WATCHER', watcher)

    return watcher
  }

  public watchAllDocuments(callback: DocumentWatchCallback): IDocumentWatcher {
    return this._model.watchAll(callback)
  }

  /**
   * Create watchers for all passed identifiers.
   * Call the callback with a list of the resolved documents, or null if a document couldn't be found.
   * The callback is always called with same array, that contains different objects at the time of calling.
   *
   * @param identifiers
   * @param callback
   * @returns IDocumentWatcher
   */
  public watchAll(identifiers: Types.IdOrReference[], callback: DocumentsWatchCallback): IDocumentWatcher {
    let stopped = false

    const documentMap = new Map<string, Types.Document>(identifiers.map((identifier) => {
      return [readId(identifier), null]
    }))
    const result = Array(documentMap.size)

    const executeCallback = (documentMap: Map<string, Types.Document>, notifications: Notification[]) => {
      result.splice(0, result.length, ...documentMap.values())
      callback(result, notifications)
    }

    let watchers: IDocumentWatcher[] = []
    const watcher = <IDocumentWatcher>{
      stop() {
        // This stops the watchers from being created and stops already created watchers.
        stopped = true
        for (const watcher of watchers) {
          watcher.stop()
        }
      },
    }

    this.getAll(Array.from(documentMap.keys())).then((documents) => {
      if (stopped) {
        return
      }

      documents.filter(Boolean).forEach(document => documentMap.set(document.id, document))

      const cb = (changedDocument: Types.Document, reason: NotificationReason) => {

        if (reason.deletedHard || reason.deleted) {
          documentMap.set(changedDocument.id, null)
        } else {
          documentMap.set(changedDocument.id, changedDocument)
        }

        const firstCallback = reason.isNoop

        if (!firstCallback) {
          const notification: Notification = {document: changedDocument, reason}
          executeCallback(documentMap, [notification])
        }
      }

      executeCallback(documentMap, [])

      watchers.push(...identifiers.map((identifier) => {
        return this.watch(identifier, cb)
      }))
    })

    return watcher
  }

  watchRevisions(identifier: Types.IdOrReference, callback: DocumentWatchCallback): IDocumentWatcher {
    const watcher = this._model.watchRevisions(identifier, callback)

    const originalStop = watcher.stop.bind(watcher)

    watcher.stop = () => {
      originalStop()
      this.context.releaseUnnamedResource('DOCUMENT_WATCHER_REV', watcher)
      this._scheduleWatcherCleanup()
    }

    this.context.addUnnamedResource('DOCUMENT_WATCHER_REV', watcher)

    return watcher
  }

  integrate(editableDocument: EditableDocument, changedDocument: Types.Document) {
    this._model.mergeChangesIntoEditableDocument(editableDocument, changedDocument)
  }

  private async _ensureLoaded() {
    if (this._status.stage !== 'LOADED') {
      await this._delayUntilLoaded()
    }
  }

  private async _ensureBooted() {
    if (this._status.stage === 'BOOTING') {
      await this._delayUntilBooted()
    }
  }

  async edit(identifier: Types.IdOrReference, options?: GetOptions): Promise<EditableDocument> {
    if (!identifier) {
      console.warn('Cannot edit missing document')
      return null
    }

    const document = await this.get(identifier)

    if (!document) {
      const documentId = typeof identifier === 'string' ? identifier : identifier.id
      throw new Error(`Unknown document ${documentId}`)
    }

    // Reuse the context-owned editable instance. Creating a second wrapper
    // for the same ID would leave the context observing a different object
    // than the form is mutating.
    if (this.isEditableDocument(document)) {
      this._autoTrackInEditContext(document)
      return document
    }

    await this.getFormForDocument(document)

    const editable = this._model.edit(document)
    this._autoTrackInEditContext(editable)
    return editable
  }

  async editAll(identifiers: (Types.IdOrReference | Types.Document)[]): Promise<EditableDocument[]> {
    if (!Array.isArray(identifiers)) {
      console.error('editAll requires an array of identifiers to retrieve. Variable argument list is no longer supported')
      return []
    }

    const edits = identifiers.map(v => this.edit(v))
    return Promise.all(edits)
  }

  private _delayUntilLoaded() {
    if (!this._dynamic.pendingLoaded) {
      let resolve = null
      const promise = new Promise<void>(resolveCb => resolve = resolveCb)
      this._dynamic.pendingLoaded = { promise, resolve }
    }

    return this._dynamic.pendingLoaded.promise
  }

  private _delayUntilBooted() {
    if (!this._dynamic.pendingBooted) {
      let resolve = null
      const promise = new Promise<void>(resolveCb => resolve = resolveCb)
      this._dynamic.pendingBooted = { promise, resolve }
    }

    return this._dynamic.pendingBooted.promise
  }

  private _scheduleWatcherCleanup(refreshIn = 5) {
    if (this._dynamic.wasDestroyed) {
      return
    }

    if (!this._dynamic.watcherCleanupPending) {
      this._dynamic.watcherCleanupPending = true

      setTimeout(() => {
        this._dynamic.watcherCleanupPending = false

        this._model.cleanupWatchers()
        this._cleanupQueryWatchers()
      }, refreshIn)
    }
  }

  private _cleanupQueryWatchers() {
    const subscriptions = this._dynamic.querySubscriptions
    this._dynamic.querySubscriptions = []

    for (const s of subscriptions) {
      if (!s.cancelled) {
        this._dynamic.querySubscriptions.push(s)
      }
    }
  }

  private _scheduleRefresh(refreshIn = 0) {
    if (!this._dynamic || this._dynamic.wasDestroyed) {
      return
    }

    if (!this._dynamic.refreshPending) {
      this._dynamic.refreshPending = true

      setTimeoutNoKeepAlive(() => {
        if (!this._dynamic) {
          return
        }

        if (this._dynamic.refreshPending) {
          this._dynamic.refreshPending = false
          this._refresh()
        }
      }, refreshIn)
    }
  }

  private _refresh() {
    const now = new Date().valueOf()
    const timeSinceLastRefresh = now - this._dynamic.lastRefresh

    if (timeSinceLastRefresh < DEFAULT_MIN_REFRESH_DELAY_IN_MS) {
      const dueIn = (this._dynamic.lastRefresh + DEFAULT_MIN_REFRESH_DELAY_IN_MS - now) + 10
      this._scheduleRefresh(dueIn)
      return
    }

    this._dynamic.lastRefresh = now

    if (this._dynamic.querySubscriptions.length === 0) {
      return
    }

    // Run all refreshes in parallel. Any server calls
    // get batched anyway.
    const subscriptions = this._dynamic.querySubscriptions
    this._dynamic.querySubscriptions = []

    if (this.isFlagEnabled('logQueryRefresh')) {
      this._log.debug({ flag: 'logQueryRefresh', timeSinceLastRefresh }, 'Refreshing #%d queries ...', subscriptions.length)
    }


    for (const s of subscriptions) {
      if (!s.cancelled) {
        this._refreshQuerySubscription(s)
        this._dynamic.querySubscriptions.push(s)
      }
    }
  }

  private async _refreshQuerySubscription(s: QuerySubscription) {
    await s.refresh(this.find(s.queryString, s.findOptions))
  }

  /**
   *
   *
   * @private
   * @param documentId
   * @param version    Only declare version when the specific version is necessary
   * @returns A promise resolved when finish
   */
  private async _loadFromPersistenceAdapter(documentId: string, version?: string, options?: GetOptions): Promise<void> {
    if (this._dynamic.wasDestroyed) {
      return null
    }

    if (!documentId) {
      // this.Log.warn('Attempt to load empty documentId')
      return null
    }

    if (typeof documentId !== 'string') {
      this.Log.warn('Attempt to load documentId that is not a string', documentId)
      return null
    }

    if (this._serverAdapter) {
      const lookupIdentifier = `${documentId}@${version}`

      if (this._dynamic.pendingPersistenceAdapterNextLookup) {
        const alreadyScheduled = this._dynamic.pendingPersistenceAdapterLookupsSet.has(lookupIdentifier)

        if (alreadyScheduled) {
          return this._dynamic.pendingPersistenceAdapterNextLookup
        }
      }

      const lookups = this._dynamic.pendingPersistenceAdapterLookups // (options && options.noImport === true) ? this._dynamic.pendingPersistenceAdapterNonImportLookups : this._dynamic.pendingPersistenceAdapterLookups

      lookups.push({
        id: documentId,
        v: version
      })

      this._dynamic.pendingPersistenceAdapterLookupsSet.add(lookupIdentifier)

      return this._scheduleLookupInPersistenceAdapter()
    } else {
      this._dynamic.pendingLookups.push(documentId)
      this._dynamic.pendingLookups.push(null)

      return this._scheduleLookup()
    }
  }

  private _scheduleLookupInPersistenceAdapter() {
    if (this._dynamic.pendingPersistenceAdapterNextLookup) {
      return this._dynamic.pendingPersistenceAdapterNextLookup
    }

    const asyncExecution = this._executeAsyncLimited(async () => {
      const lookupInfo = this._dynamic.pendingPersistenceAdapterLookups
      this._dynamic.pendingPersistenceAdapterLookups = []
      this._dynamic.pendingPersistenceAdapterNextLookup = null
      this._dynamic.pendingPersistenceAdapterLookupsSet = new Set()

      if (lookupInfo.length > 0) {
        const documents = await this._performLookupInPersistenceAdapter(lookupInfo)
        await importIntoModel(this._model, documents, { documentLookupCallback: this._lookup })
      }

    })

    asyncExecution.catch((error: any) => {
      console.error('Error performing lookup', error)
    })

    this._dynamic.pendingPersistenceAdapterNextLookup = asyncExecution

    return asyncExecution
  }

  private async _executeAsyncLimited(callback: AsyncFunction) {
    const maximumConcurrentLookupCalls = this._options.maximumConcurrentLookupCalls || DEFAULT_MAXIMUM_CONCURRENT_LOOKUPS

    let executeAfter

    if (this._dynamic.concurrentLookups.length < maximumConcurrentLookupCalls) {
      executeAfter = Promise.resolve()
    } else {
      const lastLookup = this._dynamic.concurrentLookups[this._dynamic.concurrentLookups.length - 1]
      executeAfter = lastLookup
    }

    return executeAfter.then(() => {
      return this._executeAsync(callback)
    })
  }

  private async _executeAsync(callback: AsyncFunction) {
    const nextExecution = waitForNextTick()
    this._dynamic.concurrentLookups.push(nextExecution)

    return nextExecution.then(callback).finally(() => {
      const index = this._dynamic.concurrentLookups.findIndex(p => p === nextExecution)

      if (index >= 0) {
        this._dynamic.concurrentLookups.splice(index, 1)
      } else {
        console.warn('CONCURRENT LOOKUP ALREADY REMOVED?')
      }
    })
  }

  private async _performLookupInPersistenceAdapter(lookupInfo: IDocumentLookup[]) {
    // if (lookupInfo.length === 0) {
    //   return
    // }
    if (this.isFlagEnabled('logServerLoads')) {
      this._log.debug({ flag: 'logServerLoads', lookupInfo }, 'Loading #%d documents ...', lookupInfo.length)
    }

    const documents = await this._serverAdapter.getDocuments(lookupInfo)

    if (this.isFlagEnabled('logServerLoads')) {
      this._log.debug({ flag: 'logServerLoads', lookupInfo, documents }, 'Loaded #%d documents via persistence adapter', documents.length)
    }

    for (let i = 0; i < lookupInfo.length; i += 1) {
      const li = lookupInfo[i]
      const d = documents[i]

      if (!d) {
        // tslint:disable-next-line
        // console.warn('could not find', li.id, li.v)
        // tslint:disable-next-line
        // console.log(lookupInfo, documents)
      } else if (li.id !== d.id) {
        // tslint:disable-next-line
        console.error('found wrong document', li.id, d.id)
      } else if (li.v && li.v !== d.v) {
        // tslint:disable-next-line
        console.error('found wrong document version', li.id, li.v, d.id, d.v)
      } else {
      }
    }

    return documents
  }

  private async _scheduleLookup() {
    if (this._dynamic.wasDestroyed) {
      return
    }

    await waitForNextMicroTick()

    if (this._dynamic.pendingLookups.length === 0) {
      return
    }

    const lookupInfo = this._dynamic.pendingLookups
    this._dynamic.pendingLookups = []

    const documents = await this._lookup(lookupInfo)
    await importIntoModel(this._model, documents, { documentLookupCallback: this._lookup })
  }

  async query(queryIdentifier: Types.IdOrReference | Types.Document, rootDocumentIdentifier: Types.IdOrReference, filter?: string, options?: FindOptions): Promise<Types.Document[]> {
    const optionsParam = typeof filter === 'string' ? options : filter
    const filterParam = typeof filter === 'string' ? filter : null

    const [queryDocument] = await this.getAll([queryIdentifier, rootDocumentIdentifier])

    const searchString = this._queryDocumentToSearchString(queryDocument, rootDocumentIdentifier, filterParam)

    const opts = {
      sort: <string>queryDocument.data.sort,
      ...optionsParam
    }

    return this.find(searchString, opts)
  }

  /**
   * Resolves a document path starting from a given document.
   *
   * @param start The starting document ID or reference.
   * @param path The path to resolve (e.g data.ref -> data.ref -> modified.by).
   *             It must end with a reference field
   *
   * @returns A promise that resolves to the document at the end of the path or null
   */
  async resolve(start: Types.IdOrReference, path: string): Promise<Types.Document> {
    const documents = await resolveDocumentPath(start, path, this)
    return documents[0] || null
  }

  /**
   *
   */
  async resolveAll(start: Types.IdOrReference, path: string): Promise<Types.Document[]> {
    return resolveDocumentPath(start, path, this)
  }

  /**
   * Resolves a document path starting from a given document.
   *
   * @param start The starting document ID or reference.
   * @param path  The path to resolve (e.g data.ref -> data.myRef -> modified.by).
   *              It must end with a reference field
   *
   * @returns A promise that resolves to the document at the end of the path as an EditableDocument or null
   */
  async resolveEdit(start: Types.IdOrReference, path: string): Promise<Types.Document> {
    const document = await this.resolve(start, path)

    if (document) {
      return this.edit(document)
    } else {
      return null
    }
  }

  /**
   * Resolves a field at the end of a document path.
   * @param start The starting document ID or reference.
   * @param path The path to resolve (e.g data.ref -> data.myRef -> modified.by -> data.name).
   * @returns A promise that resolves to the field value or null
   */
  async resolveField(start: Types.IdOrReference, path: string): Promise<any> {
    const parts = path.split('->')
    const lastPart = parts.pop()
    const remainingParts = parts.join('->')

    const document = await this.resolve(start, remainingParts)

    if (document) {
      return resolveField(document, lastPart.trim())
    } else {
      return null
    }
  }

  /**
   * Resolves a document path starting from a given document and then resolves a field at the end of the path.
   *
   * @param start The starting document ID or reference.
   * @param path The path to resolve (e.g data.ref -> data.myRef -> modified.by -> data.name).
   *
   * @returns A promise that resolves to the document at the end of the path or null
   */
  async resolveToField(start: Types.IdOrReference, path: string): Promise<any> {
    const parts = path.split('->')
    const lastPart = parts.pop()
    const remainingParts = parts.join('->').trim()

    const document = await this.resolve(start, remainingParts)

    if (document) {
      return resolveField(document, lastPart.trim())
    } else {
      return null
    }
  }

  /**
   * Find documents given a Query object and some options
   *
   * @param query
   * @param options
   *
   * {@label FindWithQueryAndOptions}
   */
  find(query: Query.Query, options?: FindOptions): Promise<Types.Document[]>

  /**
   * Find documents given a QueryBuilder. The query builder is converted to an
   * immutable query snapshot for this call and can be reused afterwards.
   *
   * @param query
   * @param options
   *
   * @beta
   */
  find(query: Query.QueryBuilder, options?: FindOptions): Promise<Types.Document[]>
  find(query: string, options?: FindOptions): Promise<Types.Document[]>
  find(query: string, filterString?: string, options?: FindOptions): Promise<Types.Document[]>
  find(queryArgument: string | Query.Query | Query.QueryBuilder, param1?: string | FindOptions, param2?: FindOptions): Promise<Types.Document[]> {
    const { query, options } = toQueryAndOptions(queryArgument, param1, param2)
    return this._find(query, options)
  }

  async findOne(query: Query.Query, options?: FindOneOptions): Promise<Types.Document>
  async findOne(query: Query.QueryBuilder, options?: FindOneOptions): Promise<Types.Document>
  async findOne(query: string, options?: FindOneOptions): Promise<Types.Document>
  async findOne(query: string, filterString?: string, options?: FindOneOptions): Promise<Types.Document>
  async findOne(queryArgument: string | Query.Query | Query.QueryBuilder, param1?: string | FindOneOptions, param2?: FindOneOptions): Promise<Types.Document> {
    const { query, options } = toQueryAndOptions(queryArgument, param1, param2) as { query: Query.Query, options: FindOneOptions }

    const limitedOptions = getFindOptionsForFindOne(options)
    const documents = await this._find(query, limitedOptions)
    warnIfFindOneMatchedMultiple(documents, options)
    return documents[0]
  }

  private _find(query: Query.Query, options?: FindOptions): Promise<Types.Document[]> {
    return perf.trackQuery('query-total', () => this._findWithCache(query, options))
  }

  private async _findWithCache(query: Query.Query, options?: FindOptions): Promise<Types.Document[]> {
    // Try to find in query cache first if a cache is available , otherwise do uncached find
    if (this._queryCache) {
      const result = await this._queryCache?.find(query, options)
      return Array.isArray(result) ? result : [result]
    } else {
      return await this._findUncached(query, options)
    }
  }


  private async _findUncached(query: Query.Query, options?: FindOptions): Promise<Types.Document[]> {
    if (query.graph.conditions.length === 0) {
      if ((globalThis as any).__AE_QUERY_DEBUG__) {
        console.warn('Empty query')
      }
      return []
    }

    const contextName = this.context?.name || 'none'
    options = options ?? {}

    options.cause = { id: uuid(), caller: 'client', source: contextName }

    const cacheableQuery = { ...query, options: { ...query.options, nowUtcTz: '' } }
    const queryCacheKey = calculateQueryCacheKey(cacheableQuery, options)

    const searchInModel = (this._serverAdapter == null)

    // TODO: Can be refactored to make more clear. E.g a once<T> or limit<T> function
    if (searchInModel) {
      const activePromise = this._activeModelQueries.get(queryCacheKey)

      if (activePromise) {
        this.Log.debug({ queryCacheKey }, 'Reusing active model query')
        return activePromise
      }

      const deferred = buildDeferred<Types.Document[]>()
      this._activeModelQueries.set(queryCacheKey, deferred.promise)

      waitForNextMicroTick().then(() => {
        try {
          const documents = this._model.find(query, options)
          this._activeModelQueries.delete(queryCacheKey)
          deferred.resolve(documents)
        } catch (error) {
          this._activeModelQueries.delete(queryCacheKey)
          this.Log.error({ query, options, error: error.toString(), errorStack: error.stack }, 'Could not execute query against model: %s', error.toString())
          deferred.resolve([])
        }
      })

      return deferred.promise
    } else {
      const activePromise = this._activeServerQueries.get(queryCacheKey)

      if (activePromise) {
        // this.Log.debug({ queryCacheKey }, 'Reusing active server query')
        return activePromise
      }

      const deferred = buildDeferred<Types.Document[]>()
      this._activeServerQueries.set(queryCacheKey, deferred.promise)

      // Enqueue in this turn. The adapter flushes the whole queue on the next
      // tick, so several find() calls made before an await leave together.
      const pendingDocuments = this._serverAdapter.find(query, options)
      void pendingDocuments.then(async documents => {
        try {
          if (this.isFlagEnabled('logServerLoads')) {
            this._log.debug({ flag: 'logServerLoads', query }, 'Finding documents: %s', query)
          }

          if (!isQueryOfPartialDocuments(query, options)) {
            await this._importQueue.enqueue(documents)
          }

          this._activeServerQueries.delete(queryCacheKey)
          deferred.resolve(documents)
        } catch (error) {
          this._activeServerQueries.delete(queryCacheKey)
          this.Log.error({ query, options, error: error.toString(), errorStack: error.stack }, 'Could not execute query against server: %s', error.toString())
          deferred.resolve([])
        }
      }, error => {
        this._activeServerQueries.delete(queryCacheKey)
        this.Log.error({ query, options, error: error.toString(), errorStack: error.stack }, 'Could not execute query against server: %s', error.toString())
        deferred.resolve([])
      })

      return deferred.promise
    }
  }


  async match(identifier: Types.IdOrReference | Types.Document, queryString: string, options?: Query.MatchOptions): Promise<Boolean> {
    // Use the passed document for the match.
    // If it is an id or refernce, get the most recent one.
    let document

    if (isDocument(identifier)) {
      document = identifier
    } else {
      let id
      if (typeof identifier === 'string') {
        id = identifier
      } else {
        id = identifier.id
      }
      this.Warn.once(`aeppic-match: use document`, id, "A document should be used for matching instead of ids or references.")
      document = await this.get(identifier)
    }

    return this._model.match(document, queryString, options)
  }

  async matchAll(identifiers: (Types.IdOrReference | Types.Document)[], queryString: string, options?: Query.MatchOptions): Promise<Boolean[]> {
    const documents = await this.getAll(identifiers)

    return documents.map(d => this._model.match(d, queryString, options))
  }

  subscribeQuery(identifier: Types.IdOrReference | Types.Document, rootDocumentIdentifier: Types.IdOrReference, filter?: string, cb?: (documents: Types.Document[]) => {}): IQuerySubscription {
    if (this.context.isReleased) {
      throw new Error('Context already released')
    }

    const cbParam = typeof filter === 'function' ? filter : cb
    const filterParam = typeof filter === 'string' ? filter : null

    if (this.knowsLocally(identifier) && this.knowsLocally(rootDocumentIdentifier)) {
      const searchString = this._queryDocumentToSearchString(identifier, rootDocumentIdentifier, filterParam)
      return this.subscribe(searchString, cbParam)
    }

    const subscription = this.subscribe('', cbParam)

    this._get([identifier, rootDocumentIdentifier]).then(() => {
      const searchString = this._queryDocumentToSearchString(identifier, rootDocumentIdentifier, filterParam)
      subscription.updateQuery(searchString)
    })

    return subscription
  }

  updateSubscription(subscription: IQuerySubscription, queryString: string, cb?: (documents: Types.Document[]) => {}): IQuerySubscription
  updateSubscription(subscription: IQuerySubscription, queryString: string, options?: FindOptions, cb?: (documents: Types.Document[]) => {}): IQuerySubscription
  updateSubscription(subscription: IQuerySubscription, queryString: string, param2?: any, param3?: any): IQuerySubscription {
    if (subscription) {
      const isCallback = typeof param2 === 'function'
      const options = isCallback ? {} : param2
      const changed = subscription.updateQuery(queryString, options)

      if (changed) {
        subscription.refresh(this.find(queryString, options))
      }

      return subscription
    } else {
      return this.subscribe(queryString, param2, param3)
    }
  }

  subscribe(queryString: string, cb?: (documents: Types.Document[]) => {}): IQuerySubscription
  subscribe(queryString: string, options?: FindOptions, cb?: (documents: Types.Document[]) => {}): IQuerySubscription
  subscribe(queryString: string, param2?: any, param3?: any) {
    let options: FindOptions
    let cb: Function

    if (param2 && typeof param2 !== 'function') {
      options = param2
      cb = param3
    } else {
      cb = param2
    }

    if (this.context.isReleased) {
      throw new Error('Context already released')
    }

    const localData = this._modelIncomplete ? [] : this._model.find(queryString, options)

    const newSubscription = new QuerySubscription(queryString, options, localData)
    this._dynamic.querySubscriptions.push(newSubscription)

    if (this._modelIncomplete) {
      newSubscription.refresh(this.find(queryString, options))
    }

    const originalCancel = newSubscription.cancel.bind(newSubscription)

    newSubscription.cancel = () => {
      originalCancel()
      this.context.releaseUnnamedResource('QUERY_SUBSCRIPTION', newSubscription)
    }

    this.context.addUnnamedResource('QUERY_SUBSCRIPTION', newSubscription)

    if (cb) {
      newSubscription.on('refreshed', (info) => {
        cb(newSubscription.documents, info)
      })
    }

    return newSubscription
  }

  private _queryDocumentToSearchString(identifier: Types.IdOrReference | Types.Document, rootDocumentIdentifier: Types.IdOrReference, filter?: string): string {
    const queryDocument = this._model.get(identifier)
    const rootDocument = this._model.get(rootDocumentIdentifier)

    return buildCompleteSearchString(queryDocument.data.query, filter, queryDocument.data.type, rootDocument, queryDocument)
  }

  async loadUnifiedStylesheet() {
     if (this.Features.isEnabled('unified-stylesheet')) {
       await loadUnifiedStylesheet(this.Root?.layout?.id)
     }
  }

  async renderLayout(layoutIdentifier: Types.IdOrReference, options: LayoutRenderOptions = DEFAULT_LAYOUT_OPTIONS) {
    const rootLayoutId = getDocumentId(layoutIdentifier)
    return perf.track('layout:render', () => this._renderLayout(rootLayoutId, options), rootLayoutId)
  }

  private async _renderLayout(rootLayoutId: string, options: LayoutRenderOptions) {
    await perf.track('layout:ensure-loaded', () => this._ensureLoaded())

    // Try to avoid flash of unstyled content
    await perf.track('layout:stylesheet', () => this.loadUnifiedStylesheet())

    const rootLayout = await perf.track('layout:get', () => this.get(rootLayoutId), rootLayoutId)

    this.Root.type = 'layout'
    this.Root.layout.id = rootLayoutId
    this.Root.missingLayout = !rootLayout
    this.Root.params = options.params

    const dontWaitForRenderPasses = options && options.noWait

    if (!dontWaitForRenderPasses) {
      await perf.track('layout:vue-passes', async () => {
        await this.nextRenderPass() // Wait for ae-root to get the right layout
        await this.nextRenderPass() // The generic layout component needs to be build first (due to packages this is happening async)
        await this.nextRenderPass() // The layout is read asynchronously from the model so there is an extra step
        await this.nextRenderPass() // Ensure the specific dynamic layout component to be actually built
        await this.nextRenderPass()
        await this.nextRenderPass()
      })
    }

    if (!this._dynamic.firstLayoutRendered) {
      if (this._options.removeAfterRender) {
        await this.nextRenderPass() // Ensure the specific dynamic layout component to be actually built
        removeElement(this._options.removeAfterRender)
      }

      this._dynamic.firstLayoutRendered = true
    }

    if (isInBrowser()) {
      this.dispatchDOMEvent(DOMEvents.Navigated, options.params)
    }
  }

  private _startDeferredInitialSync() {
    if (this._initialSyncStarted || !this._deferredInitialSync) return
    this._initialSyncStarted = true
    const startInitialSync = this._deferredInitialSync
    this._deferredInitialSync = null
    void perf.track('boot:initial-sync', () => startInitialSync())
  }

  public async onLinkClicked(e, href) {
    if (!this._dynamic.router) {
      return
    }

    if (this._dynamic.router.onLinkClicked) {
      e?.preventDefault?.()
      const confirmation = this.confirmNavigation()
      if (confirmation && !await confirmation) return
      closeAllDialogs()
      this._dynamic.router.onLinkClicked(e, href)
    }
  }

  public copyToClipboard(text: string) {
    if (typeof text !== 'string') {
      throw new Error('only text supported')
    }

    if (!this.el) {
      throw new Error('Cannot copy to clipboard when not bound to DOM')
    }

    const textArea = document.createElement('textarea')
    const style = textArea.style

    style.position = 'fixed'
    style.top = '0'
    style.left = '0'
    style.width = '2em'
    style.height = '2em'
    style.padding = '0'
    style.border = 'none'
    style.outline = 'none'
    style.boxShadow = 'none'
    style.background = 'transparent'

    textArea.value = text

    this.el.appendChild(textArea)

    textArea.select()

    let successful = false

    try {
      successful = document.execCommand('copy')
    } catch (error) {
      // tslint:disable-next-line
      console.error('Could not copy to clipboard', error)
    }

    textArea.remove()
    return successful
  }

  public async navigateTo(documentIdentifier: Types.Document | Types.IdOrReference, navigationParams?: RouteParams, options?: NavigationOptions) {
    const documentId = getDocumentId(documentIdentifier)

    if (!this._dynamic.router) {
      return
    }

    if (this.context.isReleased) {
      throw new Error('Context already released')
    }

    const shouldContinue = this.dispatchDOMEvent(DOMEvents.WillNavigate, { ...navigationParams, documentId })

    if (shouldContinue) {
      const confirmation = this.confirmNavigation()
      if (confirmation && !await confirmation) return
      closeAllDialogs()
      if (this._dynamic.router.onNavigateTo) {
        this._dynamic.router.onNavigateTo(documentId, navigationParams, options)
      } else if (this._dynamic.router.onLinkClicked) {
        this._dynamic.router.onLinkClicked(null, documentId)
      }
    }
  }

  /** null lets routers keep the synchronous path when there are no pending edits. */
  public confirmNavigation(): Promise<boolean> | null {
    if (this._dynamic.navigationConfirmation) return this._dynamic.navigationConfirmation
    if (!this._editContextRegistry.hasPendingNavigationEdits) return null
    const region = this._getRootDialogRegion()
    if (!region) return Promise.resolve(false)

    this._dynamic.navigationConfirmation = this._editContextRegistry.confirmNavigation((save, discard) =>
      showNavigationConfirmation(region, this.Toasts, save, discard, name => this.contextify(name))).finally(() => {
        this._dynamic.navigationConfirmation = null
      })
    return this._dynamic.navigationConfirmation
  }

  public updateRouteParams(params: RouteParams, options?: { replace?: boolean }) {
    if (!this._dynamic.router) {
      return
    }

    if (this._dynamic.router.updateRouteParams) {
      this._dynamic.router.updateRouteParams(params, options)
    }
  }

  public drillInto(documentIdentifier: string | Types.Document | EditableDocument, options: DrillIntoOptions = {}) {

    if (this.context.isReleased) {
      throw new Error('Context already released')
    }

    const sourceElement = this._resolveContextDispatchElement(
      (options.sourceElement || options.source) as HTMLElement,
      this.context.rootElement as HTMLElement,
    )

    if (!sourceElement) {
      console.error(`Cannot emit ${DOMEvents.DrillInto} event from Aeppic instance without HTML element root to dispatch from in context`, {
        contextName: (this.context as any)?.name,
        targetDocument: documentIdentifier,
      })
      return
    }

    const context = this.context
    const { event: triggerEvent, ...detailOptions } = options

    const eventDetail = {
      targetDocument: documentIdentifier,
      sourceContext: context,
      sourceElement,
      ...detailOptions,
    }

    // Ctrl/Shift are taken by multi-select, so Alt/Option is the "open the
    // document itself" modifier. Callers that do not forward their click still
    // get it through the event currently being dispatched.
    if (!eventDetail.mode && isAltModifiedEvent(triggerEvent ?? (isInBrowser() ? window.event : null))) {
      eventDetail.mode = 'navigate'
    }

    this.dispatchDOMEvent(DOMEvents.DrillInto, eventDetail)
  }

  public async showDrillIntoDialog(documentIdentifier: Types.IdOrReference | EditableDocument, options: DocumentDialogOptions = {}): Promise<DocumentDialogResult> {
    const documentId = getDocumentId(documentIdentifier)

    if (documentId) {
      const existingIndex = this._drillIntoDialogStack.findIndex(entry => entry.documentId === documentId)

      if (existingIndex >= 0) {
        this._closeDrillIntoDialogsAfter(existingIndex)
        return this._drillIntoDialogStack[existingIndex]?.result || { action: 'abort' }
      }
    }

    const defaults = this._drillIntoDialogDefaults
      ? await this._drillIntoDialogDefaults({ documentId: documentId || null, options })
      : null

    if (defaults === false) {
      return { action: 'abort' }
    }

    const explicitOptions = Object.fromEntries(Object.entries(options).filter(([, value]) => value !== undefined))
    const mergedOptions: DocumentDialogOptions = { ...(defaults || {}), ...explicitOptions }

    return this.showDocumentDialog(documentIdentifier, {
      ...mergedOptions,
      source: 'drillInto',
      preferredMode: mergedOptions.preferredMode || 'dock',
    })
  }

  /**
   * Register the application's drill-in defaults (e.g. a content design, edit mode rule and
   * actions). Returns a function that removes the provider if it is still the registered one.
   */
  public setDrillIntoDialogDefaults(provider: DrillIntoDialogDefaultsProvider | null) {
    this._drillIntoDialogDefaults = provider

    return () => {
      if (this._drillIntoDialogDefaults === provider) {
        this._drillIntoDialogDefaults = null
      }
    }
  }

  public async showDocumentDialog(documentIdentifier: Types.IdOrReference | EditableDocument, options: DocumentDialogOptions = {}): Promise<DocumentDialogResult> {
    if (!isInBrowser()) {
      return { action: 'abort' }
    }

    const documentToEdit = EditableDocument.isEditableDocument(documentIdentifier) ? documentIdentifier : await this.edit(documentIdentifier)

    if (!documentToEdit) {
      throw new Error('Cannot show document dialog without a document')
    }

    if (options.source === 'drillInto') {
      const existingIndex = this._drillIntoDialogStack.findIndex(entry => entry.documentId === documentToEdit.id)

      if (existingIndex >= 0) {
        this._closeDrillIntoDialogsAfter(existingIndex)
        return this._drillIntoDialogStack[existingIndex]?.result || { action: 'abort' }
      }
    }

    const dialogRegion = this._getDialogMountRegion(options.mountRegion, options.sourceElement)
    const dialogMountRegion = this._getDocumentDialogMountRegion(options.mountRegion, options.source, dialogRegion)

    if (!dialogRegion) {
      throw new Error('Cannot show document dialog without a dialog region')
    }

    loadStyle(COMMAND_DIALOG_CSS, 'ae-command-dialog')

    const sourceRootDocumentId = getDocumentId(options.sourceContext?.rootDocumentId || null)
    const documentAncestorIds = Array.isArray((documentToEdit as any).a) ? (documentToEdit as any).a : []
    const isInSourceContext = !!sourceRootDocumentId && (
      documentToEdit.id === sourceRootDocumentId
      || documentAncestorIds.indexOf(sourceRootDocumentId) >= 0
    )
    const isExternalDrillIntoReference = options.source === 'drillInto' && !!sourceRootDocumentId && !isInSourceContext
    const dialogReadonly = options.readonly ?? isExternalDrillIntoReference
    const dialogCommandButtons = await this._resolveDocumentDialogCommands(options.commands, documentToEdit)
    const dialogAutoSave = !dialogReadonly && (options.autoSave ?? options.source === 'drillInto')
    const isCreateDialog = options.source !== 'drillInto' && !this._model.isPersistedDocument(documentToEdit.id)
    const defaultDialogLabels = this._getDocumentDialogLabels(options.locale)
    const aeppic = this
    const layered = this._features?.isEnabled(LAYERED_OVERLAYS_FEATURE) === true
    const isLayeredDrillInto = layered && options.source === 'drillInto'
    const drillLevel = options.source === 'drillInto' ? this._drillIntoDialogStack.length + 1 : 0
    const dialogActions = Array.isArray(options.actions) ? options.actions.filter(action => action && action.id && typeof action.onClick === 'function') : []
    const layoutDefaults: DocumentDialogLayouts = this._features?.getOptionsFor('document-dialog-layouts') || {}
    const dialogLayouts: DocumentDialogLayouts = {
      dialogLayoutId: options.dialogLayoutId ?? layoutDefaults.dialogLayoutId,
      headerLayoutId: options.headerLayoutId ?? layoutDefaults.headerLayoutId,
      bodyLayoutId: options.bodyLayoutId ?? layoutDefaults.bodyLayoutId,
      commandsLayoutId: options.commandsLayoutId ?? layoutDefaults.commandsLayoutId,
    }
    const useDialogLayouts = this._features?.isEnabled('document-dialog-layouts')
      || Object.keys(dialogLayouts).some(key => Object.prototype.hasOwnProperty.call(options, key))

    if (isLayeredDrillInto && drillLevel === 1) {
      this._installDrillIntoHistoryNavigation()
      // Reserve during the opening action so browser Back closes the drill-in instead of leaving the page.
      this.Router?.reserveBrowserBackEntry?.()
    }
    const host = dialogRegion.ownerDocument.createElement('div')
    host.classList.add('ae-command-dialog-host')
    const editContextName = isCreateDialog ? 'new-document-dialog' : 'document-dialog'
    host.dataset.aeEditContextName = editContextName
    if (dialogReadonly) {
      host.setAttribute('data-ae-edit-context-boundary', '')
    }
    const dialogEditContext = options.source !== 'drillInto' && !dialogReadonly
      ? this._editContextRegistry.createHandle({ mode: 'explicit', debugName: editContextName })
      : null
    dialogEditContext?.track(documentToEdit)
    dialogRegion.appendChild(host)
    dialogEditContext?.mount(host)

    // The dialog surface moves into the overlay layer; trace it back to the element that opened it.
    if (layered && options.sourceElement) {
      setOverlayOwner(host, options.sourceElement)
    }

    let closeDrillIntoDialog = () => {}
    let dialogComponent: any = null

    const resultPromise = new Promise<DocumentDialogResult>((resolve) => {
      let settled = false
      let component

      const finish = (result: DocumentDialogResult) => {
        if (settled) {
          return
        }

        settled = true
        this._unregisterDrillIntoDialog(documentToEdit.id, resultPromise)
        resolve(result)

        aeppic.nextRenderPass(() => {
          dialogEditContext?.release()
          component.$destroy()
          if (host.parentNode) {
            host.parentNode.removeChild(host)
          }
        })
      }

      component = new Vue(<any>{
        data() {
          return {
            document: documentToEdit,
            title: options.title || documentToEdit.data?.name || documentToEdit.f?.text || defaultDialogLabels.newDocument,
            subtitle: options.subtitle || documentToEdit.f?.text || '',
            designName: options.designName || 'edit',
            layoutId: options.layoutId || '',
            saveLabel: options.saveLabel || defaultDialogLabels.save,
            cancelLabel: options.cancelLabel || defaultDialogLabels.cancel,
            openLabel: options.openLabel || (isExternalDrillIntoReference ? defaultDialogLabels.openMaster : defaultDialogLabels.open),
            expandLabel: defaultDialogLabels.expand,
            collapseLabel: defaultDialogLabels.collapse,
            newLabel: defaultDialogLabels.new,
            readonlyLabel: defaultDialogLabels.readonly,
            editableLabel: defaultDialogLabels.editable,
            detailLabel: defaultDialogLabels.detail,
            labels: defaultDialogLabels,
            backLabel: defaultDialogLabels.back,
            levelLabel: defaultDialogLabels.level,
            drillLevel,
            isLayeredDrillInto,
            headerActions: dialogActions.filter(action => action.placement !== 'footer'),
            footerActions: dialogActions.filter(action => action.placement === 'footer'),
            dialogCommands: dialogCommandButtons,
            isDrillIntoDialog: options.source === 'drillInto',
            isCreateDialog,
            isExternalDrillIntoReference,
            dialogReadonly,
            // Legacy phone dialogs use fullscreen; layered dialogs adapt to sheets through shared CSS.
            dialogFullscreen: options.fullScreen ?? (!layered && aeppic.Device === 'Phone' ? true : null),
            saving: false,
            error: null,
          }
        },
        provide() {
          return {
            getAeppicContext(contextName: string, queryScopeRoot: Types.DocumentId | Types.Document) {
              return aeppic.contextify(contextName, queryScopeRoot)
            }
          }
        },
        methods: {
          abort() {
            if (dialogEditContext?.ownsContext) {
              dialogEditContext.cancelEditing()
            }
            finish({ action: 'abort' })
          },
          async save() {
            if (this.saving) {
              return
            }

            this.saving = true
            this.error = null

            try {
              if (dialogEditContext?.ownsContext) {
                dialogEditContext.stopEditing()
              } else {
                await aeppic.save(this.document)
              }
              finish({ action: 'save', document: this.document })
            } catch (error) {
              this.error = error?.message || error?.toString() || 'Could not save document'
              this.saving = false
            }
          },
          async saveAndNavigateToDocument() {
            if (this.saving) {
              return
            }

            this.saving = true
            this.error = null

            try {
              if (dialogEditContext?.ownsContext) {
                dialogEditContext.stopEditing()
              } else {
                aeppic.save(this.document)
              }
              const documentId = this.document.id
              finish({ action: 'navigate', document: this.document })
              aeppic._closeAllDrillIntoDialogs()
              aeppic.navigateTo(documentId)
            } catch (error) {
              this.error = error?.message || error?.toString() || 'Could not save document'
              this.saving = false
            }
          },
          dialogCommandClass(dialogCommand) {
            return {
              'ae-command-dialog__button': true,
              'ae-command-dialog__button--primary': Boolean(dialogCommand?.primary),
            }
          },
          async runDialogCommand(dialogCommand, { force = false } = {}) {
            if (this.saving || !dialogCommand) {
              return
            }

            if (dialogCommand.disabled && !force) {
              return
            }

            // Cancel/abort is a built-in: just close
            if (aeppic._isAbortDocumentDialogCommand(dialogCommand.document)) {
              finish({ action: 'abort' })
              return
            }

            this.saving = true
            this.error = null

            try {
              const command = dialogCommand.document
              const executeOptions: any = { navigation: true, ui: false }
              if (force) { executeOptions.skipValidation = true }
              const result = await aeppic.Commands.execute(command, this.document, {}, executeOptions)

              if (!result?.ok) {
                throw new Error(result?.error || 'Command failed')
              }

              finish({ action: 'command', document: this.document, command: dialogCommand.document, result })
            } catch (error) {
              this.error = error?.message || error?.toString() || 'Could not execute command'
              this.saving = false
            }
          },
          async navigateToDocument() {
            const documentId = this.document.id
            this.error = null

            try {
              if (!this.dialogReadonly) {
                if (dialogEditContext?.ownsContext) {
                  dialogEditContext.stopEditing()
                } else {
                  await aeppic.save(this.document)
                }
              }

              finish({ action: 'navigate', document: this.document })
              aeppic._closeAllDrillIntoDialogs()
              aeppic.navigateTo(documentId)
            } catch (error) {
              this.error = error?.message || error?.toString() || 'Could not save document'
            }
          },
          toggleFullscreen() {
            this.dialogFullscreen = this.dialogFullscreen !== true
          },
          renderReferenceChip(createElement) {
            // A compact chip; the explanation lives in a popover instead of a banner above the content.
            return createElement('ae-popup', {
              props: { placement: 'bottom-start', title: this.labels.referenceInfoTitle, closeLabel: this.cancelLabel },
              scopedSlots: {
                base: ({ toggle, isOpen }) => createElement('button', {
                  class: 'ae-command-dialog__reference-chip',
                  attrs: { type: 'button', 'aria-haspopup': 'dialog', 'aria-expanded': isOpen ? 'true' : 'false', title: this.labels.referenceInfoTitle },
                  on: { click: (event) => toggle({ target: event.currentTarget }) }, // anchor to the chip, not the pointer
                }, [
                  createElement('i', { class: 'fa fa-share-alt', attrs: { 'aria-hidden': 'true' } }),
                  createElement('span', {}, this.labels.reference),
                  createElement('i', { class: 'ae-command-dialog__reference-chip-info fa-regular fa-circle-question', attrs: { 'aria-hidden': 'true' } }),
                ]),
                default: () => createElement('div', { class: 'ae-command-dialog__reference-info' }, [
                  createElement('strong', {}, this.labels.referenceInfoTitle),
                  createElement('p', {}, this.labels.referenceInfo),
                ]),
              },
            })
          },
          close() {
            // Layered drill-ins step back one level with Back; the close button leaves the whole drill-in.
            if (this.isLayeredDrillInto) {
              aeppic._closeAllDrillIntoDialogs()
              return
            }

            this.abort()
          },
          async runDialogAction(action) {
            if (this.saving) {
              return
            }

            this.saving = true
            this.error = null
            const toastId = aeppic.Toasts.show(action.label, { active: true })

            try {
              await action.onClick(this.document, { close: () => this.close() })
              aeppic.Toasts.complete(toastId, { message: action.label })
            } catch (error) {
              this.error = error?.message || error?.toString() || 'Action failed'
              aeppic.Toasts.complete(toastId, { message: this.error, kind: 'error' })
            } finally {
              this.saving = false
            }
          },
          renderDialogAction(createElement, action, buttonClass) {
            const iconOnly = buttonClass === 'ae-command-dialog__action-btn' && !!action.icon

            return createElement('button', {
              key: action.id,
              class: {
                [buttonClass]: true,
                'ae-command-dialog__button--primary': buttonClass !== 'ae-command-dialog__action-btn' && Boolean(action.primary),
              },
              attrs: { type: 'button', title: action.label, 'aria-label': action.label, 'data-ae-dialog-action': action.id },
              on: { click: () => this.runDialogAction(action) },
            }, [
              action.icon ? createElement('i', { class: action.icon, attrs: { 'aria-hidden': 'true' } }) : null,
              iconOnly ? null : createElement('span', {}, action.label),
            ])
          },
          getDialogContextLabel() {
            if (this.isCreateDialog) {
              return this.newLabel
            }
            if (this.dialogReadonly) {
              return this.detailLabel
            }
            if (dialogAutoSave) {
              return this.labels.autoSaveContext
            }
            return this.labels.editContext
          },
          getDialogModeLabel() {
            if (this.dialogReadonly) {
              return this.readonlyLabel
            }
            if (dialogAutoSave) {
              return this.labels.autoSaved
            }
            return this.editableLabel
          },
          getDialogLayoutParams(): Record<string, unknown> & { dialog: DocumentDialogLayoutContext } {
            // Recreate the snapshot on every render so asynchronous state reaches layouts.
            return {
              ...(options.params || {}),
              dialog: {
                document: this.document,
                title: this.title,
                subtitle: this.subtitle,
                labels: { ...this.labels, save: this.saveLabel, cancel: this.cancelLabel, open: this.openLabel },
                contextLabel: this.getDialogContextLabel(),
                modeLabel: this.getDialogModeLabel(),
                readonly: this.dialogReadonly,
                autoSave: dialogAutoSave,
                saving: this.saving,
                error: this.error,
                fullscreen: this.dialogFullscreen,
                isDrillInto: this.isDrillIntoDialog,
                isCreate: this.isCreateDialog,
                isExternalReference: this.isExternalDrillIntoReference,
                layered: this.isLayeredDrillInto,
                level: this.drillLevel,
                formLayoutId: this.layoutId,
                formView: options.formView,
                designName: this.designName,
                headerActions: this.headerActions,
                footerActions: this.footerActions,
                commands: this.dialogCommands,
                layouts: dialogLayouts,
                save: this.save,
                abort: this.abort,
                close: this.close,
                navigate: this.navigateToDocument,
                toggleFullscreen: this.toggleFullscreen,
                runAction: this.runDialogAction,
                runCommand: this.runDialogCommand,
                saveDocument: (document) => aeppic.save(document),
              },
            }
          },
          renderLayoutSlots(createElement) {
            const params = this.getDialogLayoutParams()
            const renderLayout = (layoutId) => createElement('ae-layout', { props: { layoutId, params } })

            if (dialogLayouts.dialogLayoutId) {
              return { default: () => renderLayout(dialogLayouts.dialogLayoutId) }
            }

            return {
              header: () => dialogLayouts.headerLayoutId ? renderLayout(dialogLayouts.headerLayoutId) : [],
              body: () => dialogLayouts.bodyLayoutId ? renderLayout(dialogLayouts.bodyLayoutId) : [],
              commands: () => dialogLayouts.commandsLayoutId ? renderLayout(dialogLayouts.commandsLayoutId) : [],
            }
          },
          renderForm(createElement) {
            return createElement('ae-form', {
              ref: 'form',
              class: 'ae-command-dialog__form',
              props: {
	                document: this.document,
	                layoutId: this.layoutId,
	                view: options.formView,
	                autoSave: dialogAutoSave,
	                readonly: this.dialogReadonly,
              },
              on: {
                save: (document) => aeppic.save(document),
              },
            })
          },
          renderDialogDesign(createElement) {
            if (!this.designName) {
              return this.renderForm(createElement)
            }

            return createElement('ae-design', {
              props: {
                name: this.designName,
                document: this.document,
                watch: false,
                params: {
                  ...(options.params || {}),
                  layoutId: this.layoutId,
                  readonly: this.dialogReadonly,
                  autoSave: dialogAutoSave,
                },
              },
              scopedSlots: {
                default: () => this.renderForm(createElement),
              }
            })
          }
        },
        render(createElement) {
          return createElement('ae-dialog', {
            ref: 'dialog',
            props: {
              open: true,
              title: this.title,
              fullScreen: this.dialogFullscreen,
              preferredMode: options.preferredMode,
              appearance: options.appearance,
              mountRegion: dialogMountRegion,
              stackType: this.isDrillIntoDialog ? 'drillInto' : 'dialog',
              backdropClose: options.backdropClose !== false,
              dialogSize: options.dialogSize,
              dialogStyle: options.dialogStyle,
              dialogClasses: Object.assign({
                'ae-command-dialog--drill-into': this.isDrillIntoDialog,
                'ae-command-dialog--create': this.isCreateDialog,
                'ae-command-dialog--auto-save': dialogAutoSave,
                'ae-command-dialog--readonly': this.dialogReadonly,
                'ae-command-dialog--context-editable': this.isDrillIntoDialog && !this.dialogReadonly && !this.isExternalDrillIntoReference,
                'ae-command-dialog--context-reference': this.isDrillIntoDialog && this.isExternalDrillIntoReference,
              }, options.dialogClasses || {}),
            },
            on: {
              close: this.abort,
            },
            scopedSlots: useDialogLayouts ? this.renderLayoutSlots(createElement) : {
              header: () => createElement('div', { class: 'ae-command-dialog__header' }, [
                (this.isLayeredDrillInto && this.drillLevel > 1) ? createElement('button', {
                  class: 'ae-command-dialog__action-btn ae-command-dialog__back',
                  attrs: { type: 'button', title: this.backLabel, 'aria-label': this.backLabel },
                  on: { click: this.abort },
                }, [createElement('i', { class: 'fa-solid fa-chevron-left', attrs: { 'aria-hidden': 'true' } })]) : null,
                this.isDrillIntoDialog ? null : createElement('div', { class: 'ae-command-dialog__form-icon' }, [
                  createElement('ae-design', { props: { name: 'icon', document: this.document }, class: 'ae-command-dialog__form-icon-design' }),
                ]),
                createElement('div', { class: 'ae-command-dialog__header-main' }, [
                  (this.isDrillIntoDialog || this.isCreateDialog) ? createElement('div', { class: 'ae-command-dialog__header-meta' }, [
                    (this.isLayeredDrillInto && this.drillLevel > 1) ? createElement('span', { class: 'ae-command-dialog__level' }, `${this.levelLabel} ${this.drillLevel}`) : null,
                    this.isExternalDrillIntoReference
                      ? this.renderReferenceChip(createElement)
                      : createElement('span', { class: 'ae-command-dialog__context-label' }, this.isCreateDialog ? this.newLabel : (this.dialogReadonly ? this.detailLabel : (dialogAutoSave ? this.labels.autoSaveContext : this.labels.editContext))),
                    this.isDrillIntoDialog ? createElement('span', { class: 'ae-command-dialog__mode-badge' }, [
                      createElement('i', { class: this.dialogReadonly ? 'ae-command-dialog__mode-icon fa fa-lock' : 'ae-command-dialog__mode-icon fa fa-pencil', attrs: { 'aria-hidden': 'true' } }),
                      this.dialogReadonly ? this.readonlyLabel : (dialogAutoSave ? this.labels.autoSaved : this.editableLabel),
                    ]) : null,
                  ]) : null,
                  createElement('span', { class: 'ae-command-dialog__title' }, this.title),
                  this.subtitle ? createElement('span', { class: 'ae-command-dialog__subtitle' }, this.subtitle) : null,
                ]),
                createElement('div', { class: 'ae-command-dialog__header-actions' }, [
                  ...this.headerActions.map(action => this.renderDialogAction(createElement, action, 'ae-command-dialog__action-btn')),
                  (!this.isDrillIntoDialog && this.dialogCommands.length === 0) ? createElement('button', {
                    class: 'ae-command-dialog__action-btn',
                    attrs: { type: 'button', title: this.dialogFullscreen ? this.collapseLabel : this.expandLabel },
                    on: { click: this.toggleFullscreen },
                  }, [createElement('i', { class: this.dialogFullscreen ? 'fa-solid fa-down-left-and-up-right-to-center' : 'fa-solid fa-up-right-and-down-left-from-center', attrs: { 'aria-hidden': 'true' } })]) : null,
                  createElement('button', {
                    class: 'ae-command-dialog__close',
                    attrs: { type: 'button', title: this.cancelLabel },
                    on: { click: this.close },
                  }, [createElement('i', { class: 'fa-solid fa-xmark', attrs: { 'aria-hidden': 'true' } })]),
                ]),
              ]),
              body: () => createElement('div', { class: 'ae-command-dialog__body' }, [
                this.error ? createElement('div', { class: 'ae-command-dialog__error' }, this.error) : null,
                this.renderDialogDesign(createElement),
              ]),
              commands: () => {
                if (this.isDrillIntoDialog) {
                  return createElement('div', { class: 'ae-command-dialog__commands ae-command-dialog__commands--drill-into' }, [
                    ...this.footerActions.map(action => this.renderDialogAction(createElement, action, 'ae-command-dialog__button')),
                    createElement('button', {
                      class: 'ae-command-dialog__button ae-command-dialog__button--primary ae-command-dialog__button--open',
                      attrs: { type: 'button', disabled: this.saving },
                      on: { click: this.navigateToDocument },
                    }, [
                      createElement('i', { class: 'fa-solid fa-up-right-and-down-left-from-center', attrs: { 'aria-hidden': 'true' } }),
                      createElement('span', {}, this.openLabel),
                    ]),
                  ])
                }

                if (this.dialogCommands.length > 0) {
                  return createElement('div', { class: 'ae-command-dialog__commands' }, this.dialogCommands.map(dialogCommand => (
                    createElement('button', {
                      key: dialogCommand.id,
                      class: this.dialogCommandClass(dialogCommand),
                      attrs: {
                        type: 'button',
                        disabled: this.saving || dialogCommand.disabled,
                        title: dialogCommand.disabledReason || dialogCommand.label,
                      },
                      on: { click: (e) => this.runDialogCommand(dialogCommand, { force: e?.metaKey || e?.ctrlKey }) },
                    }, dialogCommand.label)
                  )))
                }

                return createElement('div', { class: 'ae-command-dialog__commands' }, [
                  createElement('button', {
                    class: 'ae-command-dialog__button ae-command-dialog__button--cancel',
                    attrs: { type: 'button', disabled: this.saving },
                    on: { click: this.abort },
                  }, this.cancelLabel),
                  createElement('button', {
                    class: 'ae-command-dialog__button ae-command-dialog__button--primary',
                    attrs: { type: 'button', disabled: this.saving },
                    on: { click: this.save },
                  }, this.saveLabel),
                ])
              },
            }
          })
        }
      })

      component.$mount()
      host.appendChild(component.$el)
      dialogComponent = component

      closeDrillIntoDialog = () => {
        if (component && component.abort) {
          component.abort()
        } else {
          finish({ action: 'abort' })
        }
      }
    })

    if (options.source === 'drillInto') {
      this._drillIntoDialogStack.push({
        documentId: documentToEdit.id,
        close: closeDrillIntoDialog,
        result: resultPromise,
        dialog: () => dialogComponent?.$refs?.dialog,
      })
    }

    return resultPromise
  }

  private _closeDrillIntoDialogsAfter(index: number) {
    const layeredDialog = this._drillIntoDialogStack[index]?.dialog?.()

    // One logical stack: re-drilling an open document closes every entry above it, dialogs included.
    if (layeredDialog && isLayeredDialogRegistered(layeredDialog)) {
      closeLayeredDialogsAbove(layeredDialog)
      return
    }

    const dialogsToClose = this._drillIntoDialogStack.slice(index + 1).reverse()

    for (const dialog of dialogsToClose) {
      dialog.close()
    }
  }

  private _closeAllDrillIntoDialogs() {
    const firstLayeredDialog = this._drillIntoDialogStack[0]?.dialog?.()

    // Leaving the drill-in also closes dialogs opened above it in the logical stack.
    if (firstLayeredDialog && isLayeredDialogRegistered(firstLayeredDialog)) {
      closeLayeredDialogsAbove(firstLayeredDialog)
    }

    const dialogsToClose = this._drillIntoDialogStack.slice().reverse()

    for (const dialog of dialogsToClose) {
      dialog.close()
    }
  }

  /**
   * Browser Back steps out of layered drill-ins one level at a time. Content in the active
   * drill-in (e.g. folder navigation) can consume the step first by cancelling the
   * `ae-drill-into-back` event dispatched from its surface.
   */
  private _installDrillIntoHistoryNavigation() {
    if (this._rootContext.domElementListeners.has('DrillIntoHistoryNavigation')) {
      return
    }

    // Registered on the root context: the drill-in stack outlives the context that opened it.
    this._addDOMEventListener('DrillIntoHistoryNavigation', 'ae-browser-history-navigation', (event: CustomEvent) => {
      const detail = event.detail || {}
      const top = this._drillIntoDialogStack[this._drillIntoDialogStack.length - 1]

      if (event.defaultPrevented || detail.direction !== 'back' || !top) {
        return
      }

      event.preventDefault()

      const surface = top.dialog?.()?.$refs?.container as HTMLElement | undefined
      const contentBack = new CustomEvent('ae-drill-into-back', { bubbles: true, cancelable: true, detail: { documentId: top.documentId } })

      if (surface?.dispatchEvent && !surface.dispatchEvent(contentBack)) {
        return
      }

      if (this._drillIntoDialogStack.length === 1) {
        detail.releaseBackEntry = true
      }

      top.close()
    }, undefined, this._rootContext)
  }

  private _unregisterDrillIntoDialog(documentId: Types.DocumentId, result: Promise<DocumentDialogResult>) {
    const index = this._drillIntoDialogStack.findIndex(entry => entry.documentId === documentId && entry.result === result)

    if (index >= 0) {
      this._drillIntoDialogStack.splice(index, 1)
    }
  }

  private async _resolveDocumentDialogCommands(commandRefs: Array<Types.Document | Types.IdOrReference>, targetDocument: EditableDocument) {
    if (!Array.isArray(commandRefs) || commandRefs.length === 0) {
      return []
    }

    const buttons = []

    for (const commandRef of commandRefs) {
      const command = await this._resolveDocumentDialogCommand(commandRef)

      if (!command || command.hidden === true) {
        continue
      }

      const visible = await this._isDocumentDialogCommandVisible(command, targetDocument)

      if (!visible) {
        continue
      }

      const enabled = await this._isDocumentDialogCommandEnabled(command, targetDocument)
      const hasRight = await this._hasRightToExecuteDocumentDialogCommand(command, targetDocument)

      buttons.push({
        id: 'dialog-command:' + command.id,
        document: command,
        label: this._documentDialogCommandLabel(command),
        primary: command?.data?.class === 'primary',
        disabled: !enabled || !hasRight,
        disabledReason: !enabled ? 'Bedingungen nicht erfuellt' : (!hasRight ? 'Keine Berechtigung' : undefined),
        sort: Number(command?.data?.sort) || 0,
      })
    }

    buttons.sort((left, right) => left.sort - right.sort || left.label.localeCompare(right.label, 'de'))

    if (!buttons.some(button => button.primary)) {
      const defaultPrimary = buttons.slice().reverse().find(button => !this._isAbortDocumentDialogCommand(button.document))

      if (defaultPrimary) {
        defaultPrimary.primary = true
      }
    }

    return buttons
  }

  private async _resolveDocumentDialogCommand(commandRef: Types.Document | Types.IdOrReference) {
    if (!commandRef) {
      return null
    }

    if (EditableDocument.isEditableDocument(commandRef)) {
      return commandRef.cloneAsDocument()
    }

    if (isDocument(commandRef)) {
      return commandRef
    }

    try {
      return await this.get(commandRef)
    } catch (error) {
      console.warn('Could not load dialog command', commandRef, error)
      return null
    }
  }

  private async _isDocumentDialogCommandVisible(command: Types.Document, targetDocument: EditableDocument) {
    try {
      return await (this.Commands as any).isVisible(command, targetDocument)
    } catch (error) {
      console.warn('Could not evaluate dialog command visibility', command?.id, error)
      return false
    }
  }

  private async _isDocumentDialogCommandEnabled(command: Types.Document, targetDocument: EditableDocument) {
    try {
      return await (this.Commands as any).isEnabled(command, targetDocument)
    } catch (error) {
      console.warn('Could not evaluate dialog command enabled state', command?.id, error)
      return false
    }
  }

  private async _hasRightToExecuteDocumentDialogCommand(command: Types.Document, targetDocument: EditableDocument) {
    if (!(this.Commands as any).hasRightToExecute) {
      return true
    }

    try {
      return await (this.Commands as any).hasRightToExecute(command, targetDocument)
    } catch (error) {
      console.warn('Could not evaluate dialog command execution rights', command?.id, error)
      return false
    }
  }

  private _documentDialogCommandLabel(command: Types.Document) {
    const label = this._cleanDialogText(command?.data?.label)
      || this._cleanDialogText(command?.data?.name)
      || this._cleanDialogText((command as any)?.text)
      || this._cleanDialogText(command?.id)

    return this._translateDocumentDialogLabel(label)
  }

  private _getDocumentDialogLabels(locale?: string) {
    const label = (key: string) => this._translateDocumentDialogLabel(key, locale)

    return {
      reference: label('reference'),
      referenceInfoTitle: label('referenceInfoTitle'),
      referenceInfo: label('referenceInfo'),
      autoSaveContext: label('autoSaveContext'),
      editContext: label('editContext'),
      autoSaved: label('autoSaved'),
      save: label('save'),
      cancel: label('cancel'),
      open: label('open'),
      openMaster: label('openMaster'),
      expand: label('expand'),
      collapse: label('collapse'),
      new: label('new'),
      newDocument: label('newDocument'),
      readonly: label('readonly'),
      editable: label('editable'),
      detail: label('detail'),
      back: label('back'),
      level: label('level'),
    }
  }

  private _translateDocumentDialogLabel(label: string, locale?: string) {
    const cleanedLabel = this._cleanDialogText(label)
    const labelKey = cleanedLabel.toLowerCase()
    const localeKey = this._getDocumentDialogLocaleKey(locale)
    const labels = DIALOG_LABELS[localeKey] || DIALOG_LABELS.en

    return labels[labelKey] || DIALOG_LABELS.en[labelKey] || cleanedLabel
  }

  private _getDocumentDialogLocaleKey(requestedLocale?: string) {
    const accountLocale = this.Account?.data?.locale as string
    const locale = requestedLocale
      || (accountLocale && accountLocale !== 'auto' ? accountLocale : '')
      || (typeof navigator !== 'undefined' ? navigator.language : '')
    const normalizedLocale = String(locale || '').trim().toLowerCase()

    if (normalizedLocale.startsWith('de')) {
      return 'de'
    }

    if (normalizedLocale.startsWith('fr')) {
      return 'fr'
    }

    return 'en'
  }

  private _isAbortDocumentDialogCommand(command: Types.Document) {
    const text = [
      command?.id,
      command?.data?.name,
      command?.data?.label,
      (command as any)?.text,
    ].map(value => this._cleanDialogText(value).toLowerCase()).join(' ')

    return /\b(abort|cancel|abbrechen)\b/.test(text)
  }

  private _cleanDialogText(value: any) {
    return typeof value === 'string' ? value.trim() : ''
  }

  private _getDialogMountRegion(mountRegion?: string | HTMLElement, sourceElement?: HTMLElement) {
    if (!isInBrowser()) {
      return null
    }

    const rootElement = this.rootEl as HTMLElement

    if (mountRegion && typeof mountRegion === 'object' && mountRegion.nodeType === 1) {
      return mountRegion
    }

    const normalizedMountRegion = String(mountRegion || 'auto').trim().toLowerCase()

    if (normalizedMountRegion === 'window' || normalizedMountRegion === 'body') {
      return document.body
    }

    if (normalizedMountRegion === 'root' || normalizedMountRegion === 'global') {
      return this._getRootDialogRegion()
    }

    if (normalizedMountRegion === 'application' || normalizedMountRegion === 'app') {
      return getNearestDialogRegion(sourceElement, null) || this._getRootDialogRegion()
    }

    if (normalizedMountRegion.startsWith('#') || normalizedMountRegion.startsWith('.') || normalizedMountRegion.includes('[') || normalizedMountRegion.includes(' ')) {
      return document.querySelector(normalizedMountRegion) as HTMLElement || rootElement || document.body
    }

    if (normalizedMountRegion && normalizedMountRegion !== 'auto' && normalizedMountRegion !== 'nearest' && normalizedMountRegion !== 'local') {
      return document.getElementById(normalizedMountRegion) as HTMLElement
        || document.querySelector(normalizedMountRegion) as HTMLElement
        || rootElement
        || document.body
    }

    if ((normalizedMountRegion === 'auto' || normalizedMountRegion === '') && !sourceElement) {
      return document.body
    }

    if ((normalizedMountRegion === 'auto' || normalizedMountRegion === '') && this._isDeveloperElement(sourceElement)) {
      return document.body
    }

    const sourceRegion = getNearestDialogRegion(sourceElement, null)

    if (sourceRegion) {
      return sourceRegion
    }

    return this._getRootDialogRegion()
  }

  private _getDocumentDialogMountRegion(mountRegion: string | HTMLElement | undefined, source: DocumentDialogOptions['source'], dialogRegion: HTMLElement) {
    if (mountRegion != null && mountRegion !== '') {
      return dialogRegion
    }

    if (source === 'drillInto') {
      return dialogRegion
    }

    return 'window'
  }

  private _getRootDialogRegion() {
    const rootElement = this.rootEl as HTMLElement
    const ownerDocument = rootElement?.ownerDocument || document

    if (rootElement?.classList?.contains('ae-dialog-region')) {
      return rootElement
    }

    return rootElement || ownerDocument.querySelector('.ae-root') as HTMLElement || ownerDocument.body
  }

  private _isDeveloperElement(sourceElement?: HTMLElement) {
    let element = sourceElement

    while (element) {
      const classNames = `${String(element.className || '')} ${String((element as any)._prevClass || '')}`

      if (element.classList?.contains('ae-developer') || classNames.split(/\s+/).includes('ae-developer')) {
        return true
      }

      element = element.parentElement as HTMLElement
    }

    return false
  }

  public get DOMEvents() {
    return DOMEvents
  }

  public dispatchDOMEvent(name: string, detail: any) {
    const rootElement = this._resolveDispatchElement(
      detail?.sourceElement as HTMLElement,
      this.context.rootElement as HTMLElement,
      this.rootEl as HTMLElement,
    )
    const event = new CustomEvent(name, { detail, bubbles: true, cancelable: true })

    if (rootElement && rootElement.dispatchEvent) {
      return rootElement.dispatchEvent(event)
    } else {
      console.error(`Cannot emit ${name} event from Aeppic instance without HTML element root to dispatch from in context`)
    }

    return true
  }

  private _resolveDispatchElement(...candidates: Array<HTMLElement | null | undefined>) {
    for (const candidate of candidates) {
      if (!candidate || typeof candidate.dispatchEvent !== 'function') {
        continue
      }

      if ((candidate as any).isConnected === true) {
        return candidate
      }

      const ownerDocument = candidate.ownerDocument
      if (ownerDocument && ownerDocument.contains(candidate)) {
        return candidate
      }
    }

    return null
  }

  private _resolveContextDispatchElement(...candidates: Array<HTMLElement | null | undefined>) {
    const contextElement = this._resolveDispatchElement(...candidates)

    if (contextElement) {
      return contextElement
    }

    if (this.context === this._rootContext) {
      return this._resolveDispatchElement(this.rootEl as HTMLElement)
    }

    return null
  }

  async nextRenderPass(cb?: Function): Promise<void> {
    if (this.context.isReleased) {
      throw new Error('Context already released')
    }

    await Vue.nextTick()

    if (cb) {
      cb()
    }
  }



  // private findTarget(targetNodeIdentifierOrElement ?: string | Element) {
  //   if (!targetNodeIdentifierOrElement) {
  //     if (!this._el) {
  //       throw new Errors.NoTargetElementSpecified()
  //     }
  //     return this._el
  //   }

  //   const target = findElement(targetNodeIdentifierOrElement)

  //   if (!target) {
  //     throw new Errors.TargetElementNotFound(targetNodeIdentifierOrElement)
  //   }

  //   return target
  // }

  public openFile(url: string, fileName: string) {
    // TODO: add interfaces. Improve check. See src/package-loaders/load-package-filesystem
    const win: any = window

    if (!win.host || !win.host.exports) {
      return
    }

    win.host.exports.openFile(url, fileName, (err) => {
      if (err) {
        throw new Error(`[DEBUG]: openFile failed for ${url} ${fileName}: ${err.toString()}`)
      }
      // console.log(`[DEBUG]: openFile finished for for ${url} ${fileName}`)
    })
  }

  public async editFile(url: string, fileName: string, documentInformation = { id: '', v: '', fieldName: '', mimeType: '' }) {
    const win: any = window

    if (!win.host) {
      console.warn('Cannot edit file. No host found')
      return
    }

    if (win.host.type === 'aeppic-client') {
      return this._editFileAeppicClient(url, fileName, documentInformation)
    }

    return this._editFileElectron(url, fileName, documentInformation)
  }

  _editFileAeppicClient(url: string, fileName: string, documentInformation = { id: '', v: '', fieldName: '', mimeType: '' }) {
    const document = this._model.get(documentInformation.id)

    if (!document) {
      console.warn('Cannot edit file. Document not found')
      return
    }

    const field = document.data[documentInformation.fieldName] as FileField

    if (!field) {
      console.warn('Cannot edit file. Field not found')
      return
    }

    if (Array.isArray(field)) {
      console.warn('Cannot edit file. Field is an array')
      return
    }

    if (!field.dataUrl || field.dataUrl.startsWith('data:') || field.dataUrl.startsWith('blob:') || field.dataUrl.startsWith('file:')) {
      console.warn('Cannot edit file. Field dataUrl is not a downloadable file url')
      return
    }

    if ('host' in window === false) {
      console.warn('Cannot edit file. No host found')
      return
    }

    const host = (<any>window).host as AeppicClientHostInterface

    if (host.type !== 'aeppic-client') {
      console.warn('Cannot edit file. No aeppic-client host found')
      return
    }

    const validClientVersions = ['^1.3.7', '^4.0.0']
    if (!validClientVersions.some(v => host.matches(v))) {
      console.warn('Cannot edit file. aeppic-client host version is not allowed.')
      return
    }

    if (!host.features.files.edit) {
      console.error('Cannot edit file. Host does not allow editing files')
      return
    }

    const extendedDocumentInformation = {
      ...documentInformation,
      dataUrl: field.dataUrl,
    }

    if (!host.files.subscribeToEditEvents) {
      console.error('Cannot edit file. No host.files.subscribeToEditEvents found')
      return
    }

    //
    // If the host is not yet subscribed to edit events, do so now
    //
    if (!this._dynamic.unsubscribeFromEditEvents) {
      //
      // The events are coming from the host and will be rewritten
      // as `FileEditEvents` to remove the nested structure due to enum
      // usage in the host.
      //
      this._dynamic.unsubscribeFromEditEvents = host.files.subscribeToEditEvents(async (editEvent) => {
        // TODO: Serialize these events (in case many events come)
        const { payload } = editEvent

        let event: FileEditEvent = null

        function setEventData(type: string, info) {
          const uploadEvents = ['upload:started', 'upload:canceling', 'upload:canceled', 'upload:failed', 'upload:finished']
          const fileEvents = ['file:changed']

          if (uploadEvents.includes(type)) {
            const uploadInfo = info as DocumentUploadInfo

            event = {
              type: type as FileEditEvent['type'],
              localFilePath: uploadInfo.filePath,
              document: uploadInfo.document,
              fieldName: uploadInfo.fieldName,
              uploadInfo: uploadInfo,
              detectedAt: new Date().valueOf(),
            } as FileUploadEvent
          } else if (fileEvents.includes(type)) {
            const [filePath, editInfo]:[LocalFilePath, DocumentEditInfo] = info

            event = {
              type: type as FileEditEvent['type'],
              localFilePath: filePath,
              document: editInfo.document,
              fieldName: editInfo.fieldName,
              editInfo: editInfo,
              detectedAt: new Date().valueOf(),
            } as FileChangeEvent
          } else {
            console.warn('Unknown event type', type)
          }
        };

        if ('Upload' in payload) {
          const uploadStatusKey = Object.keys(payload.Upload)[0]
          const eventType = `upload:${uploadStatusKey}`.toLowerCase()
          setEventData(eventType, payload.Upload[uploadStatusKey])

          // If an upload was finished we need to update the document
          if (eventType === 'upload:finished') {
            this._log.debug({ event }, 'Merging uploaded file into document')

            const { document, fieldName, uploadInfo } = event as FileUploadEvent
            const editableDocument = await this.edit(document.id)
            let fileField = editableDocument.data[fieldName] as FileField
            fileField.dataUrl = uploadInfo.fields.dataUrl
            this.save(editableDocument)
          }
        } else if ('Changed' in payload) {
          setEventData('file:changed', payload.Changed)
        }

        if (!event) {
          console.warn('Unknown edit event or could not parse', editEvent)
          return
        }

        this._emitter.emit('client:files:edited',event);
      });
    }

    return host.files.editFile(url, fileName, extendedDocumentInformation)
  }

  _editFileElectron(url: string, fileName: string, documentInformation = { id: '', v: '', fieldName: '', mimeType: '' }) {
    const win: any = window

    if (!win.host.exports) {
      console.warn('Cannot edit file. No host.exports found')
      return
    }

    const fileChangedCallback = async (err, result: any = {}) => {
      if (err) {
        throw new Error(`[DEBUG]: editFile failed for ${result && result.document ? result.document.id : '"unknown id"'}: "${err.toString()}"`)
      }

      const editableDocument = await this.edit(result.document.id)
      if (!editableDocument) {
        throw new Error(`[DEBUG]: editFile could not find edited document ${result.document.id}`)
      }
      const fieldName = result.document.fieldName

      editableDocument.setFile(fieldName, result.changedFileBuffer, {
        name: editableDocument.data[fieldName].name,
        type: editableDocument.data[fieldName].type
      })
      this.save(editableDocument)
    }

    win.host.exports.editFile(url, fileName, documentInformation, fileChangedCallback)
  }

  public createReactiveObjectProxy(object, handler?) {
    const defaultHandler = this._defaultReactiveHandler
    return ReactiveObjectProxy.build(object, defaultHandler || handler)
  }

  private _buildDefaultReactiveHandler() {
    return {
      onNewProperty: (target, name, value) => {
        Vue.set(target, name, value)
      },
      onDocumentWatcher: (target, name, watcher: IDocumentWatcher) => {
        watcher.on('updated', (document) => {
          target[name] = document
        })
      }
    }
  }

  private get context() { return this._context }

  private renameContext(newName: string) {
    if (this._context) {
      this._context.rename(newName)
    }
  }

  private get allResources() {
    return {
      named: this._allContexts.map(c => c.namedResources).join(),
      unnamed: this._allContexts.map(c => c.unnamedResources).join(),
    }
  }

  private get resources() {
    const context = this.context

    return {
      named: context.namedResources,
      unnamed: context.unnamedResources
    }
  }

  // NOTE: Accessing SharedContext is only possible AFTER the mounted
  public get SharedContext() {
    // if (!this._sharedContext) {
    //   throw new Error('Accessing SharedContext is only possible AFTER the boot. It has not initialized yet')
    // }
    return this._sharedContext
  }

  public get EditContext() {
    return {
      Registry: this._editContextRegistry,
      create: (options?: EditContextCreateOptions): EditContextHandle => this._editContextRegistry.createHandle(options),
    }
  }

  public get Selection() {
    return this._selection
  }

  public get Navigation() {
    return this._navigation
  }

  public get Colors() {
    return Colors
  }

  /*
   * Public helper functions
   */
  uuid() {
    return uuid()
  }

  public sort(list: any[], cb?)
  public sort(list: any[], options?: FieldSortInfo)
  public sort(list: any[], param?) {
    this._deprecated('Aeppic.sort is deprecated. Use Aeppic.Functions.sort')

    if (typeof param === 'function') {
      this._deprecated('Aeppic.sort with callback is deprecated. Provide FieldSortInfo or use <Prototype>Array.sort')
      return list.sort(param)
    }

    return sort(list, param)
  }

  /** Design or control lookup from the local read model, or `null` to ask the server. */
  public lookupLocally(request: LocalLookupRequest): Promise<any | null> {
    const adapter: any = this._serverAdapter
    return typeof adapter?.lookup === 'function' ? adapter.lookup(request) : Promise.resolve(null)
  }

  public fetch(url, options: { credentials?, method?, body?, headers?} = {}) {
    const defaultOptions = { ...options }

    if (typeof defaultOptions.credentials === 'undefined') {
      defaultOptions.credentials = 'include'
    }

    return this._fetch(url, defaultOptions)
  }

  /*
   * Exposed Objects for managing advanced state or download
   * content etc.
   */
  public createQueryToolbarRuntime(options: QueryToolbarRuntimeOptions = {}) {
    return createQueryToolbarRuntime(this, options)
  }

  public get Query() {
    return this._context.Query
  }

  public get Packages() {
    return this._packages
  }

  private async _loadActionPackages(packageRefs: Types.Reference[]): Promise<Record<string, any>> {
    const packages: Record<string, any> = {}
    const packageInfos = await this._packages.loadAll(packageRefs)

    for (const info of packageInfos) {
      if (!info?.exports['.']) continue

      try {
        const { exports } = await this._packages.importSourceFile(info, '.')
        const name = info.document.data.name as string
        packages[name] = exports?.default ?? exports
      } catch (error) {
        console.warn(`Failed to import package ${info.identifier}:`, error)
      }
    }

    return packages
  }

  /** @deprecated */
  public get packages() {
    this._deprecated('Aeppic.packages is deprecated. Use Aeppic.Packages instead. Dont use it to store global data or packages')
    return this._globals
  }

  get Features() {
    if (!this._features) {
      throw new Error('Accessing Features is only possible AFTER the boot. It has not initialized yet')
    }
    return this._features
  }

  get Changes() {
    return this._changes
  }

  get Translator() {
    return this._translator
  }

  get Preferences() {
    return this._preferences
  }

  get Services() {
    return this._services
  }

  get Designs() {
    return this._designs
  }

  get Forms() {
    return this._forms
  }

  get Controls() {
    return this._controls
  }

  get Developer() {
    return this._developer
  }

  get History() {
    return this._history
  }

  get AppManifest() {
    return this._dynamic.appManifest
  }

  get Manifest() {
    return this._dynamic.appManifest
  }

  get Actions() { return this._actions }

  get Commands() { return this._commands }

  get Validations() { return this._validations }

  get Uploads() { return this._uploads }

  get Content() {
    const context = this.context
    let content = context.getNamedResource('GENERIC_RESOURCE', 'CONTENT')

    if (!content) {
      content = new Content(this, this._getApiUrl(), this._urlToDataCache)
      context.addNamedResource('GENERIC_RESOURCE', 'CONTENT', content)
    }

    return content
  }


  get LocalStorage() {
    return this._localStorage
  }

  get SessionStorage() {
    return this._sessionStorage
  }


  public get Math() {
    return Math
  }

  public get Anime() {
    return anime
  }

  public get QueryString() {
    return queryString
  }

  /**
   * @deprecated
   *
   * Use `DateTime` instead
   */
  public get Date() {
    // tslint:disable-next-line
    this._deprecated('Use DateTime instead')
    return { format: () => { } }
  }

  public get DateTime() {
    return DateTime
  }

  public get DateTimeDuration() {
    return Duration
  }

  public get DateTimeInterval() {
    return Interval
  }

  public get DateTimeInfo() {
    return LuxonInfo
  }

  public get DateTimeSettings() {
    return DateTimeSettings
  }

  public setDateTimeLocale(locale : string) {
    const fallbackLocale = 'en-US'

    if (locale.trim() === '') {
      DateTimeSettings.defaultLocale = fallbackLocale
    } else if (locale === 'auto') {
      DateTimeSettings.defaultLocale = typeof navigator !== 'undefined' ? navigator.language : fallbackLocale
    } else {
      DateTimeSettings.defaultLocale = locale
    }
  }

  public get Modules() {
    return {
      FileSaver,
      ...Popup,
      DOMPurify,
      Sortable,
      zip: Zip,
    }
  }

  public get Parser() {
    return {
      markdown,
      formdown,
      dateRange: parseDateRange,
    }
  }

  public get Functions() {
    return {
      resolveField,
      sort,
      deriveChildId,
    }
  }

  public get Formatting(): FormattingAPI {
    const addDefaultTextFormattingContext = (context = {}) => ({
      DateTime,
      DateTimeDuration: Duration,
      ...context,
    })

    const getLocale = () => {
      const locale = this.Account?.data.locale as string
      return (!locale || locale === 'auto') ? undefined : locale
    }

    const formattingAPI = createFormattingAPI(getLocale)

    return {
      ...formattingAPI,

      formatText: (valueOrFormattingExpression: any, formattingExpression?: string, context = {}) => {
        if (formattingExpression == null) {
          const formatter = this._formatting.compileText(valueOrFormattingExpression)
          return (value: any, formatterContext = {}) => formatter(value, addDefaultTextFormattingContext(formatterContext))
        }

        return this._formatting.formatText(valueOrFormattingExpression, formattingExpression, addDefaultTextFormattingContext(context))
      },
      formatPastDateTime: (datetime: string, options?: { locale?: string }) => {
        let locale = options?.locale ?? this.Account?.data.locale as string

        if (locale === 'auto') {
          locale = undefined
        }

        return formatToHumanReadableWithLuxon(Luxon, datetime, { locale, ...options })
      },
      formatCurrency: (value: Types.CurrencyField, options: FormatCurrencyOptions = {}) => {
        return formattingAPI.currency(value, options)
      }
    }
  }

  public get Financial(): FinancialAPI {
    return createFinancialAPI()
  }

  /*
   * Vue specific helper options or  functions (for advanced use-cases e.g in external packages)
   */
  public get Vue() {
    return this._vue
  }

  public get VueComponentOptions() {
    return this._vueComponentOptions
  }

  // TODO: Move into caller
  public createVueComponentElement(name, props, temporaryAttributes) {
    const self = this

    const Child = Vue.component(name)
    const componentContainer = new Vue({
      render(createElement) {
        return createElement('div', {
          attrs: temporaryAttributes
        }, [
          createElement(name, {
            props
          })
        ])
      },
      provide() {
        return {
          getAeppicContext(contextName) {
            return (<any>self._vue).Aeppic.contextify(contextName)
          }
        }
      },
      components: {
        Child
      }
    })
    componentContainer.$mount()
    return componentContainer
  }

  public applyWrites(writes: Write[]) {
    for (const write of writes) {
      try {
        (<any>this[write.type])(...write.arguments)
      } catch (error) {
        console.error('Error applying write', write, error)
      }
    }
  }
  private _deprecated(message) {
    if (!this._deprecationWarnings.has(message)) {
      this._deprecationWarnings.add(message)
      this.Log.warn('DEPRECATED', message)
    }
  }
}


declare const zip: any



async function zipText(fileName, fileContent): Promise<Blob> {
  const zip = new ZipWriter()

  await zip.open()
  await zip.addText(fileName, fileContent)
  return await zip.close() as Blob
}

function ascii2hex(str) {
  const arr = []
  for (let i = 0, l = str.length; i < l; i++) {
    let hex = Number(str.charCodeAt(i)).toString(16)
    arr.push(hex)
  }
  return arr.join('')
}

function getJsonFormattingOptions(mode: 'lines' | 'array') {
  if (mode === 'lines') {
    return { prefix: '', separator: '\n', suffix: '', extension: '.jsonl', mime: 'plain/text', stringify: safeStringify }
  } else if (mode === 'array') {
    return { prefix: '[', separator: ',', suffix: ']', extension: '.json', mime: 'application/json', stringify: o => JSON.stringify(o, null, 2) }
  } else {
    throw new Error('Unsupported json formatting option')
  }
}

function exposeVueToOtherLibraries() {
  if (typeof window !== 'undefined') {
    (<any>window)['Vue'] = Vue
  }
}

exposeVueToOtherLibraries()

// function defineNonReactiveProperties(target, properties) {
//   for (const k in properties) {
//     const value = properties[k]

//     const opts: any = {
//       configurable: false,
//       enumerable: true,
//       writable: true,
//       value
//     }

//     Object.defineProperty(target, k, opts)
//   }
// }


function removeFromArray(array, entry) {
  for (let i = array.length - 1; i >= 0; i -= 1) {
    if (array[i] === entry) {
      array.splice(i, 1)
      return
    }
  }
  // tslint:disable-next-line
  console.warn('could not find entry')
}

function extractItemsAndNestedItemsInAnArrays(items: any[]) {
  const allItems = []

  for (const rootItem of items) {
    if (Array.isArray(rootItem)) {
      allItems.push(...rootItem)
    } else {
      allItems.push(rootItem)
    }
  }

  return allItems
}

function findDocumentIndex(documents, documentToFind) {
  for (let i = 0, l = documents.length; i < l; i += 1) {
    const document = documents[i]
    if (document.id === documentToFind.id) {
      return i
    }
  }
  return -1
}

function isBrowserButVeryOldBrowser() {
  if (isInBrowser() && !('visibilityState' in document)) {
    return true
  }
}

function isInBrowser() {
  return typeof document !== 'undefined' && typeof window !== 'undefined'
}

function hasLoginInfo(val: any): val is { username: string, password: string } {
  if (!val) {
    return false
  }

  if (val === true || val === false) {
    return false
  }

  if (val.username) {
    return true
  }
}

// https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Safely_detecting_option_support
function isPassiveOnHandlersSupported() {
  let passiveSupported = false

  try {
    const options = {
      get passive() { // This function will be called when the browser
        //   attempts to access the passive property.
        passiveSupported = true
        return false
      }
    };

    (window.addEventListener as any)('ae-passive-test', null, options)
      (window.removeEventListener as any)('ae-passive-test', null, options)
  } catch (err) {
    passiveSupported = false
  }

  return passiveSupported
}

export type AeppicInterface = Pick<Aeppic,
  'new' | 'edit' | 'editAll' | 'get' | 'getAll' | 'save' | 'saveAll' | 'getForm' | 'fetch' |
  'move' | 'delete' | 'deleteHard' | 'upgrade' | 'clone' | 'cloneDeep' | 'Query' | 'exportForms' |
  'change' | 'find' | 'findOne' | 'query' | 'integrate' | 'resolve' | 'resolveAll' | 'resolveEdit' | 'resolveToField' | 'DateTime' | 'DateTimeDuration' | 'DateTimeInterval' | 'isEditableDocument' |
  'asReference' | 'getFormForDocument' | 'getPreviousChanges' | 'getFormVersions' | 'hasChildren' | 'canUpgrade' | 'uuid' | 'download' | 'hasRight' | 'canCreateDocument' |
  'Log' | 'Warn' |
  'Commands' | 'Actions' | 'Preferences' | 'Services' | 'Designs' | 'Forms' | 'Controls' | 'Log' | 'HomePage' | 'applyWrites' | 'stamp' | 'verifyStamp' | 'Translator' | 'translate' | 'getProfilePageForAccount' | 'getAccountForProfilePage' |
  'Formatting' | 'Financial' | 'Features' | 'match' | 'matchAll' >

export type AeppicUserInterface = AeppicInterface & Pick<Aeppic, 'Account' | 'ProfilePage' >
export type AeppicUiInterface = AeppicInterface & Pick<Aeppic, 'drillInto' | 'navigateTo' | 'navigateBack' | 'showDrillIntoDialog' | 'showDocumentDialog' | 'setDrillIntoDialogDefaults' | 'Cache' | 'Features' | 'Account' | 'ProfilePage' | 'Selection' | 'Navigation' | 'Functions' | 'Router' | 'updateRouteParams' | 'LocalModel' | 'Support' | 'Toasts' >

export function isUiInterface(api: AeppicInterface | CommandAeppicInterface): api is AeppicUiInterface {
  return !!(api as any).navigateTo
}

/** @deprecated */
export type AeppicApi = AeppicInterface

function getCookies(response) {
  if (!response || !response.headers) {
    return []
  } else if (response.headers.raw) {
    return response.headers.raw()['set-cookie']
  } else if (response.headers.entries) {
    // tslint:disable-next-line
    console.error('Untested response headers cookie parsing path')
    const cookies = []
    for (const [key, value] of <Map<string, any>>response.headers.entries) {
      if (key.toLowerCase() === 'set-cookie') {
        cookies.push(value)
      }
    }
    return cookies
  } else {
    // tslint:disable-next-line
    console.error('Unsupported response headers')
    return []
  }
}


function composedPath(target: Node) {
  function getParents(node: Node, memo = []) {
    // Overlay surfaces continue at their logical owner so inspection reaches the opening component.
    const parentNode = getLogicalParentNode(node)

    if (!parentNode) {
      return memo
    }
    else {
      return getParents(parentNode, memo.concat(parentNode))
    }
  }

  return [target].concat(getParents(target))
}


function isDiscoverKeyCombination(event) {
  return isDOMInspectKeyCombination(event) && !elementIsAnActivelyFocusedTextElement(event.target)
}

function isDOMInspectKeyCombination(event: MouseEvent) {
  return (event.ctrlKey || event.metaKey) && event.shiftKey
}

function elementIsAnActivelyFocusedTextElement(target: Element) {
  if (target === document.activeElement) {
    if (target.nodeName === 'input' || target.nodeName === 'textarea') {
      return true
    }
  }
  return false
}

function removeElement(elementSelector: string | HTMLElement) {
  if (elementSelector && typeof document !== 'undefined') {
    const el = (typeof elementSelector === 'string') ? document.querySelector(elementSelector) : elementSelector

    if (el) {
      el.remove()
    }
  }
}

function mergeFlags(flags: FlagStatus = {}, defaults: Flags = {}): Flags {
  const result: Flags = {}

  for (const [name, value] of Object.entries(defaults)) {
    result[name] = { ...value }

    const flagStatus: boolean | FlagSettings = flags[<FlagNames>name]

    if (flagStatus == null) {
      continue
    }

    if (typeof flagStatus === 'boolean') {
      result[name].enabled = flagStatus
    } else if (typeof flagStatus === 'object') {
      result[name].enabled = flagStatus.enabled == null ? value.enabled : flagStatus.enabled
      result[name].options = { ...value.options, ...flagStatus.options }
    }
  }

  return result
}

function isAltModifiedEvent(event: Event | null | undefined): boolean {
  return !!event && (event as MouseEvent).altKey === true && !(event as MouseEvent).ctrlKey && !(event as MouseEvent).shiftKey
}

function isQuerySubscription(subscription: IQuerySubscription): subscription is QuerySubscription {
  return ('refresh' in subscription)
}

function loadUnifiedStylesheet(rootLayoutId: string) {
  return loadStyleFromUrl(`/stylesheet.css?rootLayoutId=${rootLayoutId}`, 'unified-stylesheet', 'ae-root', 0, 5_000)
}
